import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Modal,
  Steps,
  Form,
  Button,
  Select,
  Input,
  DatePicker,
  InputNumber,
  Card,
  Radio,
  Space,
  message,
  Row,
  Col,
  Typography,
  Tag,
  Divider,
  Tooltip,
  Tabs,
  Collapse,
  Checkbox,
  Spin,
  Alert
} from 'antd';
import {
  CarOutlined,
  RocketOutlined,
  SendOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  UserOutlined,
  PlusOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { DataTable } from '../../../ui';
import {
  TransportType,
  PaymentMethod,
  CreateRouteRequest,
  Route
} from '../../../types';
import { Driver } from '../../../types';
import {
  filterApplicationsForLoadingGroup,
  resolveLoadingGroupApplicationIds
} from './multiStopAllocationHelpers';
import {
  isRouteGroupUiEnabled,
  RouteTopologyPanel,
  RouteCargoLoadingPanel,
  deriveFormFieldsFromPrimaryGroup,
  syncGroupStopPositionsFromFlatten,
  resolveGroupLoadingContext,
  syncActiveLoadingStopByGroup,
  filterCargoForGroupExecutionContext,
  buildActiveLoadingStopByGroupFromAllocations,
  validateStep4ExecutionBindings,
  buildRouteStopsSavePayload,
  validateRouteStopsSavePayload,
  logRouteStopsSaveDebug,
  ensureDefaultGroupMainLoadingStop,
  ensureDefaultGroupMainUnloadingStop,
  buildDeliveryGroupsFromViewStops,
  collectStopUidByIdFromGroups,
  mergeViewStopsWithRouteStopUids,
  type RouteEditorExecutionHydration,
  flattenDeliveryGroupsToLegacyStops,
  createEmptyDeliveryGroup,
  refreshGroupLoadingPositions,
  isSequentialStopsEditorModeEnabled,
  syncGroupStopPositionsFromEditorOrder,
  flattenDeliveryGroupsToLegacyStopsEditorOrder,
  refreshGroupLoadingPositionsFromEditorOrder,
  establishMissingStopPositions,
  finalizeHydratedDeliveryGroups,
  logEditorOrderShadowDiff,
  isSequentialStopsItineraryEditorEnabled,
  validateItineraryBeforeSave,
  buildUnloadingStopOptionsForGroup,
  validateDeliveryGroups,
  evaluateGroupCargoStepEntryGate,
  clearExecutionDraftForGroup,
  buildGroupUiPhysicalSourcesQuerySnapshot,
  evaluateGroupUiPhysicalSourceMembership,
  isGroupUiCargoQueryPermitted,
  resolveGroupUiComputedCargoSource,
  isRouteInTransit,
  acquireExecutionTopologySaveLock,
  releaseExecutionTopologySaveLock,
  isExecutionTopologySaveLocked,
  computeExecutionTopologyFingerprint,
  groupsStopPositionsEqual,
  legacyStopsStructuralEqual,
  validateStableExecutionSnapshot,
  freezeStableExecutionSnapshot,
  type DeliveryGroupDraft,
  buildRouteOperationalUpdatePatch,
  filterApplicationsForRoutePlanningPicker
} from '../../../features/routeGroups';
import {
  applyExecutionTopologyToGroups,
  buildExecutionTopologyPreview,
  logExecutionTopologyDebugOnChange
} from '../../../features/routeGroups/executionTopologyHelpers';
import {
  buildRouteCreateExecutionValidator,
  executeRouteCreatePipeline,
  RouteCreatePipelineError
} from '../../../features/routeGroups/routeCreatePipeline';
import {
  ensureSavePayloadLoadingUnloadingLinks,
  logStopsSavePayload,
  logStopsTopologyDebug,
  type LoadingUnloadingAllocationLink
} from '../../../features/routeGroups/loadingUnloadingLinkHelpers';
import {
  buildImmutableExecutionSnapshot,
  mapSnapshotAllocationsToStopIds,
  mapUidDraftsToStopIds,
  findAllocationStopMappingIssue,
  positionByStopUidFromSaveRows,
  toCompatibilityExecutionSnapshot
} from '../../../features/routeGroups/immutableExecutionSnapshot';
import { logExecutionSnapshotDebug } from '../../../features/routeGroups/executionDebugLogging';
import type {
  AllocationEdgeDraft,
  FromStopDraftByUid,
  ToStopDraftByUid
} from '../../../features/routeGroups/executionDraftTypes';
import {
  allocationEdgesToLegacyDrafts,
  ensureAllocationEdgesFromLegacy,
  formatMultiEdgeValidationMessage,
  hydrateAllocationEdgesFromApi,
  selectedCargoIdsFromEdges,
  validateMultiEdgeAllocations
} from '../../../features/routeGroups/allocationEdgeDraft';
import { isMultiEdgeAllocationEditorEnabled } from '../../../features/routeGroups/multiEdgeAllocationFlags';
import { mapRejectMessage } from '../../../features/mutationWorkspace/buildMutationPolicyPreview';
import { makeStopUid } from '../../../features/routeGroups/deliveryGroupModel';
import { createClientExecutionUid } from '../../../features/routeGroups/clientExecutionUid';
import {
  buildLegacyPositionByStopUid,
  buildLegacyUnloadingOptionsWithUid,
  cargoFromStopMatchesLoadingPosition,
  resolveLegacyLoadingStopUid
} from '../../../features/routeGroups/legacyExecutionStopIndex';
import {
  useGetAvailableCargoQuery,
  useCreateRouteMutation,
  useUpdateRouteMutation,
  useCreateAllocationsMutation,
  useUpsertRouteStopsMutation,
  useDeleteRouteMutation,
  useStartExecutionSessionMutation,
  useRollbackExecutionSessionMutation,
  useGetRouteStopsQuery,
  useGetAllocationsQuery,
  useGetAllRoutesQuery,
  useGetRouteViewQuery,
  useGetRouteByIdQuery,
  useGetPhysicalCargoSourcesQuery,
  routesApi
} from '../../../store/api/routesApi';
import { store } from '../../../store';
import {
  useLazyGetCarrierAgreementByRouteQuery,
  useLazyGetCarrierAgreementPenaltiesQuery,
  useUpdateCarrierAgreementPenaltyMutation,
  type CarrierAgreementPenaltyRow,
  type CarrierAgreementSummary
} from '../../../store/api/carrierAgreementsApi';
import { useGetSettingsQuery } from '../../../store/api/settingsApi';
import { useGetAllWarehousesQuery } from '../../../store/api/warehousesApi';
import { useGetApplicationsQuery } from '../../../store/api/applicationsApi';
import {
  mergeSuccessorContextIntoCreateRouteBody,
  type SuccessorRouteCreateContext
} from './cp060RouteCreateFlowHelpers';
import {
  applySuccessorRouteCreatePrefill,
  buildAvailableCargoQueryParams,
  resolveRouteCreateExcludeRouteId,
  resolveRouteCreateUsesGroupTopology
} from './cp060RouteCreateStateMachine';
import {
  computeLegacyLoadingSourceKey,
  evaluateLegacyRouteCreateSubmitGate,
  evaluateLegacyStepAdvanceGate,
  LEGACY_CARGO_STEP_INDEX,
  normalizeLegacyRouteCreateDraftRestore
} from './routeCreateFormLegacyNavigationGate';
import {
  evaluateRestoredWarehouseWhenPhysicalSourcesReady,
  legacyWarehouseCargoQueryAllowed,
  type PendingRestoredWarehouseVerification
} from './legacyRestoredWarehouseVerification';
import { AvailableApplicationPicker } from '../../../features/routeCreatePhysicalSource/AvailableApplicationPicker';
import { PhysicalCargoSourcePicker } from '../../../features/routeCreatePhysicalSource/PhysicalCargoSourcePicker';
import {
  buildApplicationChangeResetPatch,
  buildPhysicalSourceClearPatch
} from '../../../features/routeCreatePhysicalSource/routeCreateSourceStateReset';
import { resolveSuccessorWarehousePrefill } from '../../../features/routeCreatePhysicalSource/successorPhysicalSourcePrefill';
import {
  useGetDriversQuery,
  useGetVehiclesQuery,
  useGetCarriersQuery,
  useGetRailwayStationsQuery,
  useGetAirportsQuery,
  useGetContractorsByRoleQuery
} from '../../../store/api/referencesApi';
import { useGetUsersQuery } from '../../../store/api/usersApi';
import {
  formatResponsibleUserLabel,
  mergeResponsibleSelectUsers,
} from '../../../utils/userDisplayName';
import { PENALTY_TYPE_LABELS } from '../../../utils/penaltyTypeLabels';
import { executorTypeLabel } from '../../../utils/enumUiLabels';
import { deriveLoadingApplicationIdForUi } from '../../../utils/deriveRouteLoadingApplicationId';
import { normalizeCityLabel, resolveCityHintFromAddressString } from '../../../utils/addressUtils';
import CreateCarrierModal from '../../common/CreateCarrierModal';
import CreateRailwayStationModal from '../../common/CreateRailwayStationModal';
import CreateAirportModal from '../../common/CreateAirportModal';
import CreateContractorModal from '../../common/CreateContractorModal';
import type { Contractor } from '../../../store/api/referencesApi';
import CreateDriverModal from '../../common/CreateDriverModal';
import VehicleSelectInline from '../../common/VehicleSelectInline';
import AddressAutocomplete from '../../common/AddressAutocomplete';
import type { AddressSuggestion } from '../../../services/addressService';
import SmartSelect from '../../common/SmartSelect';
import dayjs from 'dayjs';

/** Чтение количества из черновика (ключ number и string — из-за сериализации). */
function quantityFromDraft(
  cargoId: number,
  quantityDraft: Record<number, number>
): number {
  const cid = Number(cargoId);
  if (!Number.isFinite(cid)) return 0;
  const raw =
    quantityDraft[cid] ??
    (quantityDraft as unknown as Record<string, number>)[String(cid)];
  const q = Number(raw ?? 0);
  return Number.isFinite(q) ? q : 0;
}

/**
 * Количество для аллокации: в первую очередь quantityDraft[cargoId];
 * если 0/нет — fallback по строке груза (max или 1), чтобы не ломать редактирование.
 */
function resolveCargoQuantityForAllocation(
  cargoId: number,
  quantityDraft: Record<number, number>,
  cargoLists: Array<any[] | undefined>
): number {
  const cid = Number(cargoId);
  if (!Number.isFinite(cid)) return 0;
  const fromDraft = quantityFromDraft(cid, quantityDraft);
  if (fromDraft > 0) return fromDraft;

  for (const list of cargoLists) {
    if (!Array.isArray(list)) continue;
    const item = list.find((c: any) => Number(c.id) === cid);
    if (item) {
      const cap =
        item.availableQuantity != null ? Number(item.availableQuantity) : Number(item.quantity ?? 0);
      return cap > 0 ? cap : 1;
    }
  }
  return fromDraft;
}

/** Валидация количеств перед созданием маршрута (без изменения API). */
function validateCargoQuantitiesForCreate(
  cargoIds: number[],
  quantityDraft: Record<number, number>,
  availableCargo: any[] | undefined
): string | null {
  const list = Array.isArray(availableCargo) ? availableCargo : [];
  for (const rawId of cargoIds) {
    const cid = Number(rawId);
    if (!Number.isFinite(cid)) continue;
    const row = list.find((c: any) => Number(c.id) === cid);
    const maxQty = Number(
      row?.availableQuantity != null ? row.availableQuantity : row?.quantity ?? 0
    );
    const q = resolveCargoQuantityForAllocation(cid, quantityDraft, [list]);
    if (q <= 0) {
      return 'Укажите количество больше 0 для каждого выбранного груза';
    }
    if (maxQty > 0 && q > maxQty) {
      return `Количество в маршруте не может превышать доступное по грузу (${maxQty} мест)`;
    }
  }
  return null;
}

// Keep Title/Text from Typography as they are used throughout the component
const { Title, Text } = Typography;
const { Option } = Select;

const ACTIVE_DRIVER_ROUTE_STATUSES = new Set(['CREATED', 'ASSIGNED', 'IN_PROGRESS', 'IN_TRANSIT']);

function normalizeRouteStatusForDriverBusy(status: unknown): string {
  const value = String(status || '').trim().toLowerCase();
  const map: Record<string, string> = {
    active: 'CREATED',
    new: 'CREATED',
    created: 'CREATED',
    assigned: 'ASSIGNED',
    inprogress: 'IN_TRANSIT',
    'in-progress': 'IN_TRANSIT',
    in_progress: 'IN_TRANSIT',
    in_transit: 'IN_TRANSIT'
  };
  return map[value] || value.toUpperCase();
}

function toRouteDate(value: unknown): dayjs.Dayjs | null {
  if (!value) return null;
  const parsed = dayjs(value as any);
  return parsed.isValid() ? parsed.startOf('day') : null;
}

function routePeriodsOverlap(startA: dayjs.Dayjs, endA: dayjs.Dayjs, startB: dayjs.Dayjs, endB: dayjs.Dayjs): boolean {
  return !startA.isAfter(endB, 'day') && !startB.isAfter(endA, 'day');
}

function formatBusyDate(value: dayjs.Dayjs): string {
  return value.format('DD.MM');
}

function findDriverBusyRoute(params: {
  routes: Route[];
  loadingDate?: unknown;
  unloadingDate?: unknown;
  excludeRouteId?: number;
}): { route: Route; message: string } | null {
  const newStartRaw = toRouteDate(params.loadingDate);
  const newEndRaw = toRouteDate(params.unloadingDate);
  if (!newStartRaw || !newEndRaw) return null;
  const newStart = newStartRaw.isAfter(newEndRaw, 'day') ? newEndRaw : newStartRaw;
  const newEnd = newStartRaw.isAfter(newEndRaw, 'day') ? newStartRaw : newEndRaw;

  for (const route of params.routes) {
    if (params.excludeRouteId && Number(route.id) === params.excludeRouteId) continue;
    if (!ACTIVE_DRIVER_ROUTE_STATUSES.has(normalizeRouteStatusForDriverBusy(route.status))) continue;
    const existingStartRaw = toRouteDate(route.plannedLoadingDate);
    const existingEndRaw = toRouteDate(route.plannedUnloadingDate);
    if (!existingStartRaw || !existingEndRaw) continue;
    const existingStart = existingStartRaw.isAfter(existingEndRaw, 'day') ? existingEndRaw : existingStartRaw;
    const existingEnd = existingStartRaw.isAfter(existingEndRaw, 'day') ? existingStartRaw : existingEndRaw;
    if (routePeriodsOverlap(newStart, newEnd, existingStart, existingEnd)) {
      return {
        route,
        message: `Водитель занят на маршруте №${route.id} (${formatBusyDate(existingStart)} – ${formatBusyDate(existingEnd)})`
      };
    }
  }

  return null;
}

/** Города маршрута: из полей формы или последний запрос к геокодеру — вызывать из handleFinish (await). */
async function ensureRouteLoadingUnloadingCities(fd: {
  loadingAddress?: string;
  unloadingAddress?: string;
  loadingCity?: unknown;
  unloadingCity?: unknown;
}): Promise<{ loadingCity: string; unloadingCity: string }> {
  const str = (v: unknown) => (typeof v === 'string' ? v : v != null ? String(v) : '').trim();
  const fromField = (v: unknown) => normalizeCityLabel(str(v)) || str(v);

  let loadingCity = fromField(fd.loadingCity);
  let unloadingCity = fromField(fd.unloadingCity);

  if (!loadingCity && fd.loadingAddress?.trim()) {
    const h = await resolveCityHintFromAddressString(fd.loadingAddress.trim());
    loadingCity = normalizeCityLabel(h) || (h ? String(h).trim() : '');
  }
  if (!unloadingCity && fd.unloadingAddress?.trim()) {
    const h = await resolveCityHintFromAddressString(fd.unloadingAddress.trim());
    unloadingCity = normalizeCityLabel(h) || (h ? String(h).trim() : '');
  }
  return { loadingCity: loadingCity || '', unloadingCity: unloadingCity || '' };
}

// Хелпер для безопасной конвертации в dayjs - НИКОГДА не вызываем isValid() напрямую
const toDayjs = (v: any): dayjs.Dayjs | undefined => {
  if (!v) return undefined;
  const d = dayjs(v);
  return d.isValid() ? d : undefined;
};

function sameVehicleId(a: number | null | undefined, b: number | null | undefined): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
}

function isVehicleIdEmpty(v: number | null | undefined): boolean {
  return v == null || !Number.isFinite(Number(v));
}

/** Автоподстановка тягача/прицепа при смене водителя (если поле не меняли вручную относительно предыдущего водителя). */
function computeNextVehiclesFromDriverChange(
  prevDriver: Driver | null,
  newDriver: Driver | null | undefined,
  currentTruckId: number | null | undefined,
  currentTrailerId: number | null | undefined
): { vehicleId: number | undefined; trailerVehicleId: number | undefined } {
  if (!newDriver) {
    return { vehicleId: undefined, trailerVehicleId: undefined };
  }

  const prevTruckDef = prevDriver?.defaultVehicleId;
  const prevTrailerDef = prevDriver?.defaultTrailerVehicleId;

  const truckEmpty = isVehicleIdEmpty(currentTruckId);
  const truckMatchesPrevDefault =
    prevTruckDef != null && sameVehicleId(currentTruckId, prevTruckDef);

  let nextVehicleId: number | undefined;
  if (truckEmpty || truckMatchesPrevDefault) {
    const nv = newDriver.defaultVehicleId;
    nextVehicleId = nv != null && Number.isFinite(Number(nv)) ? Number(nv) : undefined;
  } else {
    const v = currentTruckId;
    nextVehicleId = v != null && Number.isFinite(Number(v)) ? Number(v) : undefined;
  }

  const trailerEmpty = isVehicleIdEmpty(currentTrailerId);
  const trailerMatchesPrevDefault =
    prevTrailerDef != null && sameVehicleId(currentTrailerId, prevTrailerDef);

  let nextTrailerId: number | undefined;
  if (trailerEmpty || trailerMatchesPrevDefault) {
    const nt = newDriver.defaultTrailerVehicleId;
    nextTrailerId = nt != null && Number.isFinite(Number(nt)) ? Number(nt) : undefined;
  } else {
    const t = currentTrailerId;
    nextTrailerId = t != null && Number.isFinite(Number(t)) ? Number(t) : undefined;
  }

  return { vehicleId: nextVehicleId, trailerVehicleId: nextTrailerId };
}

interface RouteCreateFormProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  editingRoute?: Route | null;
  /** CP-060 deep link from carrier replacement wizard (/routes/create?...). */
  successorRouteCreateContext?: SuccessorRouteCreateContext;
}

interface StepData {
  // Основная информация
  transportType?: TransportType;
  loadingPointType?: 'warehouse' | 'application';
  loadingPointId?: number;
  unloadingPointType?: 'warehouse' | 'application' | 'consignee';
  unloadingPointId?: number;
  loadingAddress?: string;
  unloadingAddress?: string;
  loadingCity?: string;
  unloadingCity?: string;
  loadingContactPerson?: string;
  loadingPhone?: string;
  unloadingContactPerson?: string;
  unloadingPhone?: string;
  plannedLoadingDate?: string;
  plannedUnloadingDate?: string;
  clientId?: number;
  expeditorId?: number;
  shipperId?: number;
  consigneeId?: number | null;
  responsibleLogistId?: number | null;
  
  // Тип транспорта
  driverId?: number;
  vehicleId?: number;
  trailerVehicleId?: number;
  carrierId?: number;
  wagonNumber?: string;
  departureStationId?: number;
  arrivalStationId?: number;
  flightNumber?: string;
  departureAirportId?: number;
  arrivalAirportId?: number;
  
  // Финансы
  totalAmount?: number;
  paymentMethod?: PaymentMethod;
  prepaymentCondition?: string;
  prepaymentPercent?: number;
  remainderPercent?: number;
  prepaymentDelayDays?: number;
  remainderDelayDays?: number;
  additionalExpenses?: Array<{ description?: string; amount?: number }>;
  executor?: string;
  carrierContractRequestNumber?: string;
  carrierContractRequestDate?: string;
  carrierContractRequestFile?: string;
  
  // Грузы
  cargoIds?: number[];
  /** ID заявки для связи маршрута (дублирует заявку погрузки / фильтр грузов / выбор из формы) */
  applicationId?: number;
}

/** Локальная копия строки штрафа договора для редактирования в форме */
interface PenaltyDraft {
  id: number;
  name: string;
  type: string;
  value: number;
  perDay: boolean;
  maxAmount: number | null;
  requiresManualInput: boolean;
}

function mapApiPenaltiesToDrafts(rows: CarrierAgreementPenaltyRow[]): PenaltyDraft[] {
  return rows.map((p) => ({
    id: p.id,
    name: p.name,
    type: String(p.type || 'FIXED').toUpperCase(),
    value: Number(p.value),
    perDay: Boolean(p.perDay),
    maxAmount: p.maxAmount == null || p.maxAmount === '' ? null : Number(p.maxAmount),
    requiresManualInput: Boolean(p.requiresManualInput)
  }));
}

const CARRIER_AGREEMENT_FETCH_MAX_RETRIES = 5;
const CARRIER_AGREEMENT_RETRY_DELAY_MS = 500;

function serializePenaltyDrafts(rows: PenaltyDraft[]): string {
  const normalized = rows
    .map((r) => ({
      id: r.id,
      type: r.type,
      value: r.value,
      perDay: r.perDay,
      maxAmount: r.maxAmount,
      requiresManualInput: r.requiresManualInput
    }))
    .sort((a, b) => a.id - b.id);
  return JSON.stringify(normalized);
}

const RouteCreateForm: React.FC<RouteCreateFormProps> = ({
  visible,
  onCancel,
  onSuccess,
  editingRoute,
  successorRouteCreateContext
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  /** Подсветка шага «Выбор грузов» при ошибке валидации перед созданием маршрута */
  const [cargoStepError, setCargoStepError] = useState(false);
  /** Выбранные ID грузов на шаге 4 — источник правды для rowSelection (form.cargoIds дублируется для валидации) */
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [stepData, setStepData] = useState<StepData>({});
  const [form] = Form.useForm();
  const executorWatch = Form.useWatch('executor', form);

  const [successorWarehouseMissing, setSuccessorWarehouseMissing] = useState<string | null>(null);
  const successorWarehousePrefillDoneRef = useRef(false);
  const [confirmedLegacySourceKey, setConfirmedLegacySourceKey] = useState<string | null>(null);
  const pendingRestoredWarehouseRef = useRef<PendingRestoredWarehouseVerification | null>(null);
  const restoredWarehouseVerifyDoneRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      successorWarehousePrefillDoneRef.current = false;
      setSuccessorWarehouseMissing(null);
      pendingRestoredWarehouseRef.current = null;
      restoredWarehouseVerifyDoneRef.current = false;
      setConfirmedLegacySourceKey(null);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || editingRoute || !successorRouteCreateContext) return;
    const patch = applySuccessorRouteCreatePrefill(successorRouteCreateContext);
    if (patch.applicationId != null) {
      form.setFieldsValue(patch);
      setStepData((prev) => ({ ...prev, ...patch }));
    }
  }, [visible, editingRoute, successorRouteCreateContext, form]);

  // Form field watchers для реактивного обновления
  const loadingPointType = Form.useWatch('loadingPointType', form);
  const unloadingPointType = Form.useWatch('unloadingPointType', form);
  const watchedTransportType = Form.useWatch('transportType', form);
  // Используем сохранённое значение, если поле временно отсутствует в форме
  const transportType = watchedTransportType ?? stepData.transportType;
  const loadingPointId = Form.useWatch('loadingPointId', form);
  const watchedApplicationId = Form.useWatch('applicationId', form);
  const unloadingPointId = Form.useWatch('unloadingPointId', form);
  const prepaymentPercent = Form.useWatch('prepaymentPercent', form);
  const watchedConsigneeId = Form.useWatch('consigneeId', form);
  const carrierIdWatch = Form.useWatch('carrierId', form);
  const driverIdWatch = Form.useWatch('driverId', form);
  const vehicleIdWatch = Form.useWatch('vehicleId', form);
  const trailerVehicleIdWatch = Form.useWatch('trailerVehicleId', form);
  const plannedLoadingDateWatch = Form.useWatch('plannedLoadingDate', form);
  const plannedUnloadingDateWatch = Form.useWatch('plannedUnloadingDate', form);

  // Modal states
  const [carrierModalOpen, setCarrierModalOpen] = useState(false);
  const [railwayStationModalOpen, setRailwayStationModalOpen] = useState(false);
  const [airportModalOpen, setAirportModalOpen] = useState(false);
  const [contractorModalOpen, setContractorModalOpen] = useState(false);
  const [contractorModalRole, setContractorModalRole] = useState<
    'client' | 'expeditor' | 'shipper' | 'consignee'
  >('client');
  const contractorModalDefaultRoles = useMemo(
    () => [contractorModalRole] as Array<'client' | 'expeditor' | 'shipper' | 'consignee'>,
    [contractorModalRole]
  );
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  /** Предыдущий выбранный водитель — для автоподстановки ТС без перезаписи ручных правок. */
  const [prevDriver, setPrevDriver] = useState<Driver | null>(null);
  /** Инициализация prevDriver один раз при открытии формы (создание / редактирование маршрута). */
  const prevDriverInitRouteKeyRef = useRef<number | 'new' | null>(null);
  /** One-shot edit hydration — prevents destructive re-hydration on query refetch while modal stays open. */
  const editHydratedForRouteIdRef = useRef<number | null>(null);
  const loadingAddressManuallyChangedRef = useRef(false);
  const unloadingAddressManuallyChangedRef = useRef(false);

  // API hooks
  const { data: warehouses } = useGetAllWarehousesQuery();
  const { data: applicationsData } = useGetApplicationsQuery(
    { page: 1, limit: 500, statusGroup: 'in_work' },
    { skip: !visible }
  );
  const applications = React.useMemo(() => applicationsData?.applications || [], [applicationsData]);
  const routePlanningApplications = React.useMemo(
    () => filterApplicationsForRoutePlanningPicker(applications as any[], editingRoute?.id),
    [applications, editingRoute?.id]
  );

  const { data: drivers, refetch: refetchDrivers } = useGetDriversQuery();
  const { data: vehicles, refetch: refetchVehicles } = useGetVehiclesQuery();
  const selectedDriverIdForBusyCheck =
    driverIdWatch != null && Number.isFinite(Number(driverIdWatch)) ? Number(driverIdWatch) : null;
  const { data: selectedDriverRoutesData } = useGetAllRoutesQuery(
    selectedDriverIdForBusyCheck != null
      ? { driverId: selectedDriverIdForBusyCheck, page: 1, limit: 100 }
      : { page: 1, limit: 1 },
    { skip: selectedDriverIdForBusyCheck == null || !visible }
  );
  const selectedDriverBusyRoute = useMemo(
    () => findDriverBusyRoute({
      routes: selectedDriverRoutesData?.routes ?? [],
      loadingDate: plannedLoadingDateWatch,
      unloadingDate: plannedUnloadingDateWatch,
      excludeRouteId: editingRoute?.id
    }),
    [selectedDriverRoutesData?.routes, plannedLoadingDateWatch, plannedUnloadingDateWatch, editingRoute?.id]
  );
  const trucks = useMemo(
    () => (vehicles || []).filter((v) => v.vehicleCategory === 'TRUCK'),
    [vehicles]
  );
  const trailers = useMemo(
    () => (vehicles || []).filter((v) => v.vehicleCategory === 'TRAILER'),
    [vehicles]
  );

  /** Кнопка «Заполнить из водителя» — только если ТС отличаются от default у выбранного водителя. */
  const showFillFromDriverButton = useMemo(() => {
    if (driverIdWatch == null || !drivers?.length) return false;
    const d = drivers.find((x) => x.id === driverIdWatch);
    if (!d) return false;
    const norm = (v: number | null | undefined) =>
      v != null && Number.isFinite(Number(v)) ? Number(v) : null;
    const truckForm = norm(vehicleIdWatch as number | undefined);
    const truckDef = norm(d.defaultVehicleId);
    const trailForm = norm(trailerVehicleIdWatch as number | undefined);
    const trailDef = norm(d.defaultTrailerVehicleId ?? undefined);
    return truckForm !== truckDef || trailForm !== trailDef;
  }, [driverIdWatch, vehicleIdWatch, trailerVehicleIdWatch, drivers]);

  const { data: carriers, refetch: refetchCarriers } = useGetCarriersQuery();
  const { data: settings } = useGetSettingsQuery();
  const { data: railwayStations, refetch: refetchRailwayStations } = useGetRailwayStationsQuery();
  const { data: airports, refetch: refetchAirports } = useGetAirportsQuery();
  
  const { data: clients, refetch: refetchClients } = useGetContractorsByRoleQuery('client');
  const { data: expeditors, refetch: refetchExpeditors } = useGetContractorsByRoleQuery('expeditor');
  const { data: shippers, refetch: refetchShippers } = useGetContractorsByRoleQuery('shipper');
  const { data: consignees, refetch: refetchConsignees } = useGetContractorsByRoleQuery('consignee');
  const { data: users = [], isLoading: isLoadingUsers } = useGetUsersQuery();

  const applyCarrierFromDriver = useCallback(
    (selectedDriver: Driver | null | undefined) => {
      if (!selectedDriver) {
        form.setFieldsValue({ carrierId: undefined });
        setStepData((prev) => ({ ...prev, carrierId: undefined }));
        return;
      }
      const driverCarrierId = (selectedDriver as { carrierId?: number | null }).carrierId;
      if (driverCarrierId !== null && driverCarrierId !== undefined) {
        const carrierIdValue = Number(driverCarrierId);
        form.setFieldsValue({ carrierId: carrierIdValue });
        setStepData((prev) => ({ ...prev, carrierId: carrierIdValue }));
        setTimeout(() => {
          form.setFieldsValue({ carrierId: carrierIdValue });
        }, 50);
      } else if (selectedDriver.isCompanyDriver && settings && settings.companyCarrierId) {
        const companyCarrierId = Number(settings.companyCarrierId);
        form.setFieldsValue({ carrierId: companyCarrierId });
        setStepData((prev) => ({ ...prev, carrierId: companyCarrierId }));
        setTimeout(() => {
          form.setFieldsValue({ carrierId: companyCarrierId });
        }, 50);
      } else {
        form.setFieldsValue({ carrierId: undefined });
        setStepData((prev) => ({ ...prev, carrierId: undefined }));
      }
    },
    [form, settings]
  );

  // Получаем текущего пользователя и проверяем роль
  const currentUser = React.useMemo(() => {
    try {
      const userStr = localStorage.getItem('logistics_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, []);

  // В маршрутах логисты тоже могут редактировать поле "Ответственный логист",
  // так как по одной заявке может быть несколько маршрутов с разными логистами
  const canEditResponsibleLogist = React.useMemo(() => {
    if (!currentUser?.role) return false;
    const roleName = typeof currentUser.role === 'string' ? currentUser.role : currentUser.role?.name;
    // Разрешаем редактирование для всех ролей (admin, accountant, manager, logist)
    return ['admin', 'accountant', 'manager', 'logist'].includes(roleName?.toLowerCase() || '');
  }, [currentUser]);
  
  const [createRoute, { isLoading }] = useCreateRouteMutation();
  const [updateRoute] = useUpdateRouteMutation();
  const [createAllocations] = useCreateAllocationsMutation();
  const [upsertRouteStops] = useUpsertRouteStopsMutation();
  const [deleteRoute] = useDeleteRouteMutation();
  const [startExecutionSession] = useStartExecutionSessionMutation();
  const [rollbackExecutionSession] = useRollbackExecutionSessionMutation();
  const [fetchAgreementByRoute] = useLazyGetCarrierAgreementByRouteQuery();
  const [fetchAgreementPenalties] = useLazyGetCarrierAgreementPenaltiesQuery();
  const [updateAgreementPenalty] = useUpdateCarrierAgreementPenaltyMutation();

  const [carrierAgreementId, setCarrierAgreementId] = useState<number | null>(null);
  const [penaltyDrafts, setPenaltyDrafts] = useState<PenaltyDraft[]>([]);
  const [penaltiesBaselineJson, setPenaltiesBaselineJson] = useState<string>('[]');
  const [penaltiesLoading, setPenaltiesLoading] = useState(false);
  const [postCreatePenaltiesStep, setPostCreatePenaltiesStep] = useState(false);
  const [, setPostCreateRouteId] = useState<number | null>(null);
  const [penaltyAgreementNotFound, setPenaltyAgreementNotFound] = useState(false);
  const [waitingCarrierAgreement, setWaitingCarrierAgreement] = useState(false);
  const [penaltiesBatchSaving, setPenaltiesBatchSaving] = useState(false);

  const loadPenaltiesForRoute = useCallback(
    async (routeId: number): Promise<boolean> => {
      setPenaltiesLoading(true);
      setWaitingCarrierAgreement(true);
      setPenaltyAgreementNotFound(false);

      let agreement: CarrierAgreementSummary | null = null;

      for (let attempt = 0; attempt < CARRIER_AGREEMENT_FETCH_MAX_RETRIES; attempt++) {
        console.log('WAITING FOR CARRIER AGREEMENT...', attempt);
        try {
          agreement = await fetchAgreementByRoute(routeId).unwrap();
          break;
        } catch (e: any) {
          const status = e?.status ?? e?.originalStatus;
          if (status === 404) {
            if (attempt < CARRIER_AGREEMENT_FETCH_MAX_RETRIES - 1) {
              await new Promise((resolve) => setTimeout(resolve, CARRIER_AGREEMENT_RETRY_DELAY_MS));
              continue;
            }
            setCarrierAgreementId(null);
            setPenaltyDrafts([]);
            setPenaltiesBaselineJson('[]');
            setPenaltyAgreementNotFound(true);
            setWaitingCarrierAgreement(false);
            setPenaltiesLoading(false);
            return false;
          }
          console.error('loadPenaltiesForRoute (agreement)', e);
          message.error('Не удалось загрузить штрафы по договору перевозчика');
          setWaitingCarrierAgreement(false);
          setPenaltiesLoading(false);
          setPenaltyAgreementNotFound(false);
          return false;
        }
      }

      if (!agreement) {
        setCarrierAgreementId(null);
        setPenaltyDrafts([]);
        setPenaltiesBaselineJson('[]');
        setPenaltyAgreementNotFound(true);
        setWaitingCarrierAgreement(false);
        setPenaltiesLoading(false);
        return false;
      }

      setWaitingCarrierAgreement(false);

      try {
        const list = await fetchAgreementPenalties(agreement.id).unwrap();
        const drafts = mapApiPenaltiesToDrafts(list);
        setCarrierAgreementId(agreement.id);
        setPenaltyDrafts(drafts);
        setPenaltiesBaselineJson(serializePenaltyDrafts(drafts));
        setPenaltyAgreementNotFound(false);
        return true;
      } catch (e: any) {
        console.error('loadPenaltiesForRoute (penalties)', e);
        message.error('Не удалось загрузить штрафы по договору перевозчика');
        setPenaltyAgreementNotFound(false);
        return false;
      } finally {
        setPenaltiesLoading(false);
      }
    },
    [fetchAgreementByRoute, fetchAgreementPenalties]
  );

  const resetCarrierPenaltiesState = useCallback(() => {
    setCarrierAgreementId(null);
    setPenaltyDrafts([]);
    setPenaltiesBaselineJson('[]');
    setPenaltiesLoading(false);
    setPostCreatePenaltiesStep(false);
    setPostCreateRouteId(null);
    setPenaltyAgreementNotFound(false);
    setWaitingCarrierAgreement(false);
  }, []);

  useEffect(() => {
    if (!visible || !editingRoute?.id) return;
    if (editingRoute.isCompanyRoute) {
      resetCarrierPenaltiesState();
      return;
    }
    if (!editingRoute.carrierId) {
      resetCarrierPenaltiesState();
      return;
    }
    if (String(editingRoute.executor || '').toUpperCase() !== 'CONTRACTOR') {
      resetCarrierPenaltiesState();
      return;
    }
    void loadPenaltiesForRoute(editingRoute.id);
  }, [
    visible,
    editingRoute?.id,
    editingRoute?.carrierId,
    editingRoute?.executor,
    editingRoute?.isCompanyRoute,
    loadPenaltiesForRoute,
    resetCarrierPenaltiesState
  ]);

  useEffect(() => {
    if (visible && !editingRoute && !postCreatePenaltiesStep) {
      resetCarrierPenaltiesState();
    }
  }, [visible, editingRoute, postCreatePenaltiesStep, resetCarrierPenaltiesState]);

  const penaltiesDirty =
    penaltyDrafts.length > 0 && serializePenaltyDrafts(penaltyDrafts) !== penaltiesBaselineJson;

  const updatePenaltyDraft = (id: number, patch: Partial<PenaltyDraft>) => {
    setPenaltyDrafts((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const handleSaveCarrierPenalties = async () => {
    if (carrierAgreementId == null || penaltyDrafts.length === 0) return;
    setPenaltiesBatchSaving(true);
    try {
      for (const row of penaltyDrafts) {
        await updateAgreementPenalty({
          agreementId: carrierAgreementId,
          penaltyId: row.id,
          body: {
            type: row.type,
            value: row.value,
            perDay: row.perDay,
            maxAmount: row.maxAmount,
            requiresManualInput: row.requiresManualInput
          }
        }).unwrap();
      }
      setPenaltiesBaselineJson(serializePenaltyDrafts(penaltyDrafts));
      message.success('Штрафы сохранены');
    } catch (e: any) {
      message.error(e?.data?.message || e?.error || 'Ошибка сохранения штрафов');
    } finally {
      setPenaltiesBatchSaving(false);
    }
  };

  const [quantityDraft, setQuantityDraft] = useState<Record<number, number>>({});
  const [fromStopDraft, setFromStopDraft] = useState<FromStopDraftByUid>({});
  const [toStopDraft, setToStopDraft] = useState<ToStopDraftByUid>({});
  const [allocationEdges, setAllocationEdges] = useState<AllocationEdgeDraft[]>([]);
  const [draftRestored, setDraftRestored] = useState(false);
  const [cargoAppFilter, setCargoAppFilter] = useState<number | undefined>(undefined);
  // Удалён переключатель isCompanyRoute - теперь используется executor
  const [stops, setStops] = useState<Array<{
    uid: string;
    id?: number | null;
    type: 'loading' | 'unloading' | 'transfer';
    position: number;
    pointType?: 'warehouse' | 'application' | 'consignee' | null;
    pointId?: number | null;
    parentLoadingStopId?: number | null;
    parentLoadingPosition?: number | null;
    address?: string | null;
    city?: string | null;
    contactPerson?: string | null;
    phone?: string | null;
    plannedAt?: string | null;
    actualAt?: string | null;
    notes?: string | null;
  }>>([]);
  const groupUiEnabled = resolveRouteCreateUsesGroupTopology({
    groupUiFeatureFlag: isRouteGroupUiEnabled(),
    editingRoute: Boolean(editingRoute),
    successorContext: successorRouteCreateContext
  });
  const routeCreateExcludeRouteId = React.useMemo(
    () =>
      resolveRouteCreateExcludeRouteId({
        editingRouteId: editingRoute?.id,
        successorContext: successorRouteCreateContext
      }),
    [editingRoute?.id, successorRouteCreateContext]
  );
  const multiEdgeEditorEnabled = groupUiEnabled && isMultiEdgeAllocationEditorEnabled();

  const syncLegacyBridgeFromEdges = useCallback(
    (edges: AllocationEdgeDraft[]) => {
      if (!multiEdgeEditorEnabled) return;
      const legacy = allocationEdgesToLegacyDrafts(edges);
      setQuantityDraft(legacy.quantityDraft);
      setFromStopDraft(legacy.fromStopDraft);
      setToStopDraft(legacy.toStopDraft);
      const ids = selectedCargoIdsFromEdges(edges);
      if (ids.length > 0) {
        setSelectedIds((prev) => {
          const merged = Array.from(new Set([...prev.map(Number), ...ids]));
          form.setFieldsValue({ cargoIds: merged });
          setStepData((p) => ({ ...p, cargoIds: merged }));
          return merged;
        });
      }
    },
    [form, multiEdgeEditorEnabled]
  );

  const resolvedAllocationEdges = React.useMemo(
    () =>
      multiEdgeEditorEnabled
        ? ensureAllocationEdgesFromLegacy(allocationEdges, {
            selectedIds,
            fromStopDraft,
            toStopDraft,
            quantityDraft
          })
        : [],
    [allocationEdges, fromStopDraft, multiEdgeEditorEnabled, quantityDraft, selectedIds, toStopDraft]
  );

  const handleAllocationEdgesChange = useCallback(
    (edges: AllocationEdgeDraft[]) => {
      setAllocationEdges(edges);
      syncLegacyBridgeFromEdges(edges);
    },
    [syncLegacyBridgeFromEdges]
  );
  const [deliveryGroups, setDeliveryGroups] = useState<DeliveryGroupDraft[]>(() => [
    createEmptyDeliveryGroup({
      sortOrder: 0,
      isDefault: true,
      loadingPosition: 0,
      label: 'Группа маршрута 1'
    })
  ]);
  const [activeGroupUid, setActiveGroupUid] = useState<string | null>(null);
  const [activeLoadingStopByGroup, setActiveLoadingStopByGroup] = useState<Record<string, string>>({});
  const skipGroupsToStopsSyncRef = useRef(false);
  const savingExecutionTopologyRef = useRef(false);
  const [topologySavePolicyError, setTopologySavePolicyError] = useState<string | null>(null);
  const [cargoSource, setCargoSource] = useState<{ kind: 'main' | 'stop'; stopIndex?: number }>({ kind: 'main' });
  /** После восстановления черновика — отслеживаем смену точки погрузки и сбрасываем шаг 4 (грузы). */
  const prevLoadingSourceKeyRef = useRef<string | null>(null);
  const prevLegacyApplicationIdRef = useRef<string | null>(null);

  const legacyFlowApplicationId =
    watchedApplicationId ?? stepData.applicationId ?? successorRouteCreateContext?.applicationId ?? null;

  const { data: legacyPhysicalSourcesPayload, isSuccess: legacyPhysicalSourcesReady } =
    useGetPhysicalCargoSourcesQuery(
      {
        applicationId: Number(legacyFlowApplicationId),
        excludeRouteId: routeCreateExcludeRouteId
      },
      {
        skip:
          !visible ||
          groupUiEnabled ||
          legacyFlowApplicationId == null ||
          !Number.isFinite(Number(legacyFlowApplicationId)) ||
          Number(legacyFlowApplicationId) <= 0
      }
    );

  useEffect(() => {
    if (!visible || editingRoute || groupUiEnabled || !successorRouteCreateContext) return;
    if (successorWarehousePrefillDoneRef.current) return;
    if (!legacyPhysicalSourcesReady) return;
    const appId = successorRouteCreateContext.applicationId;
    if (!appId) return;
    const result = resolveSuccessorWarehousePrefill({
      applicationId: appId,
      warehouseId: successorRouteCreateContext.warehouseId,
      sources: legacyPhysicalSourcesPayload?.sources ?? []
    });
    successorWarehousePrefillDoneRef.current = true;
    if (result.kind === 'matched') {
      form.setFieldsValue(result.loadingPatch);
      setStepData((prev) => ({ ...prev, ...result.loadingPatch }));
      setSuccessorWarehouseMissing(null);
      setConfirmedLegacySourceKey(
        computeLegacyLoadingSourceKey(
          result.loadingPatch.loadingPointType,
          result.loadingPatch.loadingPointId
        )
      );
    } else if (result.kind === 'missing') {
      setSuccessorWarehouseMissing(result.message);
      const clear = buildPhysicalSourceClearPatch();
      form.setFieldsValue(clear);
      setStepData((prev) => ({ ...prev, ...clear }));
    }
  }, [
    visible,
    editingRoute,
    groupUiEnabled,
    successorRouteCreateContext,
    legacyPhysicalSourcesReady,
    legacyPhysicalSourcesPayload?.sources,
    form
  ]);

  const makeStopUidFn = React.useCallback(() => makeStopUid(), []);
  /** Execution uids for main stops without a stop draft (group or legacy); regenerated per form session. */
  const [mainStopFallbackUids, setMainStopFallbackUids] = useState(() => ({
    loading: createClientExecutionUid(),
    unloading: createClientExecutionUid()
  }));

  /**
   * Execution uids are globally unique in DB — a persisted route's group drafts must never
   * be reused by the next create in this (kept-mounted) form.
   */
  const resetExecutionTopologyDraft = useCallback(() => {
    setDeliveryGroups([
      createEmptyDeliveryGroup({
        sortOrder: 0,
        isDefault: true,
        loadingPosition: 0,
        label: 'Группа маршрута 1'
      })
    ]);
    setActiveGroupUid(null);
    setActiveLoadingStopByGroup({});
    setAllocationEdges([]);
    setFromStopDraft({});
    setToStopDraft({});
    setStops([]);
    setMainStopFallbackUids({
      loading: createClientExecutionUid(),
      unloading: createClientExecutionUid()
    });
  }, []);

  const getStopSortTs = React.useCallback((s: any): number => {
    const raw = s?.actualAt || s?.plannedAt;
    if (!raw) return Number.POSITIVE_INFINITY;
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  }, []);

  const applyStopsOrder = React.useCallback((
    prevStops: Array<any>,
    nextStopsRaw: Array<any>
  ) => {
    // 1) сортируем доп. стопы по дате (факт -> план -> без даты в конец), стабильно
    const withIdx = nextStopsRaw.map((s, idx) => ({ s: { ...s, uid: s.uid || makeStopUidFn() }, idx }));
    withIdx.sort((a, b) => {
      const ta = getStopSortTs(a.s);
      const tb = getStopSortTs(b.s);
      if (ta !== tb) return ta - tb;
      return a.idx - b.idx;
    });
    const oldParentPosByUid = new Map<string, number | undefined>();
    prevStops.forEach((s) => oldParentPosByUid.set(String(s.uid), s.parentLoadingPosition));
    const nextStops = withIdx.map((x, i) => ({ ...x.s, position: i }));

    const oldByPos = new Map<number, string>();
    prevStops.forEach((s, i) => oldByPos.set(i + 1, String(s.uid)));
    const newPosByUid = new Map<string, number>();
    nextStops.forEach((s, i) => newPosByUid.set(String(s.uid), i + 1));
    const oldMainUnloadingPos = prevStops.length + 1;
    const newMainUnloadingPos = nextStops.length + 1;

    const remapStructuralPos = (pos: number | undefined): number | undefined => {
      if (pos === undefined) return undefined;
      if (pos === 0) return 0;
      if (pos === oldMainUnloadingPos) return newMainUnloadingPos;
      const uid = oldByPos.get(pos);
      if (!uid) return 0;
      return newPosByUid.get(uid) ?? 0;
    };

    // stopUid drafts are stable across reorder — no remap needed

    // сохраняем активную вкладку погрузки по uid
    setCargoSource((prev) => {
      if (prev.kind !== 'stop' || prev.stopIndex === undefined) return prev;
      const oldUid = prevStops?.[prev.stopIndex]?.uid;
      if (!oldUid) return { kind: 'main' };
      const newIndex = nextStops.findIndex((s) => s.uid === oldUid);
      if (newIndex === -1) return { kind: 'main' };
      return { kind: 'stop', stopIndex: newIndex };
    });

    return nextStops.map((stop) => {
      if (stop.type !== 'unloading') return stop;
      const parentPos = oldParentPosByUid.get(String(stop.uid)) ?? stop.parentLoadingPosition;
      return {
        ...stop,
        parentLoadingPosition: remapStructuralPos(parentPos ?? 0) ?? 0
      };
    });
  }, [getStopSortTs, makeStopUidFn]);

  const setStopsSmart = React.useCallback((updater: (prev: any[]) => any[]) => {
    setStops((prev) => {
      const raw = updater(prev);
      return applyStopsOrder(prev, raw);
    });
  }, [applyStopsOrder]);

  const routeViewQuery = useGetRouteViewQuery(editingRoute?.id as number, {
    skip: !visible || !editingRoute || !groupUiEnabled
  });

  const routeByIdQuery = useGetRouteByIdQuery(editingRoute?.id as number, {
    skip: !visible || !editingRoute
  });

  const activeDeliveryGroup = React.useMemo(() => {
    if (!groupUiEnabled || deliveryGroups.length === 0) return null;
    const uid = activeGroupUid ?? deliveryGroups[0]?.uid;
    return deliveryGroups.find((g) => g.uid === uid) ?? deliveryGroups[0] ?? null;
  }, [activeGroupUid, deliveryGroups, groupUiEnabled]);

  const activeGroupAppIdNum =
    activeDeliveryGroup?.applicationId != null
      ? Number(activeDeliveryGroup.applicationId)
      : NaN;

  const activeGroupPhysicalSourcesRtk = useGetPhysicalCargoSourcesQuery(
    {
      applicationId: activeGroupAppIdNum,
      excludeRouteId: routeCreateExcludeRouteId
    },
    {
      skip:
        !visible ||
        !groupUiEnabled ||
        !Number.isFinite(activeGroupAppIdNum) ||
        activeGroupAppIdNum <= 0
    }
  );

  const activeGroupPhysicalSourcesSnapshot = React.useMemo(
    () =>
      groupUiEnabled && activeDeliveryGroup
        ? buildGroupUiPhysicalSourcesQuerySnapshot({
            requestedApplicationId: activeGroupAppIdNum,
            excludeRouteId: routeCreateExcludeRouteId,
            slice: {
              data: activeGroupPhysicalSourcesRtk.data,
              isLoading: activeGroupPhysicalSourcesRtk.isLoading,
              isFetching: activeGroupPhysicalSourcesRtk.isFetching,
              isError: activeGroupPhysicalSourcesRtk.isError,
              isSuccess: activeGroupPhysicalSourcesRtk.isSuccess,
              isUninitialized: activeGroupPhysicalSourcesRtk.isUninitialized
            }
          })
        : null,
    [
      groupUiEnabled,
      activeDeliveryGroup,
      activeGroupAppIdNum,
      routeCreateExcludeRouteId,
      activeGroupPhysicalSourcesRtk.data,
      activeGroupPhysicalSourcesRtk.isFetching,
      activeGroupPhysicalSourcesRtk.isLoading,
      activeGroupPhysicalSourcesRtk.isError,
      activeGroupPhysicalSourcesRtk.isSuccess,
      activeGroupPhysicalSourcesRtk.isUninitialized
    ]
  );

  React.useEffect(() => {
    if (!visible || !groupUiEnabled) return;
    const appIds = new Set<number>();
    for (const g of deliveryGroups) {
      const id = Number(g.applicationId);
      if (Number.isFinite(id) && id > 0) appIds.add(id);
    }
    const subs = Array.from(appIds).map((applicationId) =>
      store.dispatch(
        routesApi.endpoints.getPhysicalCargoSources.initiate({
          applicationId,
          excludeRouteId: routeCreateExcludeRouteId
        })
      )
    );
    return () => {
      subs.forEach((s) => s.unsubscribe());
    };
  }, [visible, groupUiEnabled, deliveryGroups, routeCreateExcludeRouteId]);

  const resolveGroupMembershipForNavigation = React.useCallback(
    (group: DeliveryGroupDraft) => {
      const appId = group.applicationId != null ? Number(group.applicationId) : NaN;
      if (!Number.isFinite(appId) || appId <= 0) {
        const m = evaluateGroupUiPhysicalSourceMembership({ group, query: null });
        return { permitted: m.permitted, message: m.permitted ? undefined : m.message };
      }
      const slice = routesApi.endpoints.getPhysicalCargoSources.select({
        applicationId: appId,
        excludeRouteId: routeCreateExcludeRouteId
      })(store.getState());
      const membership = evaluateGroupUiPhysicalSourceMembership({
        group,
        query: buildGroupUiPhysicalSourcesQuerySnapshot({
          requestedApplicationId: appId,
          excludeRouteId: routeCreateExcludeRouteId,
          slice: {
            isUninitialized: slice.isUninitialized,
            isLoading: slice.isLoading,
            isFetching:
              'isFetching' in slice && typeof slice.isFetching === 'boolean'
                ? slice.isFetching
                : slice.isLoading,
            isError: slice.isError,
            isSuccess: slice.isSuccess,
            data: slice.data
          }
        })
      });
      return {
        permitted: membership.permitted,
        message: membership.permitted ? undefined : membership.message
      };
    },
    [routeCreateExcludeRouteId]
  );

  const executionMainStopFields = React.useMemo(
    () => ({
      mainLoading: {
        uid: mainStopFallbackUids.loading,
        address: stepData.loadingAddress ?? null,
        city: String(stepData.loadingCity || '').trim() || null,
        contactPerson: stepData.loadingContactPerson ?? null,
        phone: stepData.loadingPhone ?? null,
        plannedAt: stepData.plannedLoadingDate ?? null,
        pointType: stepData.loadingPointType ?? loadingPointType ?? null,
        pointId: stepData.loadingPointId ?? loadingPointId ?? null
      },
      mainUnloading: {
        uid: mainStopFallbackUids.unloading,
        address: stepData.unloadingAddress ?? null,
        city: String(stepData.unloadingCity || '').trim() || null,
        contactPerson: stepData.unloadingContactPerson ?? null,
        phone: stepData.unloadingPhone ?? null,
        plannedAt: stepData.plannedUnloadingDate ?? null,
        pointType: stepData.unloadingPointType ?? unloadingPointType ?? null,
        pointId: stepData.unloadingPointId ?? unloadingPointId ?? null
      }
    }),
    [
      loadingPointId,
      loadingPointType,
      mainStopFallbackUids,
      stepData.loadingAddress,
      stepData.loadingCity,
      stepData.loadingContactPerson,
      stepData.loadingPhone,
      stepData.loadingPointId,
      stepData.loadingPointType,
      stepData.plannedLoadingDate,
      stepData.plannedUnloadingDate,
      stepData.unloadingAddress,
      stepData.unloadingCity,
      stepData.unloadingContactPerson,
      stepData.unloadingPhone,
      stepData.unloadingPointId,
      stepData.unloadingPointType,
      unloadingPointId,
      unloadingPointType
    ]
  );

  const executionTopologyLabelContext = React.useMemo(
    () => ({
      resolveWarehouseName: (id: number) => {
        const w = warehouses?.find((x: any) => x.id === id);
        return w?.name ?? undefined;
      },
      resolveConsigneeName: (id: number) => {
        const c = consignees?.find((x: any) => x.id === id);
        return c?.name ?? undefined;
      }
    }),
    [consignees, warehouses]
  );

  const executionTopologyPreview = React.useMemo(() => {
    if (!groupUiEnabled || deliveryGroups.length === 0) return null;
    return buildExecutionTopologyPreview({
      groups: deliveryGroups,
      mainLoading: executionMainStopFields.mainLoading,
      mainUnloading: executionMainStopFields.mainUnloading,
      labelContext: executionTopologyLabelContext
    });
  }, [
    deliveryGroups,
    executionMainStopFields,
    executionTopologyLabelContext,
    groupUiEnabled
  ]);

  const executionTopologyFingerprint = React.useMemo(
    () => computeExecutionTopologyFingerprint(executionTopologyPreview),
    [executionTopologyPreview]
  );

  const resolveWarehouseLabelForCargo = React.useCallback(
    (id: number) => {
      const w = warehouses?.find((x: any) => x.id === id);
      return w ? `Склад — ${w.name}` : 'Склад';
    },
    [warehouses]
  );

  const resolveConsigneeLabelForCargo = React.useCallback(
    (id: number) => {
      const c = consignees?.find((x: any) => x.id === id);
      return c ? `Получатель — ${c.name}` : 'Получатель';
    },
    [consignees]
  );

  const activeLoadingContext = React.useMemo(() => {
    if (!groupUiEnabled || !activeDeliveryGroup) return null;
    return resolveGroupLoadingContext(
      activeDeliveryGroup,
      activeLoadingStopByGroup,
      executionTopologyPreview
    );
  }, [
    activeDeliveryGroup,
    activeLoadingStopByGroup,
    executionTopologyPreview,
    groupUiEnabled
  ]);

  const groupValidationIssues = React.useMemo(
    () => (groupUiEnabled ? validateDeliveryGroups(deliveryGroups) : []),
    [deliveryGroups, groupUiEnabled]
  );

  const routeInTransit = React.useMemo(
    () =>
      isRouteInTransit({
        routeStatus: (editingRoute as any)?.status,
        inTransit: String((editingRoute as any)?.status || '').toUpperCase() === 'IN_TRANSIT'
      }),
    [editingRoute]
  );

  const itineraryCargoContext = React.useMemo(
    () => ({
      fromStopDraft,
      toStopDraft,
      quantityDraft,
      selectedIds,
      allocationEdges: multiEdgeEditorEnabled ? resolvedAllocationEdges : undefined
    }),
    [
      fromStopDraft,
      toStopDraft,
      quantityDraft,
      selectedIds,
      multiEdgeEditorEnabled,
      resolvedAllocationEdges
    ]
  );

  const financialTermsLocked = React.useMemo(() => {
    if (!routeInTransit) return false;
    const paid = Number(routeViewQuery.data?.finance?.payable?.totalPaid ?? 0);
    return Number.isFinite(paid) && paid > 0;
  }, [routeInTransit, routeViewQuery.data?.finance?.payable?.totalPaid]);

  React.useEffect(() => {
    if (!groupUiEnabled) return;
    if (isExecutionTopologySaveLocked() || savingExecutionTopologyRef.current) return;
    if (skipGroupsToStopsSyncRef.current) {
      skipGroupsToStopsSyncRef.current = false;
      return;
    }
    const editorMode = isSequentialStopsEditorModeEnabled();
    let flattened;
    let withPositions: DeliveryGroupDraft[];

    if (editorMode) {
      const established = establishMissingStopPositions(deliveryGroups);
      withPositions = executionTopologyPreview
        ? applyExecutionTopologyToGroups(established, executionTopologyPreview)
        : syncGroupStopPositionsFromEditorOrder(established, { reindexContiguous: false });
      flattened = flattenDeliveryGroupsToLegacyStopsEditorOrder(withPositions);
      withPositions = refreshGroupLoadingPositionsFromEditorOrder(withPositions);
      logEditorOrderShadowDiff(withPositions, 'groups-sync');
    } else {
      flattened = flattenDeliveryGroupsToLegacyStops(deliveryGroups);
      const refreshed = refreshGroupLoadingPositions(deliveryGroups, flattened);
      withPositions = executionTopologyPreview
        ? applyExecutionTopologyToGroups(refreshed, executionTopologyPreview)
        : syncGroupStopPositionsFromFlatten(refreshed, flattened);
    }

    if (!groupsStopPositionsEqual(withPositions, deliveryGroups)) {
      setDeliveryGroups(withPositions);
    }
    if (!legacyStopsStructuralEqual(flattened, stops)) {
      setStops(flattened);
    }
  }, [deliveryGroups, executionTopologyPreview, groupUiEnabled, stops]);

  React.useEffect(() => {
    if (!groupUiEnabled || !visible) return;
    if (!activeGroupUid && deliveryGroups[0]?.uid) {
      setActiveGroupUid(deliveryGroups[0].uid);
    }
  }, [activeGroupUid, deliveryGroups, groupUiEnabled, visible]);

  const clearGroupExecutionScope = React.useCallback(
    (groupUid: string) => {
      const cleared = clearExecutionDraftForGroup({
        groupUid,
        groups: deliveryGroups,
        selectedIds,
        fromStopDraft,
        quantityDraft,
        toStopDraft,
        allocationEdges: multiEdgeEditorEnabled ? resolvedAllocationEdges : undefined
      });
      setSelectedIds(cleared.selectedIds);
      setFromStopDraft(cleared.fromStopDraft);
      setQuantityDraft(cleared.quantityDraft);
      setToStopDraft(cleared.toStopDraft);
      if (multiEdgeEditorEnabled) {
        setAllocationEdges(cleared.allocationEdges);
      }
      form.setFieldsValue({ cargoIds: cleared.selectedIds });
      setStepData((prev) => ({ ...prev, cargoIds: cleared.selectedIds }));
    },
    [
      deliveryGroups,
      selectedIds,
      fromStopDraft,
      quantityDraft,
      toStopDraft,
      multiEdgeEditorEnabled,
      resolvedAllocationEdges,
      form
    ]
  );

  const handleDeliveryGroupsChange = React.useCallback((next: DeliveryGroupDraft[]) => {
    setTopologySavePolicyError(null);
    setDeliveryGroups(next);
    const primary = next.find((g) => g.isDefault) ?? next[0];
    if (!primary) return;
    const patch = deriveFormFieldsFromPrimaryGroup(primary, applications as any[]);
    if (Object.keys(patch).length > 0) {
      form.setFieldsValue(patch);
      setStepData((prev) => ({ ...prev, ...patch }));
    }
  }, [applications, form]);

  React.useEffect(() => {
    if (!groupUiEnabled || deliveryGroups.length === 0) return;
    if (isExecutionTopologySaveLocked() || savingExecutionTopologyRef.current) return;
    const primary = deliveryGroups.find((g) => g.isDefault) ?? deliveryGroups[0];
    const patch = deriveFormFieldsFromPrimaryGroup(primary, applications as any[]);
    if (Object.keys(patch).length === 0) return;
    const stepPatch = patch as Record<string, unknown>;
    const unchanged = Object.entries(stepPatch).every(
      ([key, value]) => (stepData as Record<string, unknown>)[key] === value
    );
    if (unchanged) return;
    form.setFieldsValue(patch);
    setStepData((prev) => ({ ...prev, ...patch }));
  }, [applications, deliveryGroups, form, groupUiEnabled, stepData]);

  React.useEffect(() => {
    if (!groupUiEnabled || deliveryGroups.length === 0) return;
    if (isExecutionTopologySaveLocked() || savingExecutionTopologyRef.current) return;
    setActiveLoadingStopByGroup((prev) =>
      syncActiveLoadingStopByGroup(deliveryGroups, prev, executionTopologyPreview)
    );
  }, [deliveryGroups, executionTopologyPreview, groupUiEnabled]);

  // Источник списка грузов
  // Для основной точки берём актуальные значения из формы (useWatch), иначе после шагов 0–2 stepData может быть пустым → GET /cargo/available без query.
  const computedCargoSource = React.useMemo(() => {
    if (groupUiEnabled && activeDeliveryGroup && activeLoadingContext) {
      const query = resolveGroupUiComputedCargoSource({
        activeGroup: activeDeliveryGroup,
        activeLoadingContext
      });
      if (query.loadingPointType && query.loadingPointId) {
        return {
          loadingPointType: query.loadingPointType,
          loadingPointId: query.loadingPointId
        };
      }
    }
    if (cargoSource.kind === 'stop' && cargoSource.stopIndex !== undefined) {
      const s = stops[cargoSource.stopIndex];
      if (s && s.type === 'loading' && s.pointType && s.pointId) {
        return { loadingPointType: s.pointType as 'warehouse' | 'application', loadingPointId: s.pointId };
      }
    }
    const effectiveType = loadingPointType ?? stepData.loadingPointType;
    const effectiveId = loadingPointId ?? stepData.loadingPointId;
    if (
      effectiveType == null ||
      effectiveType === '' ||
      effectiveId == null ||
      !Number.isFinite(Number(effectiveId))
    ) {
      return {
        loadingPointType: undefined as unknown as 'warehouse' | 'application',
        loadingPointId: undefined as unknown as number
      };
    }
    return {
      loadingPointType: (effectiveType === 'application' ? 'application' : 'warehouse') as 'warehouse' | 'application',
      loadingPointId: Number(effectiveId)
    };
  }, [activeDeliveryGroup, activeLoadingContext, cargoSource, groupUiEnabled, stops, loadingPointType, loadingPointId, stepData.loadingPointType, stepData.loadingPointId]);

  // Вкладки источников погрузки: основная точка + стопы погрузки
  const loadingSourceTabs = React.useMemo(() => {
    const items: Array<{ key: string; label: React.ReactNode; kind: 'main' | 'stop'; stopIndex?: number }> = [];
    items.push({
      key: 'main',
      kind: 'main',
      label: <span>Основная точка погрузки</span>
    });
    stops
      .map((s, idx) => ({ s, idx }))
      .filter(({ s }) => s.type === 'loading' && s.pointType && s.pointId)
      .forEach(({ s, idx }) => {
        let label = `Стоп ${idx + 1}: Погрузка`;
        if (s.pointType === 'warehouse') {
          const w = warehouses?.find(x => x.id === s.pointId);
          label += w ? ` — Склад ${w.name}` : '';
        } else if (s.pointType === 'application') {
          const a = applications?.find(x => x.id === s.pointId);
          label += a ? ` — Заявка ${a.internalNumber}` : '';
        }
        items.push({
          key: `stop-${idx}`,
          kind: 'stop',
          stopIndex: idx,
          label: <span title={label}>{label}</span>
        });
      });
    return items;
  }, [applications, stops, warehouses]);

  const selectedLoadingInfo = React.useMemo(() => {
    const resolveShipperName = (app: any, fallbackShipperId?: number): string => {
      const directName = app?.shipper?.name;
      if (directName) return directName;
      const shipperId = Number(app?.shipperId ?? fallbackShipperId);
      if (!Number.isFinite(shipperId)) return '—';
      const shipper = shippers?.find((s: any) => Number(s.id) === shipperId);
      return shipper?.name || '—';
    };

    const kind = cargoSource.kind;
    const stopIndex = cargoSource.stopIndex;

    // Main loading source
    if (kind === 'main') {
      if (stepData.loadingPointType === 'warehouse') {
        const w = warehouses?.find((x: any) => x.id === stepData.loadingPointId);
        return {
          title: 'Основная точка погрузки',
          lines: [
            { label: 'Склад', value: w ? `${w.name}${w.city ? ` (${w.city})` : ''}` : (stepData.loadingPointId ? `#${stepData.loadingPointId}` : '—') },
            { label: 'Адрес', value: w?.address || stepData.loadingAddress || '—' }
          ]
        };
      }
      if (stepData.loadingPointType === 'application') {
        const a = applications?.find((x: any) => x.id === stepData.loadingPointId);
        return {
          title: 'Основная точка погрузки',
          lines: [
            { label: 'Заявка', value: a?.internalNumber ? `${a.internalNumber}${a.client?.name ? ` — ${a.client.name}` : ''}` : (stepData.loadingPointId ? `#${stepData.loadingPointId}` : '—') },
            { label: 'Грузоотправитель', value: resolveShipperName(a, stepData.shipperId) },
            { label: 'Адрес погрузки', value: a?.loadingAddress || stepData.loadingAddress || '—' }
          ]
        };
      }
      return { title: 'Основная точка погрузки', lines: [] as Array<{ label: string; value: string }> };
    }

    // Stop loading source
    if (kind === 'stop' && typeof stopIndex === 'number') {
      const s = stops[stopIndex];
      if (!s) return { title: `Стоп ${stopIndex + 1}`, lines: [] as Array<{ label: string; value: string }> };
      const baseTitle = `Стоп ${stopIndex + 1}: Погрузка`;

      if (s.pointType === 'warehouse') {
        const w = warehouses?.find((x: any) => x.id === s.pointId);
        return {
          title: baseTitle,
          lines: [
            { label: 'Склад', value: w ? `${w.name}${w.city ? ` (${w.city})` : ''}` : (s.pointId ? `#${s.pointId}` : '—') },
            { label: 'Адрес', value: w?.address || s.address || '—' },
            { label: 'Контакт', value: (w as any)?.contactPerson || s.contactPerson || '—' },
            { label: 'Телефон', value: (w as any)?.phone || s.phone || '—' }
          ]
        };
      }
      if (s.pointType === 'application') {
        const a = applications?.find((x: any) => x.id === s.pointId);
        return {
          title: baseTitle,
          lines: [
            { label: 'Заявка', value: a?.internalNumber ? `${a.internalNumber}${a.client?.name ? ` — ${a.client.name}` : ''}` : (s.pointId ? `#${s.pointId}` : '—') },
            { label: 'Грузоотправитель', value: resolveShipperName(a, stepData.shipperId) },
            { label: 'Адрес погрузки', value: a?.loadingAddress || s.address || '—' },
            { label: 'Контакт', value: a?.loadingContactPerson || s.contactPerson || '—' },
            { label: 'Телефон', value: a?.loadingPhone || s.phone || '—' }
          ]
        };
      }
      return {
        title: baseTitle,
        lines: [
          { label: 'Адрес', value: s.address || '—' }
        ]
      };
    }

    return { title: 'Точка погрузки', lines: [] as Array<{ label: string; value: string }> };
  }, [applications, cargoSource.kind, cargoSource.stopIndex, shippers, stepData.loadingAddress, stepData.loadingPointId, stepData.loadingPointType, stepData.shipperId, stops, warehouses]);

  // Позиции стопов в payloadStops:
  // 0 — основная погрузка, 1..N — пользовательские стопы (по индексу +1), N+1 — основная выгрузка
  const mainLoadingPos = 0;
  const mainUnloadingPos = stops.length + 1;

  const buildGroupAwareStopsForSave = React.useCallback(
    (finalData: Record<string, any>) => {
      const mainLoading = {
        uid: mainStopFallbackUids.loading,
        address: finalData.loadingAddress || null,
        city: String(finalData.loadingCity || '').trim() || null,
        contactPerson: finalData.loadingContactPerson || null,
        phone: finalData.loadingPhone || null,
        plannedAt: finalData.plannedLoadingDate || null,
        pointType: finalData.loadingPointType || null,
        pointId: finalData.loadingPointId || null
      };
      const mainUnloading = {
        uid: mainStopFallbackUids.unloading,
        address: finalData.unloadingAddress || null,
        city: String(finalData.unloadingCity || '').trim() || null,
        contactPerson: finalData.unloadingContactPerson || null,
        phone: finalData.unloadingPhone || null,
        plannedAt: finalData.plannedUnloadingDate || null,
        pointType: finalData.unloadingPointType || null,
        pointId: finalData.unloadingPointId || null
      };
      const cargoIdsForLinks: number[] = Array.isArray(finalData.cargoIds)
        ? (finalData.cargoIds as number[]).map((x) => Number(x)).filter((n) => Number.isFinite(n))
        : selectedIds;

      if (isSequentialStopsItineraryEditorEnabled()) {
        const itineraryIssues = validateItineraryBeforeSave({
          groups: deliveryGroups,
          selectedIds: cargoIdsForLinks,
          fromStopDraft,
          toStopDraft,
          quantityDraft
        });
        if (itineraryIssues.length > 0) {
          return {
            normalized: [] as ReturnType<typeof buildRouteStopsSavePayload>,
            issues: [{ message: itineraryIssues[0].message }],
            payload: [] as Record<string, unknown>[],
            allocationLinks: [] as LoadingUnloadingAllocationLink[],
            snapshot: undefined
          };
        }
      }

      const snapshotRaw = buildImmutableExecutionSnapshot({
        groups: deliveryGroups,
        mainLoading,
        mainUnloading,
        selectedIds: cargoIdsForLinks,
        fromStopDraft,
        toStopDraft,
        quantityDraft,
        allocationEdges: multiEdgeEditorEnabled ? resolvedAllocationEdges : undefined
      });
      const snapshot = freezeStableExecutionSnapshot(snapshotRaw);
      const snapshotValidation = validateStableExecutionSnapshot(snapshot);
      if (!snapshotValidation.ok) {
        return {
          normalized: [] as ReturnType<typeof buildRouteStopsSavePayload>,
          issues: [{ message: snapshotValidation.message ?? 'Execution topology inconsistent' }],
          payload: [] as Record<string, unknown>[],
          allocationLinks: [] as LoadingUnloadingAllocationLink[],
          snapshot
        };
      }

      if (executionTopologyPreview) {
        logExecutionTopologyDebugOnChange(
          'pre-save',
          executionTopologyPreview,
          executionTopologyFingerprint,
          deliveryGroups,
          { allocationLinks: [...snapshot.allocationLinks] }
        );
      }

      logStopsSavePayload('before-links', [...snapshot.saveRows], [...snapshot.allocationLinks]);
      const normalized = ensureSavePayloadLoadingUnloadingLinks(
        [...snapshot.saveRows],
        [...snapshot.allocationLinks]
      );
      logStopsSavePayload('after-links', normalized, [...snapshot.allocationLinks]);
      const issues = validateRouteStopsSavePayload(normalized, {
        allocationLinks: [...snapshot.allocationLinks]
      });
      logStopsSavePayload('final', normalized, [...snapshot.allocationLinks]);
      logStopsTopologyDebug('pre-save', normalized, [...snapshot.allocationLinks]);
      logRouteStopsSaveDebug('pre-save', normalized, deliveryGroups);
      logExecutionSnapshotDebug('pre-save', snapshot);

      return {
        normalized,
        issues,
        payload: [...snapshot.payloadStops],
        allocationLinks: [...snapshot.allocationLinks],
        snapshot
      };
    },
    [
      deliveryGroups,
      executionTopologyFingerprint,
      executionTopologyPreview,
      fromStopDraft,
      mainStopFallbackUids,
      multiEdgeEditorEnabled,
      quantityDraft,
      resolvedAllocationEdges,
      selectedIds,
      toStopDraft
    ]
  );
  const currentLoadingPosition = React.useMemo(() => {
    if (groupUiEnabled && activeLoadingContext) {
      return activeLoadingContext.position;
    }
    if (cargoSource.kind === 'stop' && cargoSource.stopIndex !== undefined) {
      const stop = stops[cargoSource.stopIndex];
      if (stop?.type === 'loading') return cargoSource.stopIndex + 1;
    }
    return mainLoadingPos;
  }, [activeLoadingContext, cargoSource, groupUiEnabled, stops]);

  const routeStopsQuery = useGetRouteStopsQuery(editingRoute?.id as number, {
    skip: !visible || !editingRoute
  });
  const allocationsQuery = useGetAllocationsQuery(editingRoute?.id as number, {
    skip: !visible || !editingRoute
  });

  const legacyMainLoadingUid = React.useMemo(() => {
    if (!editingRoute || !routeStopsQuery.isSuccess) return mainStopFallbackUids.loading;
    const apiStops: any[] = Array.isArray(routeStopsQuery.data) ? routeStopsQuery.data : [];
    const mainLoading =
      apiStops.find((s) => s.type === 'loading' && s.position === 0) ||
      apiStops.find((s) => s.type === 'loading');
    return mainLoading?.id ? `db-${mainLoading.id}` : mainStopFallbackUids.loading;
  }, [editingRoute, mainStopFallbackUids.loading, routeStopsQuery.data, routeStopsQuery.isSuccess]);

  const legacyMainUnloadingUid = React.useMemo(() => {
    if (!editingRoute || !routeStopsQuery.isSuccess) return mainStopFallbackUids.unloading;
    const apiStops: any[] = Array.isArray(routeStopsQuery.data) ? routeStopsQuery.data : [];
    const sorted = [...apiStops].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const mainUnloading = [...sorted].reverse().find((s) => s.type === 'unloading');
    return mainUnloading?.id ? `db-${mainUnloading.id}` : mainStopFallbackUids.unloading;
  }, [editingRoute, mainStopFallbackUids.unloading, routeStopsQuery.data, routeStopsQuery.isSuccess]);

  const legacyPositionByStopUid = React.useMemo(() => {
    if (groupUiEnabled) return null;
    return buildLegacyPositionByStopUid({
      mainLoadingUid: legacyMainLoadingUid,
      stops,
      mainUnloadingUid: legacyMainUnloadingUid
    });
  }, [groupUiEnabled, legacyMainLoadingUid, legacyMainUnloadingUid, stops]);

  const currentLegacyLoadingStopUid = React.useMemo(() => {
    if (groupUiEnabled || !legacyPositionByStopUid) return null;
    return resolveLegacyLoadingStopUid({
      loadingPosition: currentLoadingPosition,
      mainLoadingUid: legacyMainLoadingUid,
      mainLoadingPos,
      stops
    });
  }, [
    currentLoadingPosition,
    groupUiEnabled,
    legacyMainLoadingUid,
    legacyPositionByStopUid,
    mainLoadingPos,
    stops
  ]);

  const groupAwareUnloadingStopOptions = React.useMemo(() => {
    if (!groupUiEnabled || !activeDeliveryGroup) return null;
    return buildUnloadingStopOptionsForGroup({
      group: activeDeliveryGroup,
      topology: executionTopologyPreview,
      mainLoading: executionMainStopFields.mainLoading,
      mainUnloading: executionMainStopFields.mainUnloading,
      labelContext: {
        resolveWarehouseName: (id) => {
          const w = warehouses?.find((x: any) => x.id === id);
          return w?.name ?? undefined;
        },
        resolveConsigneeName: (id) => {
          const c = consignees?.find((x: any) => x.id === id);
          return c?.name ?? undefined;
        }
      },
      mainUnloadingLegacy: {
        position: stops.length + 1,
        pointType: stepData.unloadingPointType ?? unloadingPointType,
        pointId: stepData.unloadingPointId ?? unloadingPointId,
        type: 'unloading'
      },
      legacyStops: stops.map((s, idx) => ({ ...s, position: idx + 1 })),
      resolveWarehouseLabel: (id) => {
        const w = warehouses?.find((x: any) => x.id === id);
        return w ? `Склад — ${w.name}${w.city ? ` (${w.city})` : ''}` : 'Склад';
      },
      resolveApplicationLabel: (id) => {
        const a = applications?.find((x: any) => x.id === id);
        return a
          ? `Заявка — ${a.internalNumber}${a.consignee?.name ? ` / ${a.consignee.name}` : ''}`
          : 'Заявка доставки';
      },
      resolveConsigneeLabel: (id) => {
        const c = consignees?.find((x: any) => x.id === id);
        return c ? `Получатель — ${c.name}` : 'Получатель';
      }
    });
  }, [
    activeDeliveryGroup,
    applications,
    consignees,
    executionMainStopFields,
    executionTopologyPreview,
    groupUiEnabled,
    stepData.unloadingPointId,
    stepData.unloadingPointType,
    stops,
    unloadingPointId,
    unloadingPointType,
    warehouses
  ]);

  // Опции выбора "куда" — стопы выгрузки + основная выгрузка (всегда есть)
  const unloadingStopOptions = React.useMemo(
    () =>
      groupAwareUnloadingStopOptions ??
      buildLegacyUnloadingOptionsWithUid({
        stops,
        currentLoadingPosition,
        mainLoadingPos,
        mainUnloadingPos,
        mainUnloadingUid: legacyMainUnloadingUid,
        mainUnloading: {
          pointType: stepData.unloadingPointType ?? unloadingPointType,
          pointId: stepData.unloadingPointId ?? unloadingPointId
        },
        resolveWarehouseLabel: (id) => {
          const w = warehouses?.find((x: any) => x.id === id);
          return w ? `Склад — ${w.name}${w.city ? ` (${w.city})` : ''}` : 'Склад';
        },
        resolveApplicationLabel: (id) => {
          const a = applications?.find((x: any) => x.id === id);
          return a
            ? `Заявка — ${a.internalNumber}${a.consignee?.name ? ` / ${a.consignee.name}` : ''}`
            : 'Заявка доставки';
        },
        resolveConsigneeLabel: (id) => {
          const c = consignees?.find((x: any) => x.id === id);
          return c ? `Получатель — ${c.name}` : 'Получатель';
        }
      }),
    [
      applications,
      consignees,
      currentLoadingPosition,
      groupAwareUnloadingStopOptions,
      legacyMainUnloadingUid,
      mainLoadingPos,
      mainUnloadingPos,
      stepData.unloadingPointId,
      stepData.unloadingPointType,
      stops,
      unloadingPointId,
      unloadingPointType,
      warehouses
    ]
  );

  const cp059CargoQueryParams = React.useMemo(() => {
    const applicationId =
      watchedApplicationId ??
      stepData.applicationId ??
      successorRouteCreateContext?.applicationId ??
      null;
    return buildAvailableCargoQueryParams({
      applicationId: applicationId != null ? Number(applicationId) : null,
      loadingPointType: computedCargoSource.loadingPointType,
      loadingPointId: computedCargoSource.loadingPointId,
      excludeRouteId: routeCreateExcludeRouteId
    });
  }, [
    watchedApplicationId,
    stepData.applicationId,
    successorRouteCreateContext?.applicationId,
    computedCargoSource.loadingPointType,
    computedCargoSource.loadingPointId,
    routeCreateExcludeRouteId
  ]);

  const expectedLegacySourceKey = React.useMemo(
    () =>
      computeLegacyLoadingSourceKey(
        computedCargoSource.loadingPointType,
        computedCargoSource.loadingPointId
      ),
    [computedCargoSource.loadingPointType, computedCargoSource.loadingPointId]
  );

  const legacyCargoQueryPermitted = legacyWarehouseCargoQueryAllowed({
    groupUiEnabled,
    loadingPointType: computedCargoSource.loadingPointType,
    loadingPointId: computedCargoSource.loadingPointId,
    applicationId: legacyFlowApplicationId,
    pendingWarehouseVerification: pendingRestoredWarehouseRef.current,
    confirmedSourceKey: confirmedLegacySourceKey,
    expectedSourceKey: expectedLegacySourceKey
  });

  const groupCargoQueryPermitted = isGroupUiCargoQueryPermitted({
    groupUiEnabled,
    activeGroup: activeDeliveryGroup,
    physicalSourcesQuery: activeGroupPhysicalSourcesSnapshot
  });

  const { data: availableCargo } = useGetAvailableCargoQuery(
    cp059CargoQueryParams ?? {
      loadingPointType: 'application',
      loadingPointId: 0
    },
    {
      skip: !cp059CargoQueryParams || !legacyCargoQueryPermitted || !groupCargoQueryPermitted
    }
  );

  const mainGroupApplicationIds = React.useMemo(
    () =>
      resolveLoadingGroupApplicationIds({
        loadingPosition: mainLoadingPos,
        mainLoading: {
          pointType: stepData.loadingPointType ?? loadingPointType,
          pointId: stepData.loadingPointId ?? loadingPointId
        },
        stops,
        cargoRows: [
          ...(Array.isArray(availableCargo) ? (availableCargo as any[]) : []),
          ...(((editingRoute?.cargo as any[]) ?? []) as any[])
        ],
        fromStopDraft,
        mainLoadingPos,
        positionByStopUid: legacyPositionByStopUid ?? undefined
      }),
    [
      availableCargo,
      editingRoute?.cargo,
      fromStopDraft,
      loadingPointId,
      loadingPointType,
      mainLoadingPos,
      stepData.loadingPointId,
      stepData.loadingPointType,
      stops,
      legacyPositionByStopUid
    ]
  );

  const mainUnloadingApplications = React.useMemo(
    () =>
      filterApplicationsForLoadingGroup(routePlanningApplications as any[], mainGroupApplicationIds),
    [routePlanningApplications, mainGroupApplicationIds]
  );

  const applicationsForStopGroup = React.useCallback(
    (loadingPosition: number) => {
      const allowed = resolveLoadingGroupApplicationIds({
        loadingPosition,
        mainLoading: {
          pointType: stepData.loadingPointType ?? loadingPointType,
          pointId: stepData.loadingPointId ?? loadingPointId
        },
        stops,
        cargoRows: [
          ...(Array.isArray(availableCargo) ? (availableCargo as any[]) : []),
          ...(((editingRoute?.cargo as any[]) ?? []) as any[])
        ],
        fromStopDraft,
        mainLoadingPos,
        positionByStopUid: legacyPositionByStopUid ?? undefined
      });
      return filterApplicationsForLoadingGroup(routePlanningApplications as any[], allowed);
    },
    [
      routePlanningApplications,
      availableCargo,
      editingRoute?.cargo,
      fromStopDraft,
      legacyPositionByStopUid,
      loadingPointId,
      loadingPointType,
      mainLoadingPos,
      stepData.loadingPointId,
      stepData.loadingPointType,
      stops
    ]
  );

  // Черновик формы маршрута
  const ROUTE_DRAFT_KEY = 'draft_route_form_v2';
  const saveDraft = () => {
    try {
      const values = form.getFieldsValue();
      const merged = { ...stepData, ...values };
      const draft = { values: merged, step: currentStep, quantityDraft };
      localStorage.setItem(ROUTE_DRAFT_KEY, JSON.stringify(draft));
    } catch {}
  };
  const clearDraft = () => localStorage.removeItem(ROUTE_DRAFT_KEY);

  const clearStep4CargoState = useCallback(() => {
    setQuantityDraft({});
    setFromStopDraft({});
    setToStopDraft({});
    setAllocationEdges([]);
    setCargoAppFilter(undefined);
    setCargoSource({ kind: 'main' });
    setSelectedIds([]);
    form.setFieldsValue({ cargoIds: [] });
    setStepData((prev) => ({ ...prev, cargoIds: [] }));
    try {
      const rawLs = localStorage.getItem(ROUTE_DRAFT_KEY);
      if (!rawLs) return;
      const d = JSON.parse(rawLs);
      const vals = { ...(d.values || {}) };
      delete (vals as { cargoIds?: number[] }).cargoIds;
      const next: Record<string, unknown> = {
        ...d,
        values: { ...vals, cargoIds: [] },
        quantityDraft: {}
      };
      delete next.allocations;
      localStorage.setItem(ROUTE_DRAFT_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, [form]);

  const loadingSourceKey = `${String(loadingPointType ?? '')}|${String(loadingPointId ?? '')}`;

  useEffect(() => {
    if (!visible) {
      prevLoadingSourceKeyRef.current = null;
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || editingRoute || !draftRestored) return;
    const prev = prevLoadingSourceKeyRef.current;
    if (prev === null) {
      prevLoadingSourceKeyRef.current = loadingSourceKey;
      return;
    }
    if (prev !== loadingSourceKey) {
      clearStep4CargoState();
      prevLoadingSourceKeyRef.current = loadingSourceKey;
    }
  }, [visible, editingRoute, draftRestored, loadingSourceKey, clearStep4CargoState]);

  const handleLegacyApplicationChange = useCallback(
    (appId: number) => {
      const reset = buildApplicationChangeResetPatch(appId);
      form.setFieldsValue(reset);
      setStepData((prev) => ({ ...prev, ...reset }));
      clearStep4CargoState();
      setSuccessorWarehouseMissing(null);
      successorWarehousePrefillDoneRef.current = true;
      pendingRestoredWarehouseRef.current = null;
      restoredWarehouseVerifyDoneRef.current = false;
      setConfirmedLegacySourceKey(null);
    },
    [form, clearStep4CargoState]
  );

  useEffect(() => {
    if (!visible || editingRoute || groupUiEnabled || !legacyPhysicalSourcesReady) return;
    if (!pendingRestoredWarehouseRef.current) return;
    if (restoredWarehouseVerifyDoneRef.current) return;

    const outcome = evaluateRestoredWarehouseWhenPhysicalSourcesReady({
      pending: pendingRestoredWarehouseRef.current,
      currentApplicationId: legacyFlowApplicationId,
      responseApplicationId: legacyPhysicalSourcesPayload?.applicationId,
      sources: legacyPhysicalSourcesPayload?.sources ?? []
    });

    if (outcome.action === 'ignore_stale') {
      return;
    }

    restoredWarehouseVerifyDoneRef.current = true;
    pendingRestoredWarehouseRef.current = null;

    if (outcome.action === 'verified') {
      const whId = Number(form.getFieldValue('loadingPointId') ?? stepData.loadingPointId);
      setConfirmedLegacySourceKey(computeLegacyLoadingSourceKey('warehouse', whId));
      if (outcome.stepAfterVerification >= 3) {
        setCurrentStep(3);
      }
      return;
    }

    message.warning(outcome.message);
    const clear = buildPhysicalSourceClearPatch();
    form.setFieldsValue(clear);
    setStepData((prev) => ({ ...prev, ...clear, cargoIds: [] }));
    setConfirmedLegacySourceKey(null);
    setCurrentStep(0);
    clearStep4CargoState();
    try {
      const rawLs = localStorage.getItem(ROUTE_DRAFT_KEY);
      if (rawLs) {
        const d = JSON.parse(rawLs);
        localStorage.setItem(
          ROUTE_DRAFT_KEY,
          JSON.stringify({
            ...d,
            step: 0,
            values: { ...(d.values || {}), ...clear, cargoIds: [] }
          })
        );
      }
    } catch {
      /* ignore */
    }
  }, [
    visible,
    editingRoute,
    groupUiEnabled,
    legacyPhysicalSourcesReady,
    legacyPhysicalSourcesPayload?.applicationId,
    legacyPhysicalSourcesPayload?.sources,
    legacyFlowApplicationId,
    form,
    stepData.loadingPointId,
    clearStep4CargoState
  ]);

  const legacyApplicationKey = String(legacyFlowApplicationId ?? '');

  useEffect(() => {
    if (!visible || editingRoute || !draftRestored || groupUiEnabled) return;
    const prev = prevLegacyApplicationIdRef.current;
    if (prev === null) {
      prevLegacyApplicationIdRef.current = legacyApplicationKey;
      return;
    }
    if (prev !== legacyApplicationKey) {
      const clear = buildPhysicalSourceClearPatch();
      form.setFieldsValue(clear);
      setStepData((prevState) => ({ ...prevState, ...clear }));
      clearStep4CargoState();
      setSuccessorWarehouseMissing(null);
      prevLegacyApplicationIdRef.current = legacyApplicationKey;
    }
  }, [
    visible,
    editingRoute,
    draftRestored,
    groupUiEnabled,
    legacyApplicationKey,
    form,
    clearStep4CargoState
  ]);

  // New 4-step structure
  const steps = [
    { title: 'Основная информация', icon: <EnvironmentOutlined /> },
    { title: 'Тип транспорта', icon: <CarOutlined /> },
    { title: 'Финансы', icon: <DollarOutlined /> },
    { title: 'Выбор грузов', icon: <UserOutlined /> }
  ];

  const transportIcons = {
    [TransportType.AUTO]: <CarOutlined />,
    [TransportType.RAILWAY]: <RocketOutlined />,
    [TransportType.AVIATION]: <SendOutlined />
  };

  const transportLabels = {
    [TransportType.AUTO]: 'Авто',
    [TransportType.RAILWAY]: 'ЖД',
    [TransportType.AVIATION]: 'Авиа'
  };

  // Fields that must persist across steps but are not shown on steps 1-3
  const hiddenFieldNames: (keyof StepData)[] = [
    'transportType',
    // Контрагенты
    'clientId','expeditorId','shipperId','consigneeId',
    // Точки погрузки/выгрузки (loadingPointId — только видимый Select на шаге 0, без дубля hidden)
    'loadingPointType','unloadingPointType','unloadingPointId',
    // Адреса и контакты
    'loadingAddress','unloadingAddress',
    'loadingCity','unloadingCity',
    'loadingContactPerson','loadingPhone','unloadingContactPerson','unloadingPhone',
    // Планируемые даты
    'plannedLoadingDate','plannedUnloadingDate',
    // Транспорт - добавляем все поля транспорта
    'driverId','vehicleId','trailerVehicleId','carrierId',
    'wagonNumber','departureStationId','arrivalStationId',
    'flightNumber','departureAirportId','arrivalAirportId',
    // Финансы, если пользователь вернётся назад
    'totalAmount','paymentMethod','prepaymentCondition','prepaymentPercent','remainderPercent',
    'prepaymentDelayDays','remainderDelayDays','executor',
    'carrierContractRequestNumber','carrierContractRequestDate','carrierContractRequestFile',
    // Выбор грузов
    'cargoIds'
  ];

  // Helper function to auto-fill with confirmation
  const autoFillWithConfirmation = React.useCallback((fieldsToFill: Record<string, any>, fieldNames: string[]) => {
    // Упрощаем поведение: auto-fill без модалки, чтобы не мигало окно и не перекрывало UI
    // Если нужно вернуть подтверждение — можно сделать это опцией в настройках
          form.setFieldsValue(fieldsToFill);
  }, [form]);

  // Helper function to clear auto-filled fields
  const clearAutoFilledFields = React.useCallback((fieldNames: string[]) => {
    if (fieldNames.includes('loadingAddress')) loadingAddressManuallyChangedRef.current = true;
    if (fieldNames.includes('unloadingAddress')) unloadingAddressManuallyChangedRef.current = true;
    const fieldsToReset = fieldNames.reduce((acc, fieldName) => {
      acc[fieldName] = '';
      return acc;
    }, {} as Record<string, any>);
    form.setFieldsValue(fieldsToReset);
    const stepPatch: Partial<StepData> = {};
    if (fieldNames.includes('loadingAddress')) stepPatch.loadingAddress = '';
    if (fieldNames.includes('unloadingAddress')) stepPatch.unloadingAddress = '';
    if (fieldNames.includes('loadingContactPerson')) stepPatch.loadingContactPerson = '';
    if (fieldNames.includes('unloadingContactPerson')) stepPatch.unloadingContactPerson = '';
    if (fieldNames.includes('loadingPhone')) stepPatch.loadingPhone = '';
    if (fieldNames.includes('unloadingPhone')) stepPatch.unloadingPhone = '';
    if (fieldNames.includes('loadingCity')) stepPatch.loadingCity = '';
    if (fieldNames.includes('unloadingCity')) stepPatch.unloadingCity = '';
    if (Object.keys(stepPatch).length) {
      setStepData((prev) => ({ ...prev, ...stepPatch }));
    }
    message.success('Поля очищены');
  }, [form]);

  // Calculate remainder percentage automatically
  useEffect(() => {
    if (prepaymentPercent !== undefined && prepaymentPercent !== null) {
      const remainder = 100 - prepaymentPercent;
      form.setFieldsValue({ remainderPercent: remainder });
      
      // Если предоплата 100%, автоматически устанавливаем доплату в 0
      if (prepaymentPercent === 100) {
        form.setFieldsValue({ remainderDelayDays: 0 });
      }
    }
  }, [prepaymentPercent, form]);

  // Initialize form with editing route data
  useEffect(() => {
    if (visible) {
      // При открытии формы для создания нового маршрута явно устанавливаем undefined для всех полей дат
      if (!editingRoute) {
        form.setFieldsValue({
          plannedLoadingDate: undefined,
          plannedUnloadingDate: undefined,
          carrierContractRequestDate: undefined
        });
      }
    }
    
    if (visible && editingRoute) {
      const derivedApplicationId =
        editingRoute.loadingPointType === 'shipper'
          ? deriveLoadingApplicationIdForUi(editingRoute)
          : undefined;

      // Populate form with route data - only use fields that exist in Route type
      const routeData = {
        transportType: editingRoute.transportType,
        loadingPointType: editingRoute.loadingPointType === 'shipper' ? 'application' : 'warehouse',
        // В БД при выборе "Заявка" route.loadingPointType='shipper' и loadingPointId=shipperId.
        // Для UI нам нужен applicationId, поэтому берём его из связей route.applications.
        loadingPointId: editingRoute.loadingPointType === 'shipper' ? derivedApplicationId : editingRoute.loadingPointId,
        unloadingPointType: editingRoute.unloadingPointType === 'consignee' && derivedApplicationId ? 'application' : editingRoute.unloadingPointType,
        unloadingPointId: editingRoute.unloadingPointType === 'consignee' && derivedApplicationId ? derivedApplicationId : editingRoute.unloadingPointId,
        loadingAddress: editingRoute.loadingAddress,
        unloadingAddress: editingRoute.unloadingAddress,
        loadingCity: (editingRoute as any).loadingCity ?? '',
        unloadingCity: (editingRoute as any).unloadingCity ?? '',
        plannedLoadingDate: editingRoute.plannedLoadingDate,
        plannedUnloadingDate: editingRoute.plannedUnloadingDate,
        totalAmount: editingRoute.totalAmount,
        paymentMethod: editingRoute.paymentMethod,
        prepaymentCondition: editingRoute.prepaymentCondition,
        prepaymentPercent: editingRoute.prepaymentPercent,
        prepaymentDelayDays: editingRoute.prepaymentDelayDays,
        remainderDelayDays: editingRoute.mainPaymentDelayDays,
        additionalExpenses: (editingRoute.additionalExpenses || []).map((item: any) => ({
          description: item.description,
          amount: item.amount
        })),
        clientId: editingRoute.clientId,
        expeditorId: editingRoute.expeditorId,
        shipperId: editingRoute.shipperId,
        consigneeId: editingRoute.consigneeId,
        driverId: editingRoute.driverId,
        vehicleId: editingRoute.vehicleId,
        trailerVehicleId: editingRoute.trailerVehicleId ?? undefined,
        carrierId: editingRoute.carrierId,
        wagonNumber: editingRoute.wagonNumber,
        departureStationId: editingRoute.departureStation ? 
          railwayStations?.find(s => s.name === editingRoute.departureStation)?.id : null,
        arrivalStationId: editingRoute.arrivalStation ? 
          railwayStations?.find(s => s.name === editingRoute.arrivalStation)?.id : null,
        flightNumber: editingRoute.flightNumber,
        departureAirportId: editingRoute.departureAirport ? 
          airports?.find(a => a.name === editingRoute.departureAirport)?.id : null,
        arrivalAirportId: editingRoute.arrivalAirport ? 
          airports?.find(a => a.name === editingRoute.arrivalAirport)?.id : null,
        cargoIds: editingRoute.cargo?.map(rc => rc.id) || []
      };

      // Конвертируем строки в dayjs объекты для формы
      const initialCargoIds = (routeData.cargoIds || [])
        .map((id: unknown) => Number(id))
        .filter((n: number) => Number.isFinite(n));
      setSelectedIds(initialCargoIds);

      form.setFieldsValue({
        ...routeData,
        plannedLoadingDate: toDayjs(routeData.plannedLoadingDate),
        plannedUnloadingDate: toDayjs(routeData.plannedUnloadingDate),
        carrierContractRequestDate: toDayjs((routeData as any).carrierContractRequestDate)
      });
      
      // Also populate stepData for proper navigation between steps
      setStepData({
        transportType: editingRoute.transportType,
        loadingPointType: editingRoute.loadingPointType === 'shipper' ? 'application' : 'warehouse',
        loadingPointId: editingRoute.loadingPointType === 'shipper' ? derivedApplicationId : editingRoute.loadingPointId,
        unloadingPointType: editingRoute.unloadingPointType === 'consignee' && derivedApplicationId ? 'application' : editingRoute.unloadingPointType,
        unloadingPointId: editingRoute.unloadingPointType === 'consignee' && derivedApplicationId ? derivedApplicationId : editingRoute.unloadingPointId,
        loadingAddress: editingRoute.loadingAddress,
        unloadingAddress: editingRoute.unloadingAddress,
        loadingCity: (editingRoute as any).loadingCity ?? '',
        unloadingCity: (editingRoute as any).unloadingCity ?? '',
        plannedLoadingDate: editingRoute.plannedLoadingDate ? dayjs(editingRoute.plannedLoadingDate).toISOString() : undefined,
        plannedUnloadingDate: editingRoute.plannedUnloadingDate ? dayjs(editingRoute.plannedUnloadingDate).toISOString() : undefined,
        totalAmount: editingRoute.totalAmount,
        paymentMethod: editingRoute.paymentMethod,
        prepaymentCondition: editingRoute.prepaymentCondition,
        prepaymentPercent: editingRoute.prepaymentPercent,
        remainderPercent: editingRoute.mainPaymentAmount && editingRoute.totalAmount ? 
          Math.round((editingRoute.mainPaymentAmount / editingRoute.totalAmount) * 100) : undefined,
        prepaymentDelayDays: editingRoute.prepaymentDelayDays,
        remainderDelayDays: editingRoute.mainPaymentDelayDays,
        additionalExpenses: (editingRoute.additionalExpenses || []).map((item: any) => ({
          description: item.description,
          amount: item.amount
        })),
        clientId: editingRoute.clientId,
        expeditorId: editingRoute.expeditorId,
        shipperId: editingRoute.shipperId,
        consigneeId: editingRoute.consigneeId,
        responsibleLogistId: editingRoute.responsibleLogistId,
        driverId: editingRoute.driverId,
        vehicleId: editingRoute.vehicleId,
        trailerVehicleId: editingRoute.trailerVehicleId ?? undefined,
        carrierId: editingRoute.carrierId,
        wagonNumber: editingRoute.wagonNumber,
        departureStationId: editingRoute.departureStation ?
          railwayStations?.find(s => s.name === editingRoute.departureStation)?.id : undefined,
        arrivalStationId: editingRoute.arrivalStation ? 
          railwayStations?.find(s => s.name === editingRoute.arrivalStation)?.id : undefined,
        flightNumber: editingRoute.flightNumber,
        departureAirportId: editingRoute.departureAirport ? 
          airports?.find(a => a.name === editingRoute.departureAirport)?.id : undefined,
        arrivalAirportId: editingRoute.arrivalAirport ? 
          airports?.find(a => a.name === editingRoute.arrivalAirport)?.id : undefined,
        cargoIds: editingRoute.cargo?.map(rc => rc.id) || [],
        executor: editingRoute.executor,
        carrierContractRequestNumber: (editingRoute as any).carrierContractRequestNumber,
        carrierContractRequestDate: (editingRoute as any).carrierContractRequestDate,
        carrierContractRequestFile: (editingRoute as any).carrierContractRequestFile
      });
    }
  }, [visible, editingRoute, form, railwayStations, airports]);

  // prevDriver: при создании — null; при редактировании — водитель маршрута с сервера (один раз при открытии, без перезаписи при смене шага).
  useEffect(() => {
    if (!visible) {
      prevDriverInitRouteKeyRef.current = null;
      return;
    }
    if (!editingRoute?.id) {
      if (prevDriverInitRouteKeyRef.current !== 'new') {
        setPrevDriver(null);
        prevDriverInitRouteKeyRef.current = 'new';
      }
      return;
    }
    const rid = editingRoute.id;
    if (prevDriverInitRouteKeyRef.current === rid) return;
    if (!drivers?.length) return;
    const d = drivers.find((x) => x.id === editingRoute.driverId);
    setPrevDriver(d ?? null);
    prevDriverInitRouteKeyRef.current = rid;
  }, [visible, editingRoute?.id, editingRoute?.driverId, drivers]);

  // Черновик: в форме уже есть driverId, но prevDriver остаётся null — синхронизируем для автоподстановки ТС при смене водителя.
  useEffect(() => {
    if (!visible || editingRoute) return;
    if (prevDriver != null) return;
    const id = driverIdWatch;
    if (id == null || id === '') return;
    if (!drivers?.length) return;
    const d = drivers.find((x) => x.id === id);
    if (d) setPrevDriver(d);
  }, [visible, editingRoute, driverIdWatch, prevDriver, drivers]);

  // Автоматическое определение executor на основе водителя
  useEffect(() => {
    if (driverIdWatch && drivers) {
      const selectedDriver = drivers.find(d => d.id === driverIdWatch);
      if (selectedDriver) {
        if (selectedDriver.isCompanyDriver) {
          form.setFieldValue('executor', 'OWN_TRANSPORT');
          setStepData(prev => ({ ...prev, executor: 'OWN_TRANSPORT' }));
          // Автоматически заполняем ответственного логиста из водителя
          if (selectedDriver.assignedLogistId) {
            form.setFieldValue('responsibleLogistId', selectedDriver.assignedLogistId);
            setStepData(prev => ({ ...prev, responsibleLogistId: selectedDriver.assignedLogistId }));
          }
        } else if (carrierIdWatch) {
          form.setFieldValue('executor', 'CONTRACTOR');
          setStepData(prev => ({ ...prev, executor: 'CONTRACTOR' }));
        }
      }
    } else if (carrierIdWatch && !driverIdWatch) {
      // Если выбран перевозчик, но не выбран водитель
      form.setFieldValue('executor', 'CONTRACTOR');
      setStepData(prev => ({ ...prev, executor: 'CONTRACTOR' }));
    }
  }, [driverIdWatch, carrierIdWatch, drivers, form]);

  // Доп. инициализация в режиме редактирования:
  // - загружаем стопы маршрута и показываем их в "Точки маршрута (опционально)"
  // - загружаем аллокации и заполняем quantityDraft / fromStopDraft / toStopDraft
  // - выставляем cargoIds по фактически назначенным аллокациям
  useEffect(() => {
    if (!visible) {
      editHydratedForRouteIdRef.current = null;
      return;
    }
    if (!editingRoute) return;
    const apiStops: any[] = Array.isArray(routeStopsQuery.data) ? routeStopsQuery.data : [];
    const apiAllocs: any[] = Array.isArray(allocationsQuery.data) ? allocationsQuery.data : [];

    // ждём загрузку, чтобы не перетирать ввод пользователя на открытой форме
    if (!routeStopsQuery.isSuccess || !allocationsQuery.isSuccess) return;
    if (groupUiEnabled && !routeViewQuery.isSuccess) return;
    if (groupUiEnabled && editingRoute && !routeByIdQuery.isSuccess) return;

    const routeId = editingRoute.id;
    if (editHydratedForRouteIdRef.current === routeId) return;

    const viewStructureStops = routeViewQuery.data?.structure?.stops;
    const viewStopsRaw = Array.isArray(viewStructureStops)
      ? (viewStructureStops as any[])
      : [];
    const { mergedStops: canonicalStops, uidByStopId } =
      groupUiEnabled && viewStopsRaw.length > 0
        ? mergeViewStopsWithRouteStopUids(viewStopsRaw, apiStops)
        : { mergedStops: apiStops, uidByStopId: new Map<number, string>() };

    const sortedStops = [...canonicalStops].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const mainLoading =
      sortedStops.find((s) => s.type === 'loading' && s.position === 0) ||
      sortedStops.find((s) => s.type === 'loading');
    const mainUnloadingLegacy = [...sortedStops].reverse().find((s) => s.type === 'unloading');
    let mainUnloadingRef: any = mainUnloadingLegacy;

    if (editingRoute.loadingPointType === 'shipper') {
      const appIdFromStops = deriveLoadingApplicationIdForUi({
        ...editingRoute,
        stops: sortedStops
      } as unknown as Route);
      if (appIdFromStops) {
        form.setFieldsValue({ loadingPointId: appIdFromStops });
        setStepData((prev) => ({ ...prev, loadingPointId: appIdFromStops }));
      }
    }

    const additionalApiStops = sortedStops.filter((s) => {
      if (mainLoading && s.id === mainLoading.id) return false;
      if (mainUnloadingRef && s.id === mainUnloadingRef.id) return false;
      return true;
    });
    const initialOrderedStops: any[] = [];
    if (mainLoading) initialOrderedStops.push(mainLoading);
    additionalApiStops.forEach((s) => initialOrderedStops.push(s));
    if (mainUnloadingRef) initialOrderedStops.push(mainUnloadingRef);
    const initialStopIdToPos = new Map<number, number>();
    initialOrderedStops.forEach((s, pos) => {
      if (s?.id) initialStopIdToPos.set(Number(s.id), pos);
    });

    // Приводим к формату stops[] из RouteCreateForm (дополнительные стопы, без "основных")
    const additionalForState = additionalApiStops
      .slice()
      .sort((a, b) => {
        const ta = a.actualAt ? new Date(a.actualAt).getTime() : (a.plannedAt ? new Date(a.plannedAt).getTime() : Number.POSITIVE_INFINITY);
        const tb = b.actualAt ? new Date(b.actualAt).getTime() : (b.plannedAt ? new Date(b.plannedAt).getTime() : Number.POSITIVE_INFINITY);
        if (ta !== tb) return ta - tb;
        return Number(a.position ?? 0) - Number(b.position ?? 0);
      })
      .map((s, idx) => ({
      uid: s?.id ? `db-${s.id}` : makeStopUidFn(),
      id: s?.id ?? null,
      type: s.type as 'loading' | 'unloading',
      position: idx + 1,
      pointType: (s.pointType ?? null) as any,
      pointId: (s.pointId ?? null) as any,
      parentLoadingStopId: s.parentLoadingStopId ?? null,
      parentLoadingPosition: s.parentLoadingStopId ? initialStopIdToPos.get(Number(s.parentLoadingStopId)) ?? 0 : 0,
      address: s.address ?? null,
      city: (s as any).city ?? null,
      contactPerson: s.contactPerson ?? null,
      phone: s.phone ?? null,
      plannedAt: s.plannedAt ?? null,
      actualAt: s.actualAt ?? null,
      notes: s.notes ?? null
    }));
    let resolvedGroupsForEdit: DeliveryGroupDraft[] | null = null;
    if (groupUiEnabled) {
      const appGroups = routeViewQuery.data?.structure?.applicationGroups?.map((g) => ({
        id: g.id,
        applicationId: g.applicationId ?? null,
        label: g.label ?? null,
        sortOrder: g.sortOrder,
        isDefault: g.isDefault
      }));
      skipGroupsToStopsSyncRef.current = true;
      const resolvedGroups = buildDeliveryGroupsFromViewStops({
        viewStops: sortedStops,
        applicationGroups: appGroups,
        uidByStopId,
        executionHydration: (routeByIdQuery.data as { executionHydration?: RouteEditorExecutionHydration })
          ?.executionHydration
      });
      mainUnloadingRef = (() => {
        const defaultG = resolvedGroups.find((g) => g.isDefault) ?? resolvedGroups[0];
        if (!defaultG) return mainUnloadingLegacy;
        const candidates = [...defaultG.unloadingStops].sort(
          (a, b) => Number(a.position ?? 0) - Number(b.position ?? 0)
        );
        const matchPointId =
          editingRoute.unloadingPointId != null
            ? candidates.find((s) => Number(s.pointId) === Number(editingRoute.unloadingPointId))
            : undefined;
        const picked = matchPointId ?? candidates[0];
        if (!picked) return mainUnloadingLegacy;
        return {
          id: picked.id,
          type: 'unloading',
          pointType: picked.pointType,
          pointId: picked.pointId,
          address: picked.address,
          contactPerson: picked.contactPerson,
          phone: picked.phone,
          plannedAt: picked.plannedAt,
          actualAt: picked.actualAt,
          status: picked.status,
          uid: picked.uid,
          city: picked.city
        };
      })();
      const groupsWithMain = ensureDefaultGroupMainLoadingStop(resolvedGroups, {
        id: mainLoading?.id ?? null,
        uid: mainLoading?.id ? uidByStopId.get(Number(mainLoading.id)) ?? `db-${mainLoading.id}` : undefined,
        pointType: mainLoading?.pointType ?? editingRoute.loadingPointType ?? null,
        pointId: mainLoading?.pointId ?? editingRoute.loadingPointId ?? null,
        address: mainLoading?.address ?? editingRoute.loadingAddress ?? null,
        contactPerson: mainLoading?.contactPerson ?? (editingRoute as any).loadingContactPerson ?? null,
        phone: mainLoading?.phone ?? (editingRoute as any).loadingPhone ?? null,
        plannedAt: mainLoading?.plannedAt ?? editingRoute.plannedLoadingDate ?? null,
        actualAt: mainLoading?.actualAt ?? null,
        status: mainLoading?.status ?? null
      });
      const groupsFinal = ensureDefaultGroupMainUnloadingStop(groupsWithMain, {
        id: mainUnloadingRef?.id ?? null,
        uid:
          mainUnloadingRef?.uid ??
          (mainUnloadingRef?.id
            ? uidByStopId.get(Number(mainUnloadingRef.id)) ?? `db-${mainUnloadingRef.id}`
            : undefined),
        pointType: mainUnloadingRef?.pointType ?? editingRoute.unloadingPointType ?? null,
        pointId: mainUnloadingRef?.pointId ?? editingRoute.unloadingPointId ?? null,
        address: mainUnloadingRef?.address ?? editingRoute.unloadingAddress ?? null,
        contactPerson:
          mainUnloadingRef?.contactPerson ?? (editingRoute as any).unloadingContactPerson ?? null,
        phone: mainUnloadingRef?.phone ?? (editingRoute as any).unloadingPhone ?? null,
        plannedAt: mainUnloadingRef?.plannedAt ?? editingRoute.plannedUnloadingDate ?? null,
        actualAt: mainUnloadingRef?.actualAt ?? null,
        status: mainUnloadingRef?.status ?? null
      });
      const hydratedFinal = finalizeHydratedDeliveryGroups(groupsFinal);
      setDeliveryGroups(hydratedFinal);
      resolvedGroupsForEdit = hydratedFinal;
      setActiveGroupUid(hydratedFinal[0]?.uid ?? null);
      const flattened = isSequentialStopsEditorModeEnabled()
        ? flattenDeliveryGroupsToLegacyStopsEditorOrder(hydratedFinal)
        : flattenDeliveryGroupsToLegacyStops(hydratedFinal);
      setStops(flattened);
      logEditorOrderShadowDiff(hydratedFinal, 'route-edit-hydrate');
    } else {
      setStops(additionalForState.map((p, i) => ({ ...p, position: i })));
      setCargoSource({ kind: 'main' });
    }

    const lcSync =
      String((mainLoading as any)?.city || '').trim() ||
      String((editingRoute as any)?.loadingCity || '').trim() ||
      '';
    const ucSync =
      String((mainUnloadingRef as any)?.city || '').trim() ||
      String((editingRoute as any)?.unloadingCity || '').trim() ||
      '';
    setStepData((prev) => ({
      ...prev,
      loadingCity: lcSync || prev.loadingCity || '',
      unloadingCity: ucSync || prev.unloadingCity || ''
    }));
    form.setFieldsValue({
      loadingCity: lcSync || form.getFieldValue('loadingCity') || '',
      unloadingCity: ucSync || form.getFieldValue('unloadingCity') || ''
    });

    // stopId -> uiPosition (0..N+1)
    const stopIdToPos = new Map<number, number>();
    sortedStops.forEach((s, pos) => {
      if (s?.id) stopIdToPos.set(Number(s.id), pos);
    });
    const stopIdToUid = resolvedGroupsForEdit
      ? collectStopUidByIdFromGroups(resolvedGroupsForEdit)
      : new Map<number, string>();
    if (!resolvedGroupsForEdit) {
      sortedStops.forEach((s) => {
        if (s?.id) {
          const uid =
            s.clientExecutionUid ??
            s.stopUid ??
            (uidByStopId.get(Number(s.id)) ?? (s.id ? `db-${s.id}` : undefined));
          if (uid) stopIdToUid.set(Number(s.id), uid);
        }
      });
    }
    const defaultLoadingUid = resolvedGroupsForEdit?.[0]?.loadingStops[0]?.uid;
    const defaultUnloadingUid = resolvedGroupsForEdit?.[0]?.unloadingStops[0]?.uid;

    const nextQty: Record<number, number> = {};
    const nextFrom: FromStopDraftByUid = {};
    const nextTo: ToStopDraftByUid = {};
    const selected: number[] = [];
    let hydratedEdgesForEdit: AllocationEdgeDraft[] = [];

    if (multiEdgeEditorEnabled) {
      hydratedEdgesForEdit = hydrateAllocationEdgesFromApi({
        apiAllocations: apiAllocs,
        stopIdToUid,
        defaultFromStopUid: defaultLoadingUid,
        defaultToStopUid: defaultUnloadingUid
      });
      setAllocationEdges(hydratedEdgesForEdit);
      const legacy = allocationEdgesToLegacyDrafts(hydratedEdgesForEdit);
      setQuantityDraft((prev) => ({ ...prev, ...legacy.quantityDraft }));
      setFromStopDraft((prev) => ({ ...prev, ...legacy.fromStopDraft }));
      setToStopDraft((prev) => ({ ...prev, ...legacy.toStopDraft }));
      selected.push(...selectedCargoIdsFromEdges(hydratedEdgesForEdit));
    } else {
      apiAllocs.forEach((a) => {
        const cargoId = Number(a.cargoId ?? a.cargo?.id);
        if (!Number.isFinite(cargoId)) return;
        const qty = Number(a.allocatedQuantity ?? 0);
        nextQty[cargoId] = qty;
        if (qty > 0) selected.push(cargoId);

        const fromUid = a.fromStopId ? stopIdToUid.get(Number(a.fromStopId)) : undefined;
        const toUid = a.toStopId ? stopIdToUid.get(Number(a.toStopId)) : undefined;
        nextFrom[cargoId] = fromUid ?? defaultLoadingUid;
        nextTo[cargoId] = toUid ?? defaultUnloadingUid;
      });

      setQuantityDraft((prev) => ({ ...prev, ...nextQty }));
      setFromStopDraft((prev) => ({ ...prev, ...nextFrom }));
      setToStopDraft((prev) => ({ ...prev, ...nextTo }));
    }

    if (selected.length > 0) {
      const cargoIds = Array.from(new Set(selected));
      setSelectedIds(cargoIds);
      form.setFieldsValue({ cargoIds });
      setStepData((prev) => ({ ...prev, cargoIds }));
    }

    if (groupUiEnabled && resolvedGroupsForEdit) {
      const fromForActive = multiEdgeEditorEnabled
        ? allocationEdgesToLegacyDrafts(hydratedEdgesForEdit).fromStopDraft
        : nextFrom;
      setActiveLoadingStopByGroup(
        buildActiveLoadingStopByGroupFromAllocations({
          groups: resolvedGroupsForEdit,
          fromStopDraft: fromForActive,
          allocationEdges: multiEdgeEditorEnabled ? hydratedEdgesForEdit : undefined,
          preferredGroupUid: resolvedGroupsForEdit[0]?.uid ?? null
        })
      );
    }

    editHydratedForRouteIdRef.current = routeId;
  }, [
    visible,
    editingRoute,
    form,
    routeStopsQuery.data,
    routeStopsQuery.isSuccess,
    allocationsQuery.data,
    allocationsQuery.isSuccess,
    makeStopUidFn,
    groupUiEnabled,
    multiEdgeEditorEnabled,
    routeViewQuery.data?.structure?.applicationGroups,
    routeViewQuery.data?.structure?.stops,
    routeViewQuery.isSuccess,
    routeByIdQuery.data,
    routeByIdQuery.isSuccess
  ]);

  // Auto-fill addresses based on selection
  useEffect(() => {
    if (loadingPointType === 'warehouse' && loadingPointId && warehouses) {
      const selectedWarehouse = warehouses.find(w => w.id === loadingPointId);
      if (selectedWarehouse) {
        const fieldsToFill = {
          loadingAddress: selectedWarehouse.address || `${selectedWarehouse.name}, ${selectedWarehouse.city}`,
          loadingContactPerson: selectedWarehouse.contactPerson || '',
          loadingPhone: selectedWarehouse.phone || ''
        };
        const fieldNames = ['loadingAddress', 'loadingContactPerson', 'loadingPhone'];
        autoFillWithConfirmation(fieldsToFill, fieldNames);
        const lc = normalizeCityLabel(selectedWarehouse.city) || '';
        form.setFieldValue('loadingCity', lc);
        setStepData(prev => ({
          ...prev,
          loadingAddress: fieldsToFill.loadingAddress,
          loadingContactPerson: fieldsToFill.loadingContactPerson,
          loadingPhone: fieldsToFill.loadingPhone,
          loadingCity: lc
        }));
      }
    }
  }, [loadingPointType, loadingPointId, warehouses, autoFillWithConfirmation, form]);

  useEffect(() => {
    if (unloadingPointType === 'warehouse' && unloadingPointId && warehouses) {
      const selectedWarehouse = warehouses.find(w => w.id === unloadingPointId);
      if (selectedWarehouse) {
        const fieldsToFill = {
          unloadingAddress: selectedWarehouse.address || `${selectedWarehouse.name}, ${selectedWarehouse.city}`,
          unloadingContactPerson: selectedWarehouse.contactPerson || '',
          unloadingPhone: selectedWarehouse.phone || ''
        };
        const fieldNames = ['unloadingAddress', 'unloadingContactPerson', 'unloadingPhone'];
        autoFillWithConfirmation(fieldsToFill, fieldNames);
        const uc = normalizeCityLabel(selectedWarehouse.city) || '';
        form.setFieldValue('unloadingCity', uc);
        setStepData(prev => ({
          ...prev,
          unloadingAddress: fieldsToFill.unloadingAddress,
          unloadingContactPerson: fieldsToFill.unloadingContactPerson,
          unloadingPhone: fieldsToFill.unloadingPhone,
          unloadingCity: uc
        }));
      }
    }
  }, [unloadingPointType, unloadingPointId, warehouses, autoFillWithConfirmation, form]);

  useEffect(() => {
    console.log('🔍 useEffect для автозаполнения из заявки:', {
      loadingPointType,
      loadingPointId,
      applicationsLength: applications?.length,
      applications: applications?.map(a => ({ id: a.id, internalNumber: a.internalNumber }))
    });
    
    if (loadingPointType === 'application' && loadingPointId && applications) {
      const selectedApplication = applications.find(a => a.id === loadingPointId);
      console.log('📋 Найденная заявка:', selectedApplication ? {
        id: selectedApplication.id,
        internalNumber: selectedApplication.internalNumber,
        loadingAddress: selectedApplication.loadingAddress,
        loadingContactPerson: selectedApplication.loadingContactPerson,
        loadingPhone: selectedApplication.loadingPhone,
        clientId: selectedApplication.clientId,
        shipperId: selectedApplication.shipperId
      } : 'НЕ НАЙДЕНА');
      
      if (selectedApplication) {
        // Парсим loadingAddress, если в нем есть телефон и имя контактного лица
        let parsedAddress = selectedApplication.loadingAddress || '';
        let parsedContactPerson = selectedApplication.loadingContactPerson || '';
        let parsedPhone = selectedApplication.loadingPhone || '';
        
        console.log('🔍 Начало парсинга:', {
          исходныйАдрес: parsedAddress,
          исходныйКонтакт: parsedContactPerson,
          исходныйТелефон: parsedPhone
        });
        
        // Если loadingAddress содержит телефон или имя, пытаемся их извлечь
        // Проверяем, что контакт или телефон отсутствуют (null, undefined или пустая строка)
        const hasEmptyContact = !parsedContactPerson || parsedContactPerson === null || parsedContactPerson === undefined || parsedContactPerson.trim() === '';
        const hasEmptyPhone = !parsedPhone || parsedPhone === null || parsedPhone === undefined || parsedPhone.trim() === '';
        const needsParsing = parsedAddress && (hasEmptyContact || hasEmptyPhone);
        
        console.log('🔍 Проверка необходимости парсинга:', {
          hasEmptyContact,
          hasEmptyPhone,
          needsParsing,
          parsedAddressLength: parsedAddress?.length
        });
        
        if (needsParsing) {
          // Паттерн для телефона: 8 или +7, затем 10 цифр, или просто 10-11 цифр подряд
          // Более гибкий паттерн: ищем последовательности из 10-11 цифр
          const phonePattern = /(\+?7|8)?[\s-]?\(?(\d{3})\)?[\s-]?(\d{3})[\s-]?(\d{2})[\s-]?(\d{2})|(\d{10,11})/g;
          const phoneMatches = parsedAddress.match(phonePattern);
          
          if (phoneMatches && phoneMatches.length > 0) {
            // Находим последний телефон (обычно он в конце строки)
            const lastPhone = phoneMatches[phoneMatches.length - 1];
            const phoneIndex = parsedAddress.lastIndexOf(lastPhone);
            
            console.log('📞 Найден телефон:', lastPhone, 'позиция:', phoneIndex);
            
            // Извлекаем телефон
            if (!parsedPhone) {
              parsedPhone = lastPhone.trim();
            }
            
            // Удаляем телефон из адреса
            parsedAddress = parsedAddress.substring(0, phoneIndex).trim();
            console.log('📞 Адрес после удаления телефона:', parsedAddress);
          }
          
          // После удаления телефона, проверяем, есть ли имя в конце адреса
          // Имя обычно состоит из одного или двух слов (2-30 символов), начинается с заглавной буквы
          const namePattern = /\s+([А-ЯЁ][а-яё]{1,20}(?:\s+[А-ЯЁ][а-яё]{1,20})?)\s*$/;
          const nameMatch = parsedAddress.match(namePattern);
          
          if (nameMatch && nameMatch.length > 1) {
            const name = nameMatch[1].trim();
            console.log('👤 Найдено потенциальное имя:', name);
            
            // Проверяем, что это не часть адреса (не содержит типичные адресные слова)
            if (!name.match(/ул\.|улица|проспект|переулок|дом|д\.|квартира|кв\.|корпус|корп\.|строение|стр\.|станица|село|деревня|поселок/i)) {
              if (!parsedContactPerson) {
                parsedContactPerson = name;
                console.log('✅ Имя извлечено:', name);
              }
              // Удаляем имя из адреса
              parsedAddress = parsedAddress.replace(namePattern, '').trim();
              console.log('👤 Адрес после удаления имени:', parsedAddress);
            } else {
              console.log('❌ Имя содержит адресные слова, пропускаем');
            }
          }
        }
        
        console.log('✅ Результат парсинга:', {
          адрес: parsedAddress,
          контакт: parsedContactPerson,
          телефон: parsedPhone
        });
        
        const fieldsToFill = {
          loadingAddress: parsedAddress,
          loadingContactPerson: parsedContactPerson || selectedApplication.loadingContactPerson || '',
          loadingPhone: parsedPhone || selectedApplication.loadingPhone || '',
          // Автоматически заполняем контрагентов из заявки
          clientId: selectedApplication.clientId,
          expeditorId: selectedApplication.expeditorId,
          shipperId: selectedApplication.shipperId,
          consigneeId: selectedApplication.consigneeId
        };
        console.log('✅ Поля для автозаполнения (после парсинга):', fieldsToFill);
        console.log('📋 Исходные данные из заявки:', {
          loadingAddress: selectedApplication.loadingAddress,
          loadingContactPerson: selectedApplication.loadingContactPerson,
          loadingPhone: selectedApplication.loadingPhone
        });
        
        void (async () => {
          const loadingHint = (await resolveCityHintFromAddressString(fieldsToFill.loadingAddress || '')) ?? '';
          const lc = normalizeCityLabel(loadingHint) || loadingHint.trim();
          const keepManualLoadingAddress = loadingAddressManuallyChangedRef.current;

          const commonForm: Record<string, unknown> = {
            loadingContactPerson: fieldsToFill.loadingContactPerson,
            loadingPhone: fieldsToFill.loadingPhone,
            clientId: selectedApplication.clientId || undefined,
            expeditorId: selectedApplication.expeditorId || undefined,
            shipperId: selectedApplication.shipperId || undefined,
            consigneeId: selectedApplication.consigneeId || undefined
          };
          if (!keepManualLoadingAddress) {
            commonForm.loadingAddress = fieldsToFill.loadingAddress;
            commonForm.loadingCity = lc;
          }

          let unloadingPatch: Record<string, string> = {};

          if (unloadingPointType === 'application') {
            commonForm.unloadingPointId = selectedApplication.id;
            if (
              selectedApplication.unloadingAddress ||
              selectedApplication.unloadingContactPerson ||
              selectedApplication.unloadingPhone
            ) {
              const unloadFields = {
                unloadingAddress: selectedApplication.unloadingAddress || '',
                unloadingContactPerson: selectedApplication.unloadingContactPerson || '',
                unloadingPhone: selectedApplication.unloadingPhone || ''
              };
              const ucHint =
                (await resolveCityHintFromAddressString(selectedApplication.unloadingAddress || '')) ?? '';
              const uc = normalizeCityLabel(ucHint) || ucHint.trim();
              if (!unloadingAddressManuallyChangedRef.current) {
                autoFillWithConfirmation(unloadFields, ['unloadingAddress', 'unloadingContactPerson', 'unloadingPhone']);
                commonForm.unloadingCity = uc;
                unloadingPatch = {
                  unloadingAddress: unloadFields.unloadingAddress,
                  unloadingContactPerson: unloadFields.unloadingContactPerson,
                  unloadingPhone: unloadFields.unloadingPhone,
                  unloadingCity: uc
                };
              }
            }
          }

          form.setFieldsValue(commonForm);
          console.log('✅ Значения установлены в форму (города после resolve)');

          setStepData((prev) => ({
            ...prev,
            ...(!keepManualLoadingAddress
              ? {
                  loadingAddress: fieldsToFill.loadingAddress,
                  loadingCity: lc
                }
              : {}),
            loadingContactPerson: fieldsToFill.loadingContactPerson,
            loadingPhone: fieldsToFill.loadingPhone,
            ...unloadingPatch,
            clientId: selectedApplication.clientId,
            expeditorId: selectedApplication.expeditorId,
            shipperId: selectedApplication.shipperId,
            consigneeId: selectedApplication.consigneeId,
            responsibleLogistId:
              selectedApplication.responsibleLogistId || (currentUser?.id ? Number(currentUser.id) : undefined)
          }));
          console.log('✅ stepData обновлен');

          if (selectedApplication.responsibleLogistId) {
            form.setFieldValue('responsibleLogistId', selectedApplication.responsibleLogistId);
          } else if (currentUser?.id) {
            form.setFieldValue('responsibleLogistId', Number(currentUser.id));
          }
        })();
      }
    }
  }, [loadingPointType, loadingPointId, applications, autoFillWithConfirmation, form, unloadingPointType, currentUser]);

  useEffect(() => {
    if (unloadingPointType === 'application' && unloadingPointId && applications) {
      const selectedApplication = applications.find(a => a.id === unloadingPointId);
      if (selectedApplication) {
        const keepManualUnloadingAddress = unloadingAddressManuallyChangedRef.current;
        const fieldsToFill = {
          unloadingContactPerson: selectedApplication.unloadingContactPerson || selectedApplication.consignee?.contactPerson || '',
          unloadingPhone: selectedApplication.unloadingPhone || selectedApplication.consignee?.phone || '',
          consigneeId: selectedApplication.consigneeId || undefined
        };
        const fieldNames = ['unloadingContactPerson', 'unloadingPhone', 'consigneeId'];
        const autoUnloadingAddress = selectedApplication.unloadingAddress || selectedApplication.consignee?.address || '';
        if (!keepManualUnloadingAddress) {
          (fieldsToFill as Record<string, string | number | undefined>).unloadingAddress = autoUnloadingAddress;
          fieldNames.push('unloadingAddress');
        }
        autoFillWithConfirmation(fieldsToFill, fieldNames);
        setStepData(prev => ({
          ...prev,
          consigneeId: selectedApplication.consigneeId,
          ...(!keepManualUnloadingAddress
            ? {
                unloadingAddress: autoUnloadingAddress,
                unloadingCity:
                  normalizeCityLabel(selectedApplication.unloadingCity || '') ||
                  normalizeCityLabel((selectedApplication.consignee as any)?.city) ||
                  undefined
              }
            : {})
        }));
      }
    }
  }, [unloadingPointType, unloadingPointId, applications, autoFillWithConfirmation]);

  useEffect(() => {
    if (unloadingPointType === 'consignee' && unloadingPointId && consignees) {
      const selectedConsignee = consignees.find(c => c.id === unloadingPointId);
      const selectedApplication = applications?.find(a => a.id === loadingPointId);
      if (selectedConsignee) {
        const keepManualUnloadingAddress = unloadingAddressManuallyChangedRef.current;
        // Приоритет: данные из заявки (если загрузка по заявке), иначе из справочника получателя
        const fieldsToFill = {
          unloadingContactPerson: selectedApplication?.unloadingContactPerson || selectedConsignee.contactPerson || '',
          unloadingPhone: selectedApplication?.unloadingPhone || selectedConsignee.phone || '',
          // ИСПРАВЛЕНИЕ: Автоматически заполняем consigneeId при выборе контрагента как точки выгрузки
          consigneeId: selectedConsignee.id
        };
        const fieldNames = ['unloadingContactPerson', 'unloadingPhone'];
        const autoUnloadingAddress = selectedApplication?.unloadingAddress || selectedConsignee.address || '';
        if (!keepManualUnloadingAddress) {
          (fieldsToFill as Record<string, unknown>).unloadingAddress = autoUnloadingAddress;
          fieldNames.unshift('unloadingAddress');
        }
        autoFillWithConfirmation(fieldsToFill, fieldNames);
        
        // Обновляем stepData для сохранения контрагента между шагами
        const ucc = normalizeCityLabel((selectedConsignee as any).city) || '';
        if (!keepManualUnloadingAddress) {
          form.setFieldValue('unloadingCity', ucc);
        }
        setStepData(prev => ({
          ...prev,
          consigneeId: selectedConsignee.id,
          ...(!keepManualUnloadingAddress
            ? {
                unloadingAddress: autoUnloadingAddress,
                unloadingCity: ucc || prev.unloadingCity || ''
              }
            : {})
        }));
      }
    }
  }, [unloadingPointType, unloadingPointId, consignees, applications, loadingPointId, autoFillWithConfirmation, form]);

  // Если пользователь сменил грузополучателя в блоке "Контрагенты" и выбрана точка выгрузки "Получатель",
  // синхронизируем поле unloadingPointId, чтобы подставились адрес/контакты нужного контрагента
  useEffect(() => {
    if (unloadingPointType === 'consignee' && watchedConsigneeId) {
      form.setFieldsValue({ unloadingPointId: watchedConsigneeId });
    }
  }, [watchedConsigneeId, unloadingPointType, form]);

  const handleNext = async () => {
    try {
      // Валидируем только поля текущего шага
      let fieldsToValidate: string[] = [];
      
      switch (currentStep) {
        case 0:
          // Шаг 1: Основная информация
          fieldsToValidate = ['transportType'];
          if (!groupUiEnabled) {
            fieldsToValidate.push('applicationId', 'loadingPointType', 'loadingPointId');
          }
          
          // ВРЕМЕННО: Убираем валидацию контрагентов для диагностики
          // if (!editingRoute) {
          //   fieldsToValidate.push('shipperId', 'consigneeId');
          // } else {
          //   fieldsToValidate.push('shipperId', 'consigneeId');
          // }
          break;
        case 1:
          // Шаг 2: Тип транспорта - не валидируем, поля не обязательные
          fieldsToValidate = [];
          break;
        case 2:
          // Шаг 3: Финансы - только основные поля
          fieldsToValidate = ['totalAmount', 'paymentMethod'];
          break;
        case 3:
          // Шаг 4: Грузы
          fieldsToValidate = ['cargoIds'];
          break;
      }
      
      console.log('=== ОТЛАДКА ВАЛИДАЦИИ ===');
      console.log('Текущий шаг:', currentStep);
      console.log('Поля для валидации:', fieldsToValidate);
      
      // Получаем все значения формы
      const allFormValues = form.getFieldsValue();
      console.log('Все значения формы:', allFormValues);
      
      // Валидируем только нужные поля
      const values = fieldsToValidate.length > 0 ? await form.validateFields(fieldsToValidate) : {};
      console.log('Результат валидации:', values);

      const mergedForStep = { ...stepData, ...allFormValues, ...values };
      if (!groupUiEnabled && currentStep < steps.length - 1) {
        const advanceGate = evaluateLegacyStepAdvanceGate({
          groupUiEnabled,
          currentStep,
          formSlice: mergedForStep,
          editingRouteId: editingRoute?.id,
          successorContext: successorRouteCreateContext,
          pendingWarehouseVerification: pendingRestoredWarehouseRef.current,
          confirmedSourceKey: confirmedLegacySourceKey
        });
        if (!advanceGate.ok) {
          message.error(advanceGate.message);
          return;
        }
        const newStepData = mergedForStep;
        console.log('Новые данные шага:', newStepData);
        setStepData(newStepData);
        setCurrentStep(advanceGate.nextStep);
        console.log('Переход на шаг:', advanceGate.nextStep);
        return;
      }

      const newStepData = mergedForStep;
      console.log('Новые данные шага:', newStepData);
      setStepData(newStepData);

      if (currentStep >= steps.length - 1) {
        return;
      }

      const nextStep = currentStep + 1;
      if (groupUiEnabled && nextStep >= LEGACY_CARGO_STEP_INDEX) {
        const groupGate = evaluateGroupCargoStepEntryGate(deliveryGroups, {
          membershipForGroup: resolveGroupMembershipForNavigation
        });
        if (!groupGate.ok) {
          message.error(groupGate.message);
          return;
        }
      }

      setCurrentStep(nextStep);
      console.log('Переход на шаг:', nextStep);
    } catch (error: any) {
      console.error('=== ОШИБКА ВАЛИДАЦИИ ===');
      console.error('Детали ошибки:', error);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    try {
      if (savingExecutionTopologyRef.current) {
        message.warning('Сохранение маршрута уже выполняется');
        return;
      }
      if (groupUiEnabled && groupValidationIssues.length > 0) {
        message.error(groupValidationIssues[0]?.message || 'Исправьте ошибки групп маршрута');
        return;
      }
      const cargoApplicationById: Record<number, number | undefined> = {};
      const allCargoRows = [
        ...(Array.isArray(availableCargo) ? (availableCargo as any[]) : []),
        ...(((editingRoute?.cargo as any[]) ?? []) as any[])
      ];
      for (const row of allCargoRows) {
        const id = Number(row?.id);
        if (!Number.isFinite(id)) continue;
        cargoApplicationById[id] = Number(row?.applicationId ?? row?.application?.id);
      }

      const cargoCapById: Record<number, number> = {};
      for (const row of allCargoRows) {
        const id = Number(row?.id);
        if (!Number.isFinite(id)) continue;
        cargoCapById[id] = Number(
          row?.availableQuantity != null ? row.availableQuantity : row?.quantity ?? 0
        );
      }

      if (multiEdgeEditorEnabled) {
        const multiIssues = validateMultiEdgeAllocations({
          edges: resolvedAllocationEdges,
          cargoCapById,
          cargoAvailableById: cargoCapById
        });
        const multiMsg = formatMultiEdgeValidationMessage(multiIssues);
        if (multiMsg) {
          message.error(multiMsg);
          return;
        }
      }

      const executionIssues = groupUiEnabled
        ? validateStep4ExecutionBindings({
            groups: deliveryGroups,
            selectedIds: selectedIds.map(Number).filter(Number.isFinite),
            fromStopDraft,
            toStopDraft,
            quantityDraft,
            allocationEdges: multiEdgeEditorEnabled ? resolvedAllocationEdges : undefined,
            cargoApplicationById,
            cargoCapById
          })
        : [];
      if (executionIssues.length > 0) {
        message.error(executionIssues[0]?.message || 'Исправьте распределение грузов');
        return;
      }
      const values = await form.validateFields();
      let finalData: any = { ...stepData, ...values };

      if (!groupUiEnabled) {
        const submitGate = evaluateLegacyRouteCreateSubmitGate({
          formSlice: finalData,
          editingRouteId: editingRoute?.id,
          successorContext: successorRouteCreateContext,
          groupUiEnabled,
          pendingWarehouseVerification: pendingRestoredWarehouseRef.current,
          confirmedSourceKey: confirmedLegacySourceKey
        });
        if (!submitGate.ok) {
          message.error(submitGate.message);
          setCurrentStep(submitGate.redirectStep);
          return;
        }
      }

      const cargoIdsFromState = selectedIds.map((n) => Number(n)).filter((n) => Number.isFinite(n));
      const cargoIdsFromForm = Array.isArray(values.cargoIds)
        ? (values.cargoIds as number[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
        : [];
      finalData.cargoIds =
        cargoIdsFromState.length > 0 ? cargoIdsFromState : cargoIdsFromForm;
      if (finalData.cargoIds.length > 0) {
        form.setFieldValue('cargoIds', finalData.cargoIds);
      }

      const driverBusyRoute = findDriverBusyRoute({
        routes: selectedDriverRoutesData?.routes ?? [],
        loadingDate: finalData.plannedLoadingDate,
        unloadingDate: finalData.plannedUnloadingDate,
        excludeRouteId: editingRoute?.id
      });
      if (driverBusyRoute) {
        message.error(driverBusyRoute.message);
        return;
      }

      const cityRes = await ensureRouteLoadingUnloadingCities({
        loadingAddress: finalData.loadingAddress,
        unloadingAddress: finalData.unloadingAddress,
        loadingCity: finalData.loadingCity,
        unloadingCity: finalData.unloadingCity
      });
      finalData = { ...finalData, loadingCity: cityRes.loadingCity, unloadingCity: cityRes.unloadingCity };
      form.setFieldsValue({ loadingCity: cityRes.loadingCity, unloadingCity: cityRes.unloadingCity });
      setStepData((prev) => ({
        ...prev,
        loadingCity: cityRes.loadingCity,
        unloadingCity: cityRes.unloadingCity
      }));

      if (!String(cityRes.loadingCity).trim() || !String(cityRes.unloadingCity).trim()) {
        message.warning('Город не определён, маршрут будет создан без города');
      }

      const formValues = form.getFieldsValue();
      console.log('FINAL FORM VALUES', formValues);

      console.log('Step data:', stepData);
      console.log('Form values:', values);
      console.log('FINAL DATA:', finalData);

      if (editingRoute) {
        // Режим редактирования — обновляем маршрут + стопы + аллокации (как в создании)

        const finalForOperationalPatch = {
          ...finalData,
          carrierContractRequestDate: finalData.carrierContractRequestDate
            ? dayjs(finalData.carrierContractRequestDate).startOf('day').toISOString()
            : finalData.carrierContractRequestDate
        };

        const updateData = buildRouteOperationalUpdatePatch({
          routeId: editingRoute.id,
          editingRoute: editingRoute as unknown as Record<string, unknown>,
          finalData: finalForOperationalPatch,
          applications: applications as any[],
          currentUserId: currentUser?.id ? Number(currentUser.id) : undefined,
          railwayStations,
          airports
        });

        const hasOperationalChanges = Object.keys(updateData).some((k) => k !== 'id');
        console.log('Route operational update patch:', updateData);
        if (hasOperationalChanges) {
          await updateRoute(updateData as any).unwrap();
        }

        const routeLc = String(finalData.loadingCity || '').trim();
        const routeUc = String(finalData.unloadingCity || '').trim();

        let payloadStops: any[];
        let normalizedStopsForAlloc: ReturnType<typeof buildRouteStopsSavePayload> = [];
        let allocationLinksForSave: LoadingUnloadingAllocationLink[] = [];
        let executionSnapshotForEdit: ReturnType<typeof buildImmutableExecutionSnapshot> | undefined;

        if (groupUiEnabled) {
          const built = buildGroupAwareStopsForSave(finalData);
          if (built.issues.length > 0) {
            message.error(built.issues[0]?.message || 'Исправьте топологию точек маршрута');
            return;
          }
          payloadStops = built.payload;
          normalizedStopsForAlloc = built.normalized;
          allocationLinksForSave = built.allocationLinks;
          executionSnapshotForEdit = built.snapshot;
        } else {
          payloadStops = [];
          payloadStops.push({
            type: 'loading',
            position: 0,
            address: finalData.loadingAddress || null,
            city: routeLc || null,
            contactPerson: finalData.loadingContactPerson || null,
            phone: finalData.loadingPhone || null,
            plannedAt: finalData.plannedLoadingDate || null,
            pointType: finalData.loadingPointType || null,
            pointId: finalData.loadingPointId || null,
            parentLoadingPosition: null
          });
          (stops || []).forEach((s, idx) => {
            const sc = s.city ? String(s.city).trim() : '';
            const fallback = s.type === 'loading' ? routeLc : routeUc;
            payloadStops.push({
              id: s.id ?? undefined,
              type: s.type,
              position: idx + 1,
              address: s.address || null,
              city: normalizeCityLabel(sc) || fallback || null,
              contactPerson: s.contactPerson || null,
              phone: s.phone || null,
              plannedAt: s.plannedAt || null,
              pointType: s.pointType || null,
              pointId: s.pointId || null,
              parentLoadingPosition: s.type === 'unloading' ? s.parentLoadingPosition ?? 0 : null,
              parentLoadingStopId: s.parentLoadingStopId ?? null
            });
          });
          payloadStops.push({
            type: 'unloading',
            position: (stops || []).length + 1,
            address: finalData.unloadingAddress || null,
            city: routeUc || null,
            contactPerson: finalData.unloadingContactPerson || null,
            phone: finalData.unloadingPhone || null,
            plannedAt: finalData.plannedUnloadingDate || null,
            pointType: finalData.unloadingPointType || null,
            pointId: finalData.unloadingPointId || null,
            parentLoadingPosition: 0
          });
        }

        console.log('STOPS PAYLOAD:', payloadStops);

        const compatSnapshotForEdit = executionSnapshotForEdit
          ? toCompatibilityExecutionSnapshot(executionSnapshotForEdit)
          : undefined;

        savingExecutionTopologyRef.current = true;
        acquireExecutionTopologySaveLock();
        try {
        const upserted = await upsertRouteStops({
          routeId: editingRoute.id,
          stops: payloadStops,
          allocationLinks: allocationLinksForSave.length > 0 ? allocationLinksForSave : undefined,
          executionSnapshot: compatSnapshotForEdit
        }).unwrap();
        const reboundSnapshotForAlloc =
          upserted?.executionSnapshot ?? compatSnapshotForEdit;
        const savedStops = (upserted || []).map((s: any) => ({
          id: s.id,
          position: s.position,
          clientExecutionUid: s.clientExecutionUid,
          stopUid: s.stopUid
        })) as Array<{
          id: number;
          position: number;
          clientExecutionUid?: string;
          stopUid?: string;
        }>;

        // 2) Сохраняем аллокации: выбранные грузы + удаляем снятые (quantity=0)
        const selectedIdsRaw = finalData.cargoIds;
        const selectedIdsForEdit: number[] = Array.isArray(selectedIdsRaw)
          ? (selectedIdsRaw as number[]).map((x) => Number(x)).filter((n) => Number.isFinite(n))
          : [];
        const existingIds: number[] = (Array.isArray(allocationsQuery.data) ? allocationsQuery.data : [])
          .map((a: any) => Number(a.cargoId ?? a.cargo?.id))
          .filter((x: any) => Number.isFinite(x));
        const allIds = Array.from(new Set([...(selectedIdsForEdit || []), ...(existingIds || [])]));

        const positionByStopUidForEdit = groupUiEnabled
          ? positionByStopUidFromSaveRows(normalizedStopsForAlloc)
          : buildLegacyPositionByStopUid({
              mainLoadingUid: legacyMainLoadingUid,
              stops,
              mainUnloadingUid: legacyMainUnloadingUid
            });
        const defaultFromUid = groupUiEnabled
          ? normalizedStopsForAlloc.find((s) => s.type === 'loading')?._stopUid
          : legacyMainLoadingUid;
        const defaultToUid = groupUiEnabled
          ? normalizedStopsForAlloc.filter((s) => s.type === 'unloading').slice(-1)[0]?._stopUid
          : legacyMainUnloadingUid;

        const allocItems = mapUidDraftsToStopIds({
          cargoIds: allIds,
          fromStopDraft,
          toStopDraft,
          quantityDraft,
          allocationEdges: multiEdgeEditorEnabled ? resolvedAllocationEdges : undefined,
          positionByStopUid: positionByStopUidForEdit,
          savedStops,
          defaultFromStopUid: defaultFromUid,
          defaultToStopUid: defaultToUid,
          isSelected: (cargoId) =>
            selectedIdsForEdit.some((sid) => Number(sid) === Number(cargoId))
        }).map((row) => ({
          ...row,
          quantity:
            multiEdgeEditorEnabled
              ? row.quantity
              : selectedIdsForEdit.some((sid) => Number(sid) === Number(row.cargoId))
                ? resolveCargoQuantityForAllocation(row.cargoId, quantityDraft, [
                    availableCargo as any[] | undefined,
                    editingRoute?.cargo as any[] | undefined
                  ])
                : 0
        }));
        const mappingIssue = findAllocationStopMappingIssue(allocItems);
        if (mappingIssue) {
          message.error(mappingIssue);
          return;
        }

        console.log('ALLOCATIONS TO SEND:', allocItems);
        console.log('ALLOCATIONS PAYLOAD DEBUG (edit):', {
          routeId: editingRoute.id,
          items: allocItems.map((row) => ({
            cargoId: row.cargoId,
            allocatedQuantity: row.quantity,
            fromStopId: row.fromStopId,
            toStopId: row.toStopId
          }))
        });
        try {
          const allocResponseEdit = await createAllocations({
            routeId: editingRoute.id,
            allocations: allocItems,
            executionSnapshot: reboundSnapshotForAlloc
          }).unwrap();
          console.log('ALLOCATIONS RESPONSE:', allocResponseEdit);
        } catch (allocInnerErr: any) {
          console.error('ALLOCATIONS ERROR:', allocInnerErr);
          const allocErrorMessage =
            (typeof allocInnerErr?.data?.error === 'string' && allocInnerErr.data.error.trim()) ||
            (typeof allocInnerErr?.error === 'string' && allocInnerErr.error.trim()) ||
            (typeof allocInnerErr?.message === 'string' && allocInnerErr.message.trim()) ||
            'Не удалось сохранить распределение грузов';
          message.error(allocErrorMessage);
          return;
        }
        } finally {
          savingExecutionTopologyRef.current = false;
          releaseExecutionTopologySaveLock();
        }

        if (
          editingRoute.carrierId &&
          !editingRoute.isCompanyRoute &&
          String(editingRoute.executor || '').toUpperCase() === 'CONTRACTOR'
        ) {
          await loadPenaltiesForRoute(editingRoute.id);
        }
        await Promise.all([
          allocationsQuery.refetch(),
          routeStopsQuery.refetch(),
          groupUiEnabled ? routeViewQuery.refetch() : Promise.resolve()
        ]);
        message.success('Маршрут успешно обновлен');
        setTopologySavePolicyError(null);
      } else {
        // Режим создания: без грузов с ненулевой аллокацией маршрут не создаём
        const selectedIdsForCreate: number[] = Array.isArray(finalData.cargoIds)
          ? (finalData.cargoIds as number[]).map((x) => Number(x)).filter((n) => Number.isFinite(n))
          : [];
        const allocationsPreview = multiEdgeEditorEnabled
          ? resolvedAllocationEdges
              .filter((e) => e.quantity > 0)
              .map((e) => ({ cargoId: Number(e.cargoId), quantity: e.quantity }))
          : selectedIdsForCreate
              .map((id) => {
                const cid = Number(id);
                const resolvedQuantity = resolveCargoQuantityForAllocation(cid, quantityDraft, [
                  availableCargo as any[] | undefined
                ]);
                return { cargoId: cid, quantity: resolvedQuantity };
              })
              .filter((a) => a.quantity > 0);
        if (
          selectedIds.length === 0 ||
          selectedIdsForCreate.length === 0 ||
          allocationsPreview.length === 0
        ) {
          message.error('Добавьте хотя бы один груз в маршрут и укажите количество');
          setCargoStepError(true);
          setCurrentStep(3);
          form.setFields([
            {
              name: 'cargoIds',
              errors: ['Добавьте хотя бы один груз в маршрут']
            }
          ]);
          return;
        }
        setCargoStepError(false);

        if (multiEdgeEditorEnabled) {
          const capById: Record<number, number> = {};
          for (const row of Array.isArray(availableCargo) ? (availableCargo as any[]) : []) {
            const id = Number(row?.id);
            if (!Number.isFinite(id)) continue;
            capById[id] = Number(
              row?.availableQuantity != null ? row.availableQuantity : row?.quantity ?? 0
            );
          }
          const multiMsg = formatMultiEdgeValidationMessage(
            validateMultiEdgeAllocations({
              edges: resolvedAllocationEdges,
              cargoCapById: capById,
              cargoAvailableById: capById
            })
          );
          if (multiMsg) {
            message.error(multiMsg);
            setCargoStepError(true);
            setCurrentStep(3);
            return;
          }
        } else {
          const qtyValidationError = validateCargoQuantitiesForCreate(
            selectedIdsForCreate,
            quantityDraft,
            availableCargo as any[] | undefined
          );
          if (qtyValidationError) {
            message.error(qtyValidationError);
            setCargoStepError(true);
            setCurrentStep(3);
            return;
          }
        }

        // Режим создания - используем существующую логику
        // Определяем грузоотправителя и заявки при выборе заявки как точки погрузки
        let resolvedLoadingPointType: 'warehouse' | 'shipper' = finalData.loadingPointType === 'application' ? 'shipper' : 'warehouse';
        let resolvedLoadingPointId: number = finalData.loadingPointId!;
        let resolvedShipperId: number | undefined = finalData.shipperId;

        const applicationIdSet = new Set<number>();

        if (finalData.loadingPointType === 'application' && finalData.loadingPointId != null) {
          const selectedApp = applications.find((app) => app.id === finalData.loadingPointId);
          if (selectedApp?.shipperId) {
            resolvedLoadingPointId = selectedApp.shipperId;
            resolvedShipperId = selectedApp.shipperId;
          }
          const aid = Number(finalData.loadingPointId);
          if (Number.isFinite(aid) && aid > 0) {
            applicationIdSet.add(aid);
          }
        }

        const formApplicationIdRaw = form.getFieldValue('applicationId');
        if (formApplicationIdRaw != null && formApplicationIdRaw !== '') {
          const n = Number(formApplicationIdRaw);
          if (Number.isFinite(n) && n > 0) applicationIdSet.add(n);
        }

        if (cargoAppFilter != null) {
          const n = Number(cargoAppFilter);
          if (Number.isFinite(n) && n > 0) applicationIdSet.add(n);
        }

        if (Array.isArray(finalData.cargoIds) && finalData.cargoIds.length > 0 && availableCargo) {
          const cargoList = availableCargo as any[];
          for (const rawCid of finalData.cargoIds) {
            const cid = Number(rawCid);
            if (!Number.isFinite(cid)) continue;
            const cargoRow = cargoList.find((c: any) => Number(c.id) === cid);
            if (cargoRow?.applicationId != null) {
              const aid = Number(cargoRow.applicationId);
              if (Number.isFinite(aid) && aid > 0) applicationIdSet.add(aid);
            }
          }
        }

        const applicationIds: number[] | undefined =
          applicationIdSet.size > 0 ? Array.from(applicationIdSet) : undefined;

        if (!applicationIds || applicationIds.length === 0) {
          message.error('Не выбрана заявка для маршрута');
          setCurrentStep(0);
          return;
        }

        // Определяем consigneeId правильно
        let resolvedConsigneeId: number | undefined = finalData.consigneeId;
        if (finalData.unloadingPointType === 'consignee') {
          resolvedConsigneeId = finalData.unloadingPointId;
        } else if (finalData.unloadingPointType === 'application' && finalData.unloadingPointId) {
          resolvedConsigneeId = applications.find((app) => app.id === finalData.unloadingPointId)?.consigneeId;
        }

        // Определяем responsibleLogistId: из формы, из заявки или текущий пользователь
        let resolvedResponsibleLogistId: number | undefined = finalData.responsibleLogistId;
        if (!resolvedResponsibleLogistId && finalData.loadingPointType === 'application') {
          const selectedApp = applications.find(app => app.id === finalData.loadingPointId);
          if (selectedApp?.responsibleLogistId) {
            resolvedResponsibleLogistId = selectedApp.responsibleLogistId;
          }
        }
        if (!resolvedResponsibleLogistId && currentUser?.id) {
          resolvedResponsibleLogistId = Number(currentUser.id);
        }

        const routeRequest: CreateRouteRequest & { applicationIds?: number[] } = {
          transportType: finalData.transportType!,
          loadingPointType: resolvedLoadingPointType,
          loadingPointId: resolvedLoadingPointId,
          unloadingPointType: finalData.unloadingPointType!,
          unloadingPointId: finalData.unloadingPointId!,
          loadingAddress: finalData.loadingAddress,
          unloadingAddress: finalData.unloadingAddress,
          loadingCity: finalData.loadingCity ? String(finalData.loadingCity).trim() : undefined,
          unloadingCity: finalData.unloadingCity ? String(finalData.unloadingCity).trim() : undefined,
          loadingContactPerson: finalData.loadingContactPerson,
          loadingPhone: finalData.loadingPhone,
          unloadingContactPerson: finalData.unloadingContactPerson,
          unloadingPhone: finalData.unloadingPhone,
          plannedLoadingDate: finalData.plannedLoadingDate,
          plannedUnloadingDate: finalData.plannedUnloadingDate,
          totalAmount: finalData.totalAmount,
          paymentMethod: finalData.paymentMethod,
          prepaymentCondition: finalData.prepaymentCondition,
          prepaymentPercent: finalData.prepaymentPercent,
          prepaymentDelayDays: finalData.prepaymentDelayDays,
          mainPaymentAmount: finalData.totalAmount && finalData.remainderPercent ? 
            (finalData.totalAmount * finalData.remainderPercent / 100) : undefined,
          mainPaymentDelayDays: finalData.remainderDelayDays,
          additionalExpenses: [],
          clientId: finalData.clientId,
          expeditorId: finalData.expeditorId,
          shipperId: resolvedShipperId,
          consigneeId: resolvedConsigneeId,
          responsibleLogistId: resolvedResponsibleLogistId,
          driverId: finalData.driverId,
          vehicleId: finalData.vehicleId,
          trailerVehicleId: finalData.trailerVehicleId,
          carrierId: finalData.carrierId,
          wagonNumber: finalData.wagonNumber,
          departureStation: finalData.departureStationId ? 
            railwayStations?.find(s => s.id === finalData.departureStationId)?.name : undefined,
          arrivalStation: finalData.arrivalStationId ? 
            railwayStations?.find(s => s.id === finalData.arrivalStationId)?.name : undefined,
          flightNumber: finalData.flightNumber,
          departureAirport: finalData.departureAirportId ? 
            airports?.find(a => a.id === finalData.departureAirportId)?.name : undefined,
          arrivalAirport: finalData.arrivalAirportId ? 
            airports?.find(a => a.id === finalData.arrivalAirportId)?.name : undefined,
          // Не отправляем cargoIds на создание маршрута, чтобы не споткнуться о валидацию основной точки.
          // Грузы добавим отдельным шагом через allocations после сохранения стопов.
          cargoIds: [],
          applicationIds,
          // Исполнитель маршрута (определяется автоматически на backend, но отправляем для совместимости)
          isCompanyRoute: values.executor === 'OWN_TRANSPORT',
          executor: values.executor || 'CONTRACTOR',
          // Поля договор-заявки с перевозчиком
          carrierContractRequestNumber: finalData.carrierContractRequestNumber,
          carrierContractRequestDate: finalData.carrierContractRequestDate
            ? dayjs(finalData.carrierContractRequestDate).startOf('day').toISOString()
            : undefined,
          carrierContractRequestFile: finalData.carrierContractRequestFile
        };
        const routeRequestWithSuccessor = mergeSuccessorContextIntoCreateRouteBody(
          routeRequest,
          successorRouteCreateContext
        );

        console.log('FINAL selectedIds:', selectedIds);
        console.log('Route request to send:', routeRequest);
        console.log('ROUTE CREATE applicationIds (payload):', routeRequest.applicationIds);

        const routeLc = String(finalData.loadingCity || '').trim();
        const routeUc = String(finalData.unloadingCity || '').trim();
        let payloadStops: any[];
        let allocationLinksForCreate: Array<{ fromLoadingPosition: number; toUnloadingPosition: number }> = [];
        let executionSnapshotForCreate: ReturnType<typeof buildImmutableExecutionSnapshot> | undefined;

        if (groupUiEnabled) {
          const built = buildGroupAwareStopsForSave(finalData);
          if (built.issues.length > 0) {
            message.error(built.issues[0]?.message || 'Исправьте топологию точек маршрута');
            return;
          }
          payloadStops = built.payload;
          allocationLinksForCreate = built.allocationLinks;
          executionSnapshotForCreate = built.snapshot;
        } else {
          payloadStops = [];
          payloadStops.push({
            type: 'loading',
            position: 0,
            address: finalData.loadingAddress || null,
            city: routeLc || null,
            contactPerson: finalData.loadingContactPerson || null,
            phone: finalData.loadingPhone || null,
            plannedAt: finalData.plannedLoadingDate || null,
            pointType: finalData.loadingPointType || null,
            pointId: finalData.loadingPointId || null,
            parentLoadingPosition: null
          });
          (stops || []).forEach((s, idx) => {
            const sc = s.city ? String(s.city).trim() : '';
            const fallback = s.type === 'loading' ? routeLc : routeUc;
            payloadStops.push({
              type: s.type,
              position: idx + 1,
              address: s.address || null,
              city: normalizeCityLabel(sc) || fallback || null,
              contactPerson: s.contactPerson || null,
              phone: s.phone || null,
              plannedAt: s.plannedAt || null,
              pointType: s.pointType || null,
              pointId: s.pointId || null,
              parentLoadingPosition: s.type === 'unloading' ? s.parentLoadingPosition ?? 0 : null,
              parentLoadingStopId: s.parentLoadingStopId ?? null
            });
          });
          payloadStops.push({
            type: 'unloading',
            position: (stops || []).length + 1,
            address: finalData.unloadingAddress || null,
            city: routeUc || null,
            contactPerson: finalData.unloadingContactPerson || null,
            phone: finalData.unloadingPhone || null,
            plannedAt: finalData.plannedUnloadingDate || null,
            pointType: finalData.unloadingPointType || null,
            pointId: finalData.unloadingPointId || null,
            parentLoadingPosition: 0
          });
        }

        const idsForAlloc: number[] = Array.isArray(finalData.cargoIds)
          ? (finalData.cargoIds as number[]).map((x) => Number(x)).filter((n) => Number.isFinite(n))
          : [];
        let allocations = idsForAlloc.map((id) => {
          const cargoId = Number(id);
          const resolvedQuantity = quantityFromDraft(cargoId, quantityDraft);
          return { cargoId, quantity: resolvedQuantity };
        }).filter((item) => item.quantity > 0);

        if (groupUiEnabled && executionSnapshotForCreate) {
          allocations = executionSnapshotForCreate.allocations.map((a) => ({
            cargoId: a.cargoId,
            quantity: a.quantity
          }));
        } else if (allocations.length === 0 && idsForAlloc.length > 0) {
          allocations = idsForAlloc
            .map((id) => ({
              cargoId: Number(id),
              quantity: quantityFromDraft(Number(id), quantityDraft) || 1
            }))
            .filter((item) => item.quantity > 0);
        }

        const cargoApplicationById: Record<number, number | undefined> = {};
        (Array.isArray(availableCargo) ? (availableCargo as any[]) : []).forEach((c) => {
          const cid = Number(c.id);
          if (Number.isFinite(cid)) {
            cargoApplicationById[cid] = Number(c.applicationId ?? c.application?.id);
          }
        });

        const cargoCapByIdCreate: Record<number, number> = {};
        for (const row of Array.isArray(availableCargo) ? (availableCargo as any[]) : []) {
          const id = Number(row?.id);
          if (!Number.isFinite(id)) continue;
          cargoCapByIdCreate[id] = Number(
            row?.availableQuantity != null ? row.availableQuantity : row?.quantity ?? 0
          );
        }

        const validateExecution = buildRouteCreateExecutionValidator({
          groupUiEnabled,
          groups: deliveryGroups,
          mainLoading: executionMainStopFields.mainLoading,
          mainUnloading: executionMainStopFields.mainUnloading,
          selectedIds: idsForAlloc,
          fromStopDraft,
          toStopDraft,
          quantityDraft,
          allocationEdges: multiEdgeEditorEnabled ? resolvedAllocationEdges : undefined,
          cargoApplicationById,
          cargoCapById: cargoCapByIdCreate
        });

        const preValidation = validateExecution();
        if (!preValidation.ok) {
          message.error(preValidation.message ?? 'Маршрут не может быть создан');
          setCurrentStep(3);
          return;
        }

        if (groupUiEnabled && executionSnapshotForCreate) {
          const stableValidation = validateStableExecutionSnapshot(executionSnapshotForCreate);
          if (!stableValidation.ok) {
            message.error(stableValidation.message ?? 'Execution topology inconsistent');
            setCurrentStep(3);
            return;
          }
        }

        console.log('STOPS PAYLOAD:', payloadStops);

        savingExecutionTopologyRef.current = true;
        acquireExecutionTopologySaveLock();
        let pipelineResult;
        try {
          pipelineResult = await executeRouteCreatePipeline({
            routeRequest: routeRequestWithSuccessor as unknown as Record<string, unknown>,
            payloadStops,
            allocationLinks:
              allocationLinksForCreate.length > 0 ? allocationLinksForCreate : undefined,
            executionSnapshot: executionSnapshotForCreate,
            allocations,
            validateExecution,
            deferredResourceAssignment: groupUiEnabled
              ? {
                  driverId: finalData.driverId,
                  vehicleId: finalData.vehicleId,
                  trailerVehicleId: finalData.trailerVehicleId,
                  carrierId: finalData.carrierId
                }
              : undefined,
            createRoute: (body) => createRoute(body as any),
            startExecutionSession: groupUiEnabled && executionSnapshotForCreate
              ? (args) => startExecutionSession(args)
              : undefined,
            rollbackExecutionSession: groupUiEnabled
              ? (args) => rollbackExecutionSession(args)
              : undefined,
            upsertRouteStops: (args) => upsertRouteStops(args),
            createAllocations: (args) => createAllocations(args),
            deleteRoute: (routeId) => deleteRoute(routeId),
            assignRouteResources: groupUiEnabled
              ? ({ routeId, assignment }) =>
                  updateRoute({
                    id: routeId,
                    driverId: assignment.driverId,
                    vehicleId: assignment.vehicleId,
                    trailerVehicleId: assignment.trailerVehicleId,
                    carrierId: assignment.carrierId
                  } as any)
              : undefined,
            mapAllocationsToStopIds: (rows, savedStops) => {
              if (executionSnapshotForCreate) {
                return mapSnapshotAllocationsToStopIds(executionSnapshotForCreate, savedStops);
              }
              const positionByStopUid = legacyPositionByStopUid
                ?? buildLegacyPositionByStopUid({
                    mainLoadingUid: legacyMainLoadingUid,
                    stops,
                    mainUnloadingUid: legacyMainUnloadingUid
                  });
              return mapUidDraftsToStopIds({
                cargoIds: rows.map((r) => Number(r.cargoId)),
                fromStopDraft,
                toStopDraft,
                quantityDraft,
                allocationEdges: multiEdgeEditorEnabled ? resolvedAllocationEdges : undefined,
                positionByStopUid,
                savedStops,
                defaultFromStopUid: legacyMainLoadingUid,
                defaultToStopUid: legacyMainUnloadingUid
              });
            }
          });
        } catch (pipelineErr) {
          const errMsg =
            pipelineErr instanceof RouteCreatePipelineError
              ? pipelineErr.message
              : (pipelineErr as any)?.data?.error || 'Не удалось сохранить маршрут';
          message.error(errMsg);
          return;
        } finally {
          savingExecutionTopologyRef.current = false;
          releaseExecutionTopologySaveLock();
        }

        const newRouteId = pipelineResult.routeId;
        const cr = { id: newRouteId, number: pipelineResult.routeNumber };
        const createdExecutor = String(values.executor ?? 'CONTRACTOR').toUpperCase();
        const createdCarrierId = finalData.carrierId;
        const createdIsCompany = values.executor === 'OWN_TRANSPORT';
        const shouldStayForPenalties =
          createdExecutor === 'CONTRACTOR' && Boolean(createdCarrierId) && !createdIsCompany;

        if (shouldStayForPenalties) {
          setPostCreateRouteId(newRouteId);
          await loadPenaltiesForRoute(newRouteId);
          setPostCreatePenaltiesStep(true);
          setCurrentStep(2);
          message.success(
            `Маршрут ${String(cr.number ?? `#${newRouteId}`)} создан. При необходимости настройте штрафы, затем нажмите «Закрыть».`
          );
          return;
        }

        message.success(`Маршрут ${String(cr.number ?? `#${newRouteId}`)} успешно создан`);
      }

      onSuccess();
      handleCancel();
    } catch (error: any) {
      console.error('Error saving route:', error);
      const policyMsg =
        mapRejectMessage(error?.data) ??
        (typeof error?.data?.error === 'string' ? error.data.error : null) ??
        (typeof error?.message === 'string' ? error.message : null);
      if (policyMsg) setTopologySavePolicyError(policyMsg);
      message.error(policyMsg || 'Ошибка при сохранении маршрута');
    }
  };

  const handleCancel = () => {
    if (postCreatePenaltiesStep) {
      onSuccess();
    }
    resetCarrierPenaltiesState();
    resetExecutionTopologyDraft();
    setTopologySavePolicyError(null);
    setCurrentStep(0);
    setCargoStepError(false);
    setSelectedIds([]);
    setStepData({});
    // Сбрасываем форму и явно устанавливаем undefined для всех полей дат
    form.resetFields();
    form.setFieldsValue({
      plannedLoadingDate: undefined,
      plannedUnloadingDate: undefined,
      carrierContractRequestDate: undefined
    });
    onCancel();
  };

  /** Полный сброс черновика и локального состояния (создание маршрута, форма открыта). */
  const handleResetForm = () => {
    clearDraft();
    prevLoadingSourceKeyRef.current = null;
    setCargoStepError(false);
    setCurrentStep(0);
    setSelectedIds([]);
    setQuantityDraft({});
    resetExecutionTopologyDraft();
    setCargoAppFilter(undefined);
    setCargoSource({ kind: 'main' });
    setStepData({});
    setPrevDriver(null);
    form.resetFields();
    form.setFieldsValue({
      plannedLoadingDate: undefined,
      plannedUnloadingDate: undefined,
      carrierContractRequestDate: undefined,
      driverId: undefined,
      vehicleId: undefined,
      trailerVehicleId: undefined
    });
    // saveDraft (onValuesChange) может записать черновик со старым stepData из замыкания — убираем с накоплением после флша состояний
    clearDraft();
    queueMicrotask(() => {
      clearDraft();
    });
  };

  /** Явная подстановка тягача/прицепа из карточки водителя (не затрагивает prevDriver и логику автоподстановки при смене водителя). */
  const handleFillVehiclesFromDriver = () => {
    const did = form.getFieldValue('driverId') ?? driverIdWatch;
    if (did == null || !drivers?.length) return;
    const d = drivers.find((x) => x.id === did);
    if (!d) return;
    const vid =
      d.defaultVehicleId != null && Number.isFinite(Number(d.defaultVehicleId))
        ? Number(d.defaultVehicleId)
        : undefined;
    const tid =
      d.defaultTrailerVehicleId != null && Number.isFinite(Number(d.defaultTrailerVehicleId))
        ? Number(d.defaultTrailerVehicleId)
        : undefined;
    form.setFieldsValue({ vehicleId: vid, trailerVehicleId: tid });
    setStepData((prev) => ({ ...prev, vehicleId: vid, trailerVehicleId: tid }));
  };

  // Handlers for create buttons
  const handleCreateCarrier = () => {
    setCarrierModalOpen(true);
  };

  const handleCreateRailwayStation = () => {
    setRailwayStationModalOpen(true);
  };

  const handleCreateAirport = () => {
    setAirportModalOpen(true);
  };

  const handleCreateContractor = (role: 'client' | 'expeditor' | 'shipper' | 'consignee') => {
    setContractorModalRole(role);
    setContractorModalOpen(true);
  };

  const handleCreateDriver = () => {
    setDriverModalOpen(true);
  };

  // Refetch handlers
  const handleCarrierCreated = () => {
    refetchCarriers();
    setCarrierModalOpen(false);
  };

  const handleRailwayStationCreated = () => {
    refetchRailwayStations();
    setRailwayStationModalOpen(false);
  };

  const handleAirportCreated = () => {
    refetchAirports();
    setAirportModalOpen(false);
  };

  const handleContractorCreated = (contractor: Contractor) => {
    // Refetch appropriate list based on role
    switch (contractorModalRole) {
      case 'client':
        refetchClients();
        form.setFieldsValue({ clientId: contractor.id });
        setStepData(prev => ({ ...prev, clientId: contractor.id }));
        break;
      case 'expeditor':
        refetchExpeditors();
        form.setFieldsValue({ expeditorId: contractor.id });
        setStepData(prev => ({ ...prev, expeditorId: contractor.id }));
        break;
      case 'shipper':
        refetchShippers();
        form.setFieldsValue({ shipperId: contractor.id });
        setStepData(prev => ({ ...prev, shipperId: contractor.id }));
        break;
      case 'consignee':
        refetchConsignees();
        form.setFieldsValue({ consigneeId: contractor.id });
        setStepData(prev => ({ ...prev, consigneeId: contractor.id }));
        break;
    }
    setContractorModalOpen(false);
  };

  const handleDriverCreated = (driver: Driver) => {
    void refetchDrivers();
    const currentTruckId = form.getFieldValue('vehicleId') as number | undefined;
    const currentTrailerId = form.getFieldValue('trailerVehicleId') as number | undefined;
    const { vehicleId: nextVehicleId, trailerVehicleId: nextTrailerId } =
      computeNextVehiclesFromDriverChange(prevDriver, driver, currentTruckId, currentTrailerId);
    form.setFieldsValue({
      driverId: driver.id,
      vehicleId: nextVehicleId,
      trailerVehicleId: nextTrailerId
    });
    setStepData((prev) => ({
      ...prev,
      driverId: driver.id,
      vehicleId: nextVehicleId,
      trailerVehicleId: nextTrailerId
    }));
    applyCarrierFromDriver(driver);
    setPrevDriver(driver);
    setDriverModalOpen(false);
  };

  // При смене шага восстанавливаем ранее сохранённые значения формы
  useEffect(() => {
    // Конвертируем строки из stepData в dayjs объекты для формы
    form.setFieldsValue({
      ...stepData,
      plannedLoadingDate: toDayjs(stepData.plannedLoadingDate),
      plannedUnloadingDate: toDayjs(stepData.plannedUnloadingDate),
      carrierContractRequestDate: toDayjs(stepData.carrierContractRequestDate)
    });
  }, [currentStep, stepData, form]);

  // При открытии модального окна загружаем сохранённые данные (один раз)
  useEffect(() => {
    if (!visible) {
      setDraftRestored(false);
      // При закрытии формы сбрасываем все поля дат
      form.setFieldsValue({
        plannedLoadingDate: undefined,
        plannedUnloadingDate: undefined,
        carrierContractRequestDate: undefined
      });
      return;
    }
    
    // При открытии формы для создания нового маршрута явно устанавливаем undefined для всех полей дат
    if (!editingRoute && !draftRestored) {
      form.setFieldsValue({
        plannedLoadingDate: undefined,
        plannedUnloadingDate: undefined,
        carrierContractRequestDate: undefined
      });
    }
    
    if (draftRestored) return;
    if (!editingRoute) {
      const raw = localStorage.getItem(ROUTE_DRAFT_KEY);
      if (raw) {
        try {
          const draft = JSON.parse(raw);
          const draftValues = { ...(draft.values || {}) };
          delete (draftValues as { cargoIds?: number[] }).cargoIds;
          const sanitizedDraft: Record<string, unknown> = {
            ...draft,
            values: { ...draftValues, cargoIds: [] },
            quantityDraft: {}
          };
          delete sanitizedDraft.allocations;
          localStorage.setItem(ROUTE_DRAFT_KEY, JSON.stringify(sanitizedDraft));
          const v = sanitizedDraft.values as typeof draftValues;
          const restored = normalizeLegacyRouteCreateDraftRestore({
            draftStep: draft.step,
            draftValues: v,
            groupUiEnabled,
            successorContext: successorRouteCreateContext
          });
          if (restored.clearedStaleSource) {
            const nextDraft = {
              ...sanitizedDraft,
              values: { ...restored.values },
              step: restored.step
            };
            localStorage.setItem(ROUTE_DRAFT_KEY, JSON.stringify(nextDraft));
          }
          form.setFieldsValue({
            ...restored.values,
            plannedLoadingDate: toDayjs(restored.values.plannedLoadingDate),
            plannedUnloadingDate: toDayjs(restored.values.plannedUnloadingDate),
            carrierContractRequestDate: toDayjs(restored.values.carrierContractRequestDate),
            cargoIds: []
          });
          setSelectedIds([]);
          setQuantityDraft({});
          setFromStopDraft({});
          setToStopDraft({});
          setCargoAppFilter(undefined);
          setCargoSource({ kind: 'main' });
          setCurrentStep(restored.step);
          setStepData((prev) => ({
            ...prev,
            ...restored.values,
            cargoIds: []
          }));
          if (restored.pendingWarehouseVerification) {
            pendingRestoredWarehouseRef.current = restored.pendingWarehouseVerification;
            restoredWarehouseVerifyDoneRef.current = false;
            setConfirmedLegacySourceKey(null);
          }
          if (!restored.cargoQueryAllowed && restored.step === 0 && Number(draft.step) >= 3) {
            message.info('Черновик: выберите заявку и физический источник погрузки перед выбором груза');
          }
        } catch {
          /* ignore */
        }
      } else {
        clearStep4CargoState();
      }
    } else {
      // Конвертируем строки из stepData в dayjs объекты для формы
      form.setFieldsValue({
        ...stepData,
        plannedLoadingDate: toDayjs(stepData.plannedLoadingDate),
        plannedUnloadingDate: toDayjs(stepData.plannedUnloadingDate),
        carrierContractRequestDate: toDayjs(stepData.carrierContractRequestDate)
      });
    }
    setDraftRestored(true);
  }, [
    visible,
    editingRoute,
    form,
    stepData,
    draftRestored,
    clearStep4CargoState,
    groupUiEnabled,
    successorRouteCreateContext
  ]);

  // Функция для рендеринга скрытых полей
  const renderHiddenFields = () => {
    return hiddenFieldNames.map((field) => (
      <Form.Item key={field} name={field} noStyle>
        <Input type="hidden" />
      </Form.Item>
    ));
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: 
        return (
          <Card title="Шаг 1: Основная информация маршрута">
            {/* Скрытые поля для сохранения данных между шагами */}
            {renderHiddenFields()}
            
            {/* Тип транспорта */}
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="transportType"
                  label="Тип транспорта"
                  rules={[{ required: true, message: 'Выберите тип транспорта' }]}
                >
                  <Radio.Group>
                    <Radio.Button value={TransportType.AUTO}>
                      <Space>
                        {transportIcons[TransportType.AUTO]}
                        {transportLabels[TransportType.AUTO]}
                      </Space>
                    </Radio.Button>
                    <Radio.Button value={TransportType.RAILWAY}>
                      <Space>
                        {transportIcons[TransportType.RAILWAY]}
                        {transportLabels[TransportType.RAILWAY]}
                      </Space>
                    </Radio.Button>
                    <Radio.Button value={TransportType.AVIATION}>
                      <Space>
                        {transportIcons[TransportType.AVIATION]}
                        {transportLabels[TransportType.AVIATION]}
                      </Space>
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">{groupUiEnabled ? 'Структура маршрута' : 'Точки загрузки и выгрузки'}</Divider>

            {!groupUiEnabled && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="applicationId"
                  label="Заявка"
                  rules={[{ required: true, message: 'Выберите заявку перед выбором груза' }]}
                >
                  <AvailableApplicationPicker
                    value={
                      watchedApplicationId ?? stepData.applicationId ?? undefined
                    }
                    excludeRouteId={routeCreateExcludeRouteId}
                    skip={!visible}
                    onChange={handleLegacyApplicationChange}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                {successorWarehouseMissing ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={successorWarehouseMissing}
                  />
                ) : null}
                <Form.Item
                  label="Физический источник груза"
                  required
                  help="Источники с доступным остатком по CP-059 (без справочника складов)"
                >
                  <PhysicalCargoSourcePicker
                    applicationId={legacyFlowApplicationId}
                    loadingPointType={loadingPointType ?? stepData.loadingPointType}
                    loadingPointId={loadingPointId ?? stepData.loadingPointId}
                    excludeRouteId={routeCreateExcludeRouteId}
                    skip={!visible}
                    onSourceSelected={(patch) => {
                      loadingAddressManuallyChangedRef.current = false;
                      form.setFieldsValue(patch);
                      setStepData((prev) => ({ ...prev, ...patch }));
                      setSuccessorWarehouseMissing(null);
                      setConfirmedLegacySourceKey(
                        computeLegacyLoadingSourceKey(
                          patch.loadingPointType,
                          patch.loadingPointId
                        )
                      );
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="loadingPointType"
                  hidden
                  rules={[{ required: true, message: 'Выберите физический источник погрузки' }]}
                >
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item
                  name="loadingPointId"
                  hidden
                  rules={[{ required: true, message: 'Выберите физический источник погрузки' }]}
                >
                  <Input type="hidden" />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="unloadingPointType"
                  label="Точка выгрузки"
                  rules={[{ required: true, message: 'Выберите тип точки выгрузки' }]}
                >
                  <Radio.Group
                    value={unloadingPointType ?? stepData.unloadingPointType}
                    onChange={(e) => {
                      const value = e.target.value;
                      unloadingAddressManuallyChangedRef.current = false;
                      // Обновляем и состояние формы, и stepData
                      form.setFieldsValue({ unloadingPointType: value, unloadingPointId: undefined });
                      setStepData(prev => ({ ...prev, unloadingPointType: value, unloadingPointId: undefined }));
                    }}
                  >
                    <Radio value="warehouse">Склад</Radio>
                    <Radio value="application">Заявка доставки</Radio>
                  </Radio.Group>
                </Form.Item>

                {unloadingPointType === 'warehouse' && (
                  <Form.Item
                    name="unloadingPointId"
                    label="Склад"
                    rules={[{ required: true, message: 'Выберите склад' }]}
                  >
                    <Select 
                      placeholder="Выберите склад" 
                      showSearch
                      onChange={(value) => {
                        unloadingAddressManuallyChangedRef.current = false;
                        form.setFieldValue('unloadingPointId', value);
                        setStepData(prev => ({ ...prev, unloadingPointId: value }));
                      }}
                    >
                      {warehouses?.map((warehouse) => (
                        <Option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name} - {warehouse.city}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {unloadingPointType === 'application' && (
                  <Form.Item
                    name="unloadingPointId"
                    label="Заявка доставки"
                    rules={[{ required: true, message: 'Выберите заявку доставки' }]}
                  >
                    <Select 
                      placeholder="Выберите заявку/cargo delivery node"
                      showSearch
                      optionFilterProp="children"
                      onChange={(value) => {
                        unloadingAddressManuallyChangedRef.current = false;
                        const selectedApplication = applications.find((app) => app.id === value);
                        form.setFieldsValue({
                          unloadingPointId: value,
                          consigneeId: selectedApplication?.consigneeId || undefined
                        });
                        setStepData(prev => ({
                          ...prev,
                          unloadingPointId: value,
                          consigneeId: selectedApplication?.consigneeId
                        }));
                      }}
                    >
                      {mainUnloadingApplications.map((application) => (
                        <Option key={application.id} value={application.id}>
                          {application.internalNumber}
                          {application.consignee?.name ? ` → ${application.consignee.name}` : ''}
                          {application.unloadingAddress ? ` — ${application.unloadingAddress}` : ''}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
            </Row>
            )}

            {groupUiEnabled ? (
              <RouteTopologyPanel
                groups={deliveryGroups}
                onChange={handleDeliveryGroupsChange}
                applications={routePlanningApplications as any[]}
                editingRouteId={editingRoute?.id}
                warehouses={warehouses}
                consignees={consignees}
                makeStopUid={makeStopUidFn}
                inTransit={routeInTransit}
                cargoContext={itineraryCargoContext}
                savePolicyError={topologySavePolicyError}
                resolveCityHint={resolveCityHintFromAddressString}
                normalizeCity={normalizeCityLabel}
                onGroupExecutionScopeClear={clearGroupExecutionScope}
              />
            ) : (
            <Card size="small" style={{ marginTop: 12 }} title="Точки маршрута (опционально)">
                <Space style={{ marginBottom: 12 }}>
                  <Button
                    type="dashed"
                    onClick={() => {
                      const addr = form.getFieldValue('loadingAddress') || '';
                      const cp = form.getFieldValue('loadingContactPerson') || '';
                      const ph = form.getFieldValue('loadingPhone') || '';
                      const city = form.getFieldValue('loadingCity') || '';
                      const unloadAddr = form.getFieldValue('unloadingAddress') || '';
                      const unloadCp = form.getFieldValue('unloadingContactPerson') || '';
                      const unloadPh = form.getFieldValue('unloadingPhone') || '';
                      const unloadCity = form.getFieldValue('unloadingCity') || '';
                      setStopsSmart((prev) => {
                        const loadingIdx = prev.length;
                        const parentPos = loadingIdx + 1;
                        return [
                          ...prev,
                          {
                            uid: makeStopUidFn(),
                            id: null,
                            type: 'loading' as const,
                            position: loadingIdx,
                            address: addr || null,
                            city: city ? String(city).trim() || null : null,
                            contactPerson: cp || null,
                            phone: ph || null,
                            plannedAt: stepData.plannedLoadingDate || null,
                            actualAt: null
                          },
                          {
                            uid: makeStopUidFn(),
                            id: null,
                            type: 'unloading' as const,
                            position: loadingIdx + 1,
                            parentLoadingPosition: parentPos,
                            address: unloadAddr || null,
                            city: unloadCity ? String(unloadCity).trim() || null : null,
                            contactPerson: unloadCp || null,
                            phone: unloadPh || null,
                            plannedAt: stepData.plannedUnloadingDate || null,
                            actualAt: null
                          }
                        ];
                      });
                    }}
                  >
                    Добавить точку погрузки
                  </Button>
                  <Button
                    type="dashed"
                    onClick={() => {
                      const addr = form.getFieldValue('unloadingAddress') || '';
                      const cp = form.getFieldValue('unloadingContactPerson') || '';
                      const ph = form.getFieldValue('unloadingPhone') || '';
                      const city = form.getFieldValue('unloadingCity') || '';
                      setStopsSmart((prev) => ([
                        ...prev,
                        {
                          uid: makeStopUidFn(),
                          id: null,
                          type: 'unloading' as const,
                          position: prev.length,
                          parentLoadingPosition: 0,
                          address: addr || null,
                          city: city ? String(city).trim() || null : null,
                          contactPerson: cp || null,
                          phone: ph || null,
                          plannedAt: stepData.plannedUnloadingDate || null,
                          actualAt: null
                        }
                      ]));
                    }}
                  >
                    Добавить точку выгрузки
                  </Button>
                </Space>
                {stops.length > 0 && (
                  <DataTable
                    dataSource={stops.map((s, idx) => ({ key: idx, ...s }))}
                    pagination={false}
                    size="small"
                    columns={[
                      { title: '#', dataIndex: 'position', key: 'pos', width: 50, render: (_: any, __: any, index: number) => index + 1 },
                      { title: 'Детали', key: 'details', render: (_: any, record: any, index: number) => (
                        <div style={{ lineHeight: 1.2 }}>
                          {/* Первая строка: тип + источник */}
                          <Space wrap style={{ marginBottom: 8 }}>
                            <Select
                              value={record.type}
                              style={{ width: 110 }}
                              onChange={(val) => setStopsSmart(prev => prev.map((p, i) => i === index ? {
                                ...p,
                                type: val as ('loading'|'unloading'|'transfer'),
                                pointType: null,
                                pointId: null,
                                parentLoadingPosition: val === 'unloading' ? p.parentLoadingPosition ?? 0 : null
                              } : p))}
                              options={[
                                { value: 'loading', label: 'Погрузка' },
                                { value: 'unloading', label: 'Выгрузка' }
                              ]}
                            />
                            {record.type === 'unloading' && (
                              <Select
                                style={{ width: 220 }}
                                placeholder="Связана с погрузкой"
                                value={record.parentLoadingPosition ?? 0}
                                onChange={(val) =>
                                  setStopsSmart(prev => prev.map((p, i) => i === index ? { ...p, parentLoadingPosition: Number(val) } : p))
                                }
                                options={[
                                  { value: 0, label: 'Основная погрузка' },
                                  ...stops
                                    .map((s, idx) => ({ s, idx }))
                                    .filter(({ s }) => s.type === 'loading')
                                    .map(({ s, idx }) => ({
                                      value: idx + 1,
                                      label:
                                        s.pointType === 'application'
                                          ? `Стоп ${idx + 1}: заявка #${s.pointId ?? ''}`
                                          : s.pointType === 'warehouse'
                                            ? `Стоп ${idx + 1}: склад #${s.pointId ?? ''}`
                                            : `Стоп ${idx + 1}: погрузка`
                                    }))
                                ]}
                              />
                            )}
                            <Select
                              style={{ width: 130 }}
                              placeholder="Источник"
                              value={record.pointType || undefined}
                              onChange={(val) => setStopsSmart(prev => prev.map((p, i) => i === index ? { ...p, pointType: val as any, pointId: null } : p))}
                              options={
                                record.type === 'loading'
                                  ? [
                                      { value: 'warehouse', label: 'Склад' },
                                      { value: 'application', label: 'Заявка' }
                                    ]
                                  : [
                                      { value: 'warehouse', label: 'Склад' },
                                      { value: 'application', label: 'Заявка доставки' }
                                    ]
                              }
                            />
                            <Select
                              style={{ width: 320 }}
                              placeholder="Выбор"
                              value={record.pointId || undefined}
                              showSearch
                              optionFilterProp="children"
                              dropdownMatchSelectWidth={false}
                              onChange={(val) => {
                                void (async () => {
                                  const idx = index;
                                  const p = record;
                                  const next: any = { ...p, pointId: val as number };
                                  try {
                                    if (p.pointType === 'warehouse') {
                                      const w = (warehouses || []).find((x) => x.id === val);
                                      if (w) {
                                        next.address = w.address || `${w.name}, ${w.city}` || '';
                                        next.city = normalizeCityLabel(w.city);
                                        next.contactPerson = w.contactPerson || '';
                                        next.phone = w.phone || '';
                                      }
                                    } else if (p.pointType === 'application') {
                                      const a = (applications || []).find((x) => x.id === val);
                                      if (a) {
                                        const address = p.type === 'unloading' ? a.unloadingAddress : a.loadingAddress;
                                        next.address = address || '';
                                        const hint = await resolveCityHintFromAddressString(address || '');
                                        next.city = normalizeCityLabel(hint) || hint?.trim() || null;
                                        next.contactPerson =
                                          p.type === 'unloading'
                                            ? a.unloadingContactPerson || a.consignee?.contactPerson || ''
                                            : a.loadingContactPerson || '';
                                        next.phone =
                                          p.type === 'unloading'
                                            ? a.unloadingPhone || a.consignee?.phone || ''
                                            : a.loadingPhone || '';
                                      }
                                    } else if (p.pointType === 'consignee') {
                                      const c = (consignees || []).find((x) => x.id === val);
                                      if (c) {
                                        next.address = c.address || '';
                                        next.city = normalizeCityLabel((c as any).city);
                                        next.contactPerson = c.contactPerson || '';
                                        next.phone = c.phone || '';
                                      }
                                    }
                                  } catch {
                                    /* ignore */
                                  }
                                  setStopsSmart((prev) => prev.map((row, i) => (i === idx ? next : row)));
                                })();
                              }}
                            >
                              {record.pointType === 'warehouse' && warehouses?.map(w => (
                                <Option key={w.id} value={w.id}>
                                  {w.name}{w.city ? ` — ${w.city}` : ''}
                                </Option>
                              ))}
                              {record.pointType === 'application' && applicationsForStopGroup(Number(record.parentLoadingPosition ?? 0)).map(a => (
                                <Option key={a.id} value={a.id}>
                                  {a.internalNumber}
                                  {record.type === 'unloading' && a.consignee?.name ? ` → ${a.consignee.name}` : ''}
                                  {a.client?.name ? ` — ${a.client.name}` : ''}
                                </Option>
                              ))}
                              {record.pointType === 'consignee' && consignees?.map(c => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                              ))}
                            </Select>
                          </Space>
                          {/* Вторая строка: адрес + контакт + телефон + план + удалить */}
                          <Space wrap>
                            <Input
                              placeholder="Адрес"
                              style={{ width: 220 }}
                              value={record.address || ''}
                              onChange={(e) => setStopsSmart(prev => prev.map((p, i) => i === index ? { ...p, address: e.target.value } : p))}
                            />
                            <Input
                              placeholder="Контакт"
                              style={{ width: 160 }}
                              value={record.contactPerson || ''}
                              onChange={(e) => setStopsSmart(prev => prev.map((p, i) => i === index ? { ...p, contactPerson: e.target.value } : p))}
                            />
                            <Input
                              placeholder="Телефон"
                              style={{ width: 140 }}
                              value={record.phone || ''}
                              onChange={(e) => setStopsSmart(prev => prev.map((p, i) => i === index ? { ...p, phone: e.target.value } : p))}
                            />
                            <DatePicker
                              style={{ width: 160 }}
                              format="DD.MM.YYYY"
                              value={record.plannedAt ? dayjs(record.plannedAt) : undefined}
                              onChange={(d) => setStopsSmart(prev => prev.map((p, i) => i === index ? { ...p, plannedAt: d ? d.toISOString() : null } : p))}
                            />
                            <Button
                              danger
                              onClick={() => {
                                setStopsSmart((prev) => prev.filter((_, i) => i !== index));
                              }}
                            >
                              Удалить
                            </Button>
                          </Space>
                        </div>
                      ) }
                    ]}
                  />
                )}
            </Card>
            )}

            {!groupUiEnabled && (
            <>
            <Divider orientation="left">Адреса погрузки</Divider>

            {/* Адреса */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="loadingAddress" 
                  label={
                    <Space>
                      Точный адрес загрузки
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<ClearOutlined />}
                        onClick={() => clearAutoFilledFields(['loadingAddress', 'loadingContactPerson', 'loadingPhone', 'loadingCity'])}
                        title="Очистить автозаполненные поля загрузки"
                      />
                    </Space>
                  }
                >
                  <AddressAutocomplete
                    placeholder="Введите точный адрес загрузки"
                    onChange={async (value, suggestion?: AddressSuggestion) => {
                      loadingAddressManuallyChangedRef.current = true;
                      let c =
                        suggestion && typeof suggestion.city === 'string'
                          ? normalizeCityLabel(suggestion.city) || suggestion.city.trim()
                          : '';
                      if (!c && value?.trim()) {
                        const hint = await resolveCityHintFromAddressString(value.trim());
                        c = normalizeCityLabel(hint) || (hint ? hint.trim() : '');
                      }
                      form.setFieldValue('loadingAddress', value);
                      form.setFieldValue('loadingCity', c);
                      setStepData((prev) => ({ ...prev, loadingAddress: value, loadingCity: c }));
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="unloadingAddress" 
                  label={
                    <Space>
                      Точный адрес выгрузки
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<ClearOutlined />}
                        onClick={() => clearAutoFilledFields(['unloadingAddress', 'unloadingContactPerson', 'unloadingPhone', 'unloadingCity'])}
                        title="Очистить автозаполненные поля выгрузки"
                      />
                    </Space>
                  }
                >
                  <AddressAutocomplete
                    placeholder="Введите точный адрес выгрузки"
                    onChange={async (value, suggestion?: AddressSuggestion) => {
                      unloadingAddressManuallyChangedRef.current = true;
                      let c =
                        suggestion && typeof suggestion.city === 'string'
                          ? normalizeCityLabel(suggestion.city) || suggestion.city.trim()
                          : '';
                      if (!c && value?.trim()) {
                        const hint = await resolveCityHintFromAddressString(value.trim());
                        c = normalizeCityLabel(hint) || (hint ? hint.trim() : '');
                      }
                      form.setFieldValue('unloadingAddress', value);
                      form.setFieldValue('unloadingCity', c);
                      setStepData((prev) => ({ ...prev, unloadingAddress: value, unloadingCity: c }));
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="loadingCity" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="unloadingCity" hidden>
              <Input />
            </Form.Item>

            <Divider orientation="left">Контактные данные</Divider>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="loadingContactPerson" 
                  label="Контактное лицо (загрузка)"
                >
                  <Input placeholder="ФИО контактного лица" />
                </Form.Item>
                <Form.Item 
                  name="loadingPhone" 
                  label="Телефон (загрузка)"
                >
                  <Input placeholder="Номер телефона" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="unloadingContactPerson" 
                  label="Контактное лицо (выгрузка)"
                >
                  <Input placeholder="ФИО контактного лица" />
                </Form.Item>
                <Form.Item 
                  name="unloadingPhone" 
                  label="Телефон (выгрузки)"
                >
                  <Input placeholder="Номер телефона" />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Планируемые даты</Divider>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="plannedLoadingDate"
                  label="Планируемая дата загрузки"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Выберите дату загрузки"
                    format="DD.MM.YYYY"
                    onChange={date => {
                      setStepData(prev => ({ ...prev, plannedLoadingDate: date ? date.toISOString() : undefined }));
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item 
                  name="plannedUnloadingDate"
                  label="Планируемая дата выгрузки"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Выберите дату выгрузки"
                    format="DD.MM.YYYY"
                    onChange={date => {
                      setStepData(prev => ({ ...prev, plannedUnloadingDate: date ? date.toISOString() : undefined }));
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            </>
            )}

            <Divider orientation="left">Дополнительно</Divider>
            <Collapse
              size="small"
              items={[
                {
                  key: 'contractors',
                  label: 'Контрагенты (опционально)',
                  children: (
                    <>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="clientId" label="Клиент">
                            <Space.Compact style={{ width: '100%' }}>
                              <SmartSelect
                                placeholder="Выберите клиента"
                                style={{ flex: 1 }}
                                allowClear
                                options={(clients || []).map(client => ({ value: client.id, label: client.name }))}
                                onChange={(value) => setStepData(prev => ({ ...prev, clientId: value }))}
                              />
                              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreateContractor('client')} />
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="expeditorId" label="Экспедитор">
                            <Space.Compact style={{ width: '100%' }}>
                              <SmartSelect
                                placeholder="Выберите экспедитора"
                                style={{ flex: 1 }}
                                allowClear
                                options={(expeditors || []).map(expeditor => ({ value: expeditor.id, label: expeditor.name }))}
                                onChange={(value) => setStepData(prev => ({ ...prev, expeditorId: value }))}
                              />
                              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreateContractor('expeditor')} />
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item name="shipperId" label="Грузоотправитель">
                            <Space.Compact style={{ width: '100%' }}>
                              <SmartSelect
                                placeholder="Выберите грузоотправителя"
                                style={{ flex: 1 }}
                                allowClear
                                options={(shippers || []).map(shipper => ({ value: shipper.id, label: shipper.name }))}
                                onChange={(value) => setStepData(prev => ({ ...prev, shipperId: value }))}
                              />
                              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreateContractor('shipper')} />
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="consigneeId" label="Грузополучатель">
                            <Space.Compact style={{ width: '100%' }}>
                              <SmartSelect
                                placeholder="Выберите грузополучателя"
                                style={{ flex: 1 }}
                                allowClear
                                options={(consignees || []).map(consignee => ({ value: consignee.id, label: consignee.name }))}
                                onChange={(value) => setStepData(prev => ({ ...prev, consigneeId: value }))}
                              />
                              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleCreateContractor('consignee')} />
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  )
                }
              ]}
            />
          </Card>
        );
      
      case 1:
        return (
          <Card title="Шаг 2: Тип транспорта">
            {/* Скрытые поля для сохранения данных между шагами */}
            {renderHiddenFields()}

            {/* Общие поля для автотранспорта */}
            {transportType === TransportType.AUTO && (
              <>
                <Title level={5}>Автомобильная доставка</Title>
                <Row gutter={16}>
                  {/* Водитель - первое поле */}
                  <Col span={8}>
                    <Form.Item name="driverId" label="Водитель">
                      <Space.Compact style={{ width: '100%' }}>
                        <Select 
                          placeholder="Выберите водителя" 
                          style={{ flex: 1 }} 
                          value={form.getFieldValue('driverId')}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) => {
                            const text = String(option?.children ?? '').toLowerCase();
                            return text.includes(input.toLowerCase());
                          }}
                          onClear={() => {
                            setPrevDriver(null);
                            form.setFieldsValue({
                              driverId: undefined,
                              vehicleId: undefined,
                              trailerVehicleId: undefined,
                              carrierId: undefined
                            });
                            setStepData((prev) => ({
                              ...prev,
                              driverId: undefined,
                              vehicleId: undefined,
                              trailerVehicleId: undefined,
                              carrierId: undefined
                            }));
                            applyCarrierFromDriver(null);
                          }}
                          onChange={(value) => {
                            const currentTruckId = form.getFieldValue('vehicleId') as number | undefined;
                            const currentTrailerId = form.getFieldValue('trailerVehicleId') as number | undefined;

                            if (value == null) {
                              setPrevDriver(null);
                              form.setFieldsValue({
                                driverId: undefined,
                                vehicleId: undefined,
                                trailerVehicleId: undefined,
                                carrierId: undefined
                              });
                              setStepData((prev) => ({
                                ...prev,
                                driverId: undefined,
                                vehicleId: undefined,
                                trailerVehicleId: undefined,
                                carrierId: undefined
                              }));
                              applyCarrierFromDriver(null);
                              return;
                            }

                            const newDriver = drivers?.find((dr) => dr.id === value);
                            const { vehicleId: nextVehicleId, trailerVehicleId: nextTrailerId } =
                              computeNextVehiclesFromDriverChange(
                                prevDriver,
                                newDriver,
                                currentTruckId,
                                currentTrailerId
                              );

                            form.setFieldsValue({
                              driverId: value,
                              vehicleId: nextVehicleId,
                              trailerVehicleId: nextTrailerId
                            });
                            setStepData((prev) => ({
                              ...prev,
                              driverId: value,
                              vehicleId: nextVehicleId,
                              trailerVehicleId: nextTrailerId
                            }));

                            applyCarrierFromDriver(newDriver);
                            setPrevDriver(newDriver ?? null);
                          }}
                        >
                          {drivers?.map((driver) => (
                            <Option key={driver.id} value={driver.id}>
                              {driver.fullName}
                            </Option>
                          ))}
                        </Select>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={handleCreateDriver}
                          title="Создать водителя"
                        />
                      </Space.Compact>
                      {selectedDriverBusyRoute ? (
                        <Alert
                          type="warning"
                          showIcon
                          message={selectedDriverBusyRoute.message}
                          style={{ marginTop: 8 }}
                        />
                      ) : null}
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      name="vehicleId"
                      label="Тягач"
                      rules={[{ required: true, message: 'Выберите тягач' }]}
                    >
                      <VehicleSelectInline
                        vehicles={trucks}
                        placeholder="Выберите тягач"
                        allowClear={false}
                        modalCategory="TRUCK"
                        addButtonTooltip="Добавить тягач"
                        addButtonAriaLabel="Добавить тягач"
                        onRefetchVehicles={refetchVehicles}
                        onCommittedChange={(id) => setStepData((prev) => ({ ...prev, vehicleId: id }))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item name="trailerVehicleId" label="Прицеп">
                      <VehicleSelectInline
                        vehicles={trailers}
                        placeholder="Выберите прицеп"
                        allowClear
                        modalCategory="TRAILER"
                        addButtonTooltip="Добавить прицеп"
                        addButtonAriaLabel="Добавить прицеп"
                        onRefetchVehicles={refetchVehicles}
                        onCommittedChange={(id) => setStepData((prev) => ({ ...prev, trailerVehicleId: id }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {showFillFromDriverButton ? (
                  <Row gutter={16} style={{ marginTop: -8 }}>
                    <Col span={8} />
                    <Col span={16}>
                      <Tooltip title="Подставить тягач и прицеп, указанные у водителя">
                        <Button type="default" onClick={handleFillVehiclesFromDriver}>
                          Заполнить из водителя
                        </Button>
                      </Tooltip>
                    </Col>
                  </Row>
                ) : null}

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="carrierId" label="Перевозчик">
                      <Space.Compact style={{ width: '100%' }}>
                        <Select 
                          placeholder="Выберите перевозчика" 
                          style={{ flex: 1 }} 
                          value={form.getFieldValue('carrierId')}
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) => {
                            const text = String(option?.children ?? '').toLowerCase();
                            return text.includes(input.toLowerCase());
                          }}
                          onChange={value => {
                            setStepData(prev => ({ ...prev, carrierId: value }));
                            form.setFieldsValue({ carrierId: value });
                          }}
                        >
                          {(() => {
                            if (!carriers || carriers.length === 0) return null;
                            
                            const normalizeName = (name: string): string => {
                              if (!name) return '';
                              let normalized = name
                                .replace(/^(ИП|ООО|ЗАО|ОАО|ПАО|АО|НПО|НТЦ|ТОО|ЧП|LLC|LTD|INC|CORP)[\s«»""'']*/i, '')
                                .trim();
                              
                              normalized = normalized.replace(/^[«»""'']+|[«»""'']+$/g, '').trim();
                              
                              return normalized.toLowerCase();
                            };
                            
                            const sortedCarriers = [...carriers].sort((a, b) => {
                              const nameA = normalizeName(a.name);
                              const nameB = normalizeName(b.name);
                              return nameA.localeCompare(nameB, 'ru', { sensitivity: 'base', numeric: true });
                            });
                            
                            return sortedCarriers.map((carrier) => (
                              <Option key={carrier.id} value={carrier.id}>
                                {carrier.name}
                              </Option>
                            ));
                          })()}
                        </Select>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateCarrier} />
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            {/* Поля для железнодорожного транспорта */}
            {transportType === TransportType.RAILWAY && (
              <>
                <Title level={5}>Железнодорожная доставка</Title>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="wagonNumber" label="Номер вагона">
                      <Input placeholder="Введите номер вагона" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="departureStationId" label="Станция отправления">
                      <Space.Compact style={{ width: '100%' }}>
                        <Select 
                          placeholder="Станция отправления" 
                          style={{ flex: 1 }} 
                          allowClear
                          onChange={value => setStepData(prev => ({ ...prev, departureStationId: value }))}
                        >
                          {railwayStations?.map((station) => (
                            <Option key={station.id} value={station.id}>
                              {station.name}
                            </Option>
                          ))}
                        </Select>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateRailwayStation} />
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="arrivalStationId" label="Станция прибытия">
                      <Space.Compact style={{ width: '100%' }}>
                        <Select 
                          placeholder="Станция прибытия" 
                          style={{ flex: 1 }} 
                          allowClear
                          onChange={value => setStepData(prev => ({ ...prev, arrivalStationId: value }))}
                        >
                          {railwayStations?.map((station) => (
                            <Option key={station.id} value={station.id}>
                              {station.name}
                            </Option>
                          ))}
                        </Select>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateRailwayStation} />
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            {/* Поля для авиатранспорта */}
            {transportType === TransportType.AVIATION && (
              <>
                <Title level={5}>Авиационная доставка</Title>
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="flightNumber" label="Номер рейса">
                      <Input placeholder="Введите номер рейса" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="departureAirportId" label="Аэропорт отправления">
                      <Space.Compact style={{ width: '100%' }}>
                        <Select 
                          placeholder="Аэропорт отправления" 
                          style={{ flex: 1 }} 
                          allowClear
                          onChange={value => setStepData(prev => ({ ...prev, departureAirportId: value }))}
                        >
                          {airports?.map((airport) => (
                            <Option key={airport.id} value={airport.id}>
                              {airport.name}
                            </Option>
                          ))}
                        </Select>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateAirport} />
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="arrivalAirportId" label="Аэропорт прибытия">
                      <Space.Compact style={{ width: '100%' }}>
                        <Select 
                          placeholder="Аэропорт прибытия" 
                          style={{ flex: 1 }} 
                          allowClear
                          onChange={value => setStepData(prev => ({ ...prev, arrivalAirportId: value }))}
                        >
                          {airports?.map((airport) => (
                            <Option key={airport.id} value={airport.id}>
                              {airport.name}
                            </Option>
                          ))}
                        </Select>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateAirport} />
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            {!transportType && (
              <Text type="secondary">Сначала выберите тип транспорта на предыдущем шаге</Text>
            )}
          </Card>
        );

      case 2:
        return (
          <Card title="Шаг 3: Финансы">
            {/* Скрытые поля для сохранения данных между шагами */}
            {renderHiddenFields()}

            {/* Кто выполняет перевозку - автоматически определяется */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Form.Item 
                  label="Кто выполняет перевозку"
                  shouldUpdate={(prevValues, currentValues) => prevValues.executor !== currentValues.executor}
                >
                  {({ getFieldValue }) => (
                    <Input 
                      readOnly 
                      disabled
                      style={{ backgroundColor: '#f5f5f5' }}
                      value={executorTypeLabel(getFieldValue('executor'))}
                    />
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ display: 'block', marginTop: 30 }}>
                  {executorWatch === 'OWN_TRANSPORT' && '📋 Счёт выставит бухгалтер. Ставка опциональна.'}
                  {executorWatch === 'CONTRACTOR' && '💰 Укажите стоимость перевозки перевозчику.'}
                </Text>
              </Col>
            </Row>

            {/* Ответственный логист */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Form.Item
                  name="responsibleLogistId"
                  label="Ответственный логист"
                  rules={executorWatch === 'CONTRACTOR' ? [{ required: true, message: 'Выберите логиста' }] : []}
                >
                  <Select
                    placeholder="Выберите логиста"
                    showSearch
                    optionFilterProp="label"
                    disabled={!canEditResponsibleLogist || isLoadingUsers}
                    loading={isLoadingUsers}
                    allowClear
                    onChange={(value) => setStepData(prev => ({ ...prev, responsibleLogistId: value }))}
                  >
                    {mergeResponsibleSelectUsers(users, editingRoute?.responsibleLogist)
                      .slice()
                      .sort((a: any, b: any) => {
                        const nameA = formatResponsibleUserLabel(a).toLowerCase();
                        const nameB = formatResponsibleUserLabel(b).toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((user: any) => {
                        const label = formatResponsibleUserLabel(user);
                        return (
                          <Option key={user.id} value={user.id} label={label}>
                            {label}
                          </Option>
                        );
                      })}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Блок "Договор-заявка с перевозчиком" - только для перевозчиков */}
            {executorWatch === 'CONTRACTOR' && (
              <>
                <Divider orientation="left">Договор-заявка с перевозчиком</Divider>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={12}>
                    <Form.Item
                      name="carrierContractRequestNumber"
                      label="Номер договор-заявки"
                    >
                      <Input placeholder="Введите номер договор-заявки" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="carrierContractRequestDate"
                      label="Дата договор-заявки"
                    >
                      <DatePicker 
                        style={{ width: '100%' }}
                        format="DD.MM.YYYY"
                        placeholder="Выберите дату"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={24}>
                    <Form.Item
                      name="carrierContractRequestFile"
                      label="Файл договор-заявки"
                    >
                      <Input 
                        placeholder="Путь к файлу (будет реализована загрузка)" 
                        disabled
                      />
                      {/* TODO: Добавить компонент загрузки файла */}
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}

            {/* Финансовые поля - показываем только если не свой транспорт */}
            {executorWatch !== 'OWN_TRANSPORT' && (
              <>
            {financialTermsLocked ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="Финансовые условия заблокированы"
                description="По маршруту есть проведённые оплаты перевозчику. Ставка и условия оплаты доступны только для просмотра."
              />
            ) : null}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  name="totalAmount" 
                      label={executorWatch === 'PARTNER' ? "Ставка перевозки (для учёта)" : "Ставка перевозки (руб.)"}
                      rules={executorWatch === 'CONTRACTOR' ? [{ required: true, message: 'Укажите стоимость' }] : []}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                        placeholder={executorWatch === 'PARTNER' ? "Опционально" : "Введите сумму"}
                    min={0}
                    precision={2}
                    disabled={financialTermsLocked}
                  />
                </Form.Item>
              </Col>
                  {executorWatch === 'CONTRACTOR' && (
              <Col span={12}>
                <Form.Item 
                  name="paymentMethod" 
                        label="Способ оплаты перевозчику"
                        rules={[{ required: true, message: 'Выберите способ оплаты' }]}
                >
                        <Select placeholder="Выберите способ оплаты" disabled={financialTermsLocked}>
                    <Option value="cash">Наличные</Option>
                    <Option value="fuel">Топливо</Option>
                    <Option value="non_cash_vat">Безналичный с НДС</Option>
                    <Option value="non_cash_no_vat">Безналичный без НДС</Option>
                  </Select>
                </Form.Item>
              </Col>
                  )}
            </Row>

                {executorWatch === 'CONTRACTOR' && (
            <Row gutter={16}>
                    <Col span={6}>
                <Form.Item 
                  name="prepaymentPercent" 
                        label="Предоплата (%)"
                        rules={[{ type: 'number', min: 0, max: 100, message: 'От 0 до 100' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0"
                    min={0}
                    max={100}
                    disabled={financialTermsLocked}
                    onChange={(value) => {
                      if (value !== null && value !== undefined) {
                        form.setFieldsValue({ remainderPercent: 100 - value });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
                    <Col span={6}>
                      <Form.Item name="remainderPercent" label="Доплата (%)">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="100"
                    disabled
                  />
                </Form.Item>
              </Col>
                    <Col span={6}>
                      <Form.Item name="prepaymentDelayDays" label="Отсрочка предоплаты (дней)">
                        <InputNumber style={{ width: '100%' }} placeholder="0" min={0} disabled={financialTermsLocked} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="remainderDelayDays" label="Отсрочка доплаты (дней)">
                        <InputNumber style={{ width: '100%' }} placeholder="0" min={0} disabled={financialTermsLocked} />
                </Form.Item>
              </Col>
            </Row>
                )}

                {executorWatch === 'CONTRACTOR' && (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="prepaymentCondition" label="Тип предоплаты">
                        <Select placeholder="Выберите тип предоплаты">
                          <Option value="BEFORE_LOADING">До погрузки</Option>
                          <Option value="AFTER_LOADING">После погрузки</Option>
                          <Option value="BEFORE_UNLOADING">До выгрузки</Option>
                          <Option value="AFTER_UNLOADING">После выгрузки</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                {executorWatch === 'CONTRACTOR' && carrierIdWatch && (
                  <>
                    <Divider orientation="left">Штрафы перевозчика</Divider>
                    <Spin spinning={penaltiesLoading}>
                      {penaltiesLoading && waitingCarrierAgreement && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          Создаётся договор...
                        </Text>
                      )}
                      {!carrierAgreementId && !penaltiesLoading && penaltyAgreementNotFound && (
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          Договор не найден
                        </Text>
                      )}
                      {!carrierAgreementId &&
                        !penaltiesLoading &&
                        !penaltyAgreementNotFound &&
                        !editingRoute &&
                        !postCreatePenaltiesStep && (
                          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                            Договор с перевозчиком появится после сохранения маршрута.
                          </Text>
                        )}
                      {carrierAgreementId && penaltyDrafts.length === 0 && !penaltiesLoading && (
                        <Text type="secondary">Нет штрафов</Text>
                      )}
                      {carrierAgreementId && penaltyDrafts.length > 0 && (
                        <>
                          <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            {penaltyDrafts.map((row) => (
                              <Card key={row.id} size="small" title={<Text strong>{row.name}</Text>}>
                                <Row gutter={[12, 8]}>
                                  <Col xs={24} sm={12} md={5}>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                                      Тип
                                    </Text>
                                    <Select
                                      style={{ width: '100%' }}
                                      value={row.type}
                                      onChange={(v) => updatePenaltyDraft(row.id, { type: v })}
                                      options={[
                                        { value: 'PERCENT', label: PENALTY_TYPE_LABELS.PERCENT },
                                        { value: 'FIXED', label: PENALTY_TYPE_LABELS.FIXED }
                                      ]}
                                    />
                                  </Col>
                                  <Col xs={24} sm={12} md={5}>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                                      Значение
                                    </Text>
                                    <InputNumber
                                      style={{ width: '100%' }}
                                      min={0}
                                      max={row.type === 'PERCENT' ? 100 : undefined}
                                      value={row.value}
                                      onChange={(v) => updatePenaltyDraft(row.id, { value: v ?? 0 })}
                                    />
                                  </Col>
                                  <Col xs={24} sm={12} md={6}>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                                      Макс. сумма (пусто — без лимита)
                                    </Text>
                                    <InputNumber
                                      style={{ width: '100%' }}
                                      min={0}
                                      placeholder="—"
                                      value={row.maxAmount ?? undefined}
                                      onChange={(v) =>
                                        updatePenaltyDraft(row.id, {
                                          maxAmount: v === null || v === undefined ? null : Number(v)
                                        })
                                      }
                                    />
                                  </Col>
                                  <Col xs={12} sm={6} md={4}>
                                    <div style={{ marginTop: 22 }}>
                                      <Checkbox
                                        checked={row.perDay}
                                        onChange={(e) => updatePenaltyDraft(row.id, { perDay: e.target.checked })}
                                      >
                                        За день
                                      </Checkbox>
                                    </div>
                                  </Col>
                                  <Col xs={12} sm={6} md={4}>
                                    <div style={{ marginTop: 22 }}>
                                      <Checkbox
                                        checked={row.requiresManualInput}
                                        onChange={(e) =>
                                          updatePenaltyDraft(row.id, { requiresManualInput: e.target.checked })
                                        }
                                      >
                                        Ручной ввод дней
                                      </Checkbox>
                                    </div>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                          </Space>
                          {!postCreatePenaltiesStep && (
                            <div style={{ marginTop: 12, textAlign: 'right' }}>
                              <Button
                                type="primary"
                                onClick={() => void handleSaveCarrierPenalties()}
                                loading={penaltiesBatchSaving}
                                disabled={!penaltiesDirty || penaltiesBatchSaving}
                              >
                                Сохранить штрафы
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </Spin>
                  </>
                )}

                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Дополнительные расходы"
                  description="Расходы по маршруту добавляются в разделе «Финансы» или при завершении рейса (исполнение). Единый workflow: черновик → подтверждение бухгалтером."
                />
              </>
            )}

            {/* Для своего транспорта показываем опциональное поле стоимости */}
            {executorWatch === 'OWN_TRANSPORT' && (
            <Row gutter={16}>
                <Col span={12}>
                <Form.Item 
                    name="totalAmount" 
                    label="Ставка (опционально, для учёта)"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                      placeholder="Можно не заполнять"
                    min={0}
                      precision={2}
                  />
                </Form.Item>
              </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ display: 'block', marginTop: 30 }}>
                    ℹ️ Затраты на свой транспорт учитываются в бухгалтерии.
                  </Text>
                </Col>
            </Row>
            )}
          </Card>
        );

      case 3:
        const availableCargoColumns: any[] = [
          { title: 'Наименование', dataIndex: 'name', key: 'name', width: 260, ellipsis: true, sorter: (a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')), render: (text: string) => <span style={{ display: 'inline-block', maxWidth: 240 }} title={text}>{text}</span> },
          {
            title: 'Количество',
            key: 'quantityAvail',
            width: 130,
            sorter: (a: any, b: any) => Number(a.quantity || 0) - Number(b.quantity || 0),
            render: (_: any, record: any) => {
              const total = Number(record.quantity ?? 0);
              const avail =
                record.availableQuantity != null ? Number(record.availableQuantity) : total;
              const partial = avail < total;
              return (
                <div>
                  <div>Всего: {total}</div>
                  {partial ? <span style={{ color: 'rgba(0,0,0,0.45)' }}>Доступно: {avail}</span> : null}
                </div>
              );
            }
          },
          { title: 'Вес (кг)', dataIndex: 'totalWeightKg', key: 'totalWeightKg', width: 110, sorter: (a: any, b: any) => Number(a.totalWeightKg || 0) - Number(b.totalWeightKg || 0) },
          {
            title: 'Заявка',
            dataIndex: ['application', 'internalNumber'],
            key: 'applicationNumber',
            width: 130,
            sorter: (a: any, b: any) => String(a?.application?.internalNumber || '').localeCompare(String(b?.application?.internalNumber || '')),
            render: (text: string) => <Tag color="blue">{text}</Tag>
          },
          {
            title: 'Клиент',
            dataIndex: ['application', 'client', 'name'],
            key: 'clientName',
            width: 200,
            ellipsis: true,
            sorter: (a: any, b: any) => String(a?.application?.client?.name || '').localeCompare(String(b?.application?.client?.name || '')),
            render: (text: string) => <span style={{ display: 'inline-block', maxWidth: 180 }} title={text}>{text}</span>
          },
          {
            title: 'Куда (выгрузка)',
            key: 'toStop',
            width: 200,
            render: (_: any, record: any) => {
              const isSelected = selectedIds.some((sid) => Number(sid) === Number(record.id));
              const value = toStopDraft[record.id];
              const fromUid = fromStopDraft[record.id] ?? currentLegacyLoadingStopUid ?? legacyMainLoadingUid;
              const options = unloadingStopOptions
                .filter((o) => {
                  if (!legacyPositionByStopUid || !fromUid) return true;
                  const fromPos = legacyPositionByStopUid.get(fromUid);
                  const toPos = o.position;
                  return fromPos === undefined || toPos >= fromPos;
                })
                .map((o) => ({ value: o.stopUid, label: o.label }));
              return (
                <Select
                  placeholder={options.length ? 'Стоп выгрузки' : 'Нет доступных точек выгрузки'}
                  style={{ width: '100%' }}
                  disabled={!isSelected || options.length === 0}
                  value={value}
                  onChange={(val) => setToStopDraft(prev => ({ ...prev, [record.id]: val }))}
                  onClick={(e) => e.stopPropagation()}
                  options={options}
                />
              );
            }
          },
          {
            title: 'В маршрут (шт)',
            key: 'allocQty',
            width: 160,
            render: (_: any, record: any) => {
              const isSelected = selectedIds.some((sid) => Number(sid) === Number(record.id));
              const maxAlloc =
                record.availableQuantity != null
                  ? Number(record.availableQuantity)
                  : Number(record.quantity || 0);
              const value = isSelected
                ? ((quantityDraft as any)[record.id] ?? (maxAlloc > 0 ? maxAlloc : 1))
                : ((quantityDraft as any)[record.id] ?? 0);
              return (
                <InputNumber
                  min={0}
                  max={maxAlloc}
                  value={value}
                  disabled={!isSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(val) => {
                    const raw = Number(val ?? 0);
                    const clamped =
                      maxAlloc > 0 ? Math.min(maxAlloc, Math.max(0, raw)) : Math.max(0, raw);
                    setQuantityDraft((prev: any) => ({ ...prev, [record.id]: clamped }));
                  }}
                  style={{ width: 120 }}
                />
              );
            }
          }
        ];

        const currentFromStopUid = groupUiEnabled && activeLoadingContext
          ? activeLoadingContext.stopUid
          : currentLegacyLoadingStopUid ?? legacyMainLoadingUid;
        const cargoDataForSelection = (() => {
          const available = Array.isArray(availableCargo) ? (availableCargo as any[]) : [];
          const existing = (editingRoute?.cargo || []) as any[];
          const existingForTab = existing.filter((c) =>
            groupUiEnabled
              ? fromStopDraft[c.id] === currentFromStopUid
              : cargoFromStopMatchesLoadingPosition({
                  fromStopUid: fromStopDraft[c.id],
                  loadingPosition: currentLoadingPosition,
                  positionByStopUid: legacyPositionByStopUid ?? new Map(),
                  mainLoadingPos
                })
          );
          const map = new Map<number, any>();
          available.forEach((c) => map.set(Number(c.id), c));
          existingForTab.forEach((c) => {
            const id = Number(c.id);
            if (!Number.isFinite(id)) return;
            if (!map.has(id)) map.set(id, c);
          });
          let list = Array.from(map.values());

          if (groupUiEnabled && activeDeliveryGroup && activeLoadingContext) {
            list = filterCargoForGroupExecutionContext({
              cargoRows: list,
              group: activeDeliveryGroup,
              context: activeLoadingContext,
              fromStopDraft,
              quantityDraft,
              allocationEdges: multiEdgeEditorEnabled ? resolvedAllocationEdges : undefined
            });
          }

          if (stepData.loadingPointType === 'warehouse' && cargoAppFilter) {
            list = list.filter((c) => Number(c.application?.id) === Number(cargoAppFilter));
          }

          const selectedSet = new Set<number>(
            selectedIds.map((n) => Number(n)).filter((n) => Number.isFinite(n))
          );
          list = list.slice().sort((a, b) => {
            const aSel = selectedSet.has(Number(a.id)) ? 0 : 1;
            const bSel = selectedSet.has(Number(b.id)) ? 0 : 1;
            if (aSel !== bSel) return aSel - bSel;
            return String(a.name || '').localeCompare(String(b.name || ''));
          });

          return list;
        })();

        const cargoAppOptions = (() => {
          const map = new Map<number, { label: string }>();
          (cargoDataForSelection || []).forEach((c: any) => {
            const appId = Number(c?.application?.id);
            if (!Number.isFinite(appId)) return;
            if (map.has(appId)) return;
            const num = c?.application?.internalNumber || `#${appId}`;
            const client = c?.application?.client?.name ? ` — ${c.application.client.name}` : '';
            map.set(appId, { label: `${num}${client}` });
          });
          return Array.from(map.entries()).map(([value, meta]) => ({ value, label: meta.label }));
        })();

        return (
          <Card
            title={groupUiEnabled ? 'Шаг 4: Загрузка и распределение грузов' : 'Шаг 4: Выбор грузов'}
            extra={
              <Button
                danger
                onClick={() => {
                  setSelectedIds([]);
                  form.setFieldsValue({ cargoIds: [] });
                  setStepData((prev) => ({ ...prev, cargoIds: [] }));
                  setQuantityDraft({});
                  setFromStopDraft({});
                  setToStopDraft({});
                  message.success('Все выбранные грузы удалены из формы');
                }}
              >
                Удалить все
              </Button>
            }
          >
            {groupUiEnabled ? (
              <>
                {hiddenFieldNames.map((field) => (
                  <Form.Item key={field} name={field} noStyle>
                    <Input type="hidden" />
                  </Form.Item>
                ))}
                <Form.Item name="cargoIds" hidden rules={[{ required: true, message: 'Выберите хотя бы один груз' }]}>
                  <Input type="hidden" />
                </Form.Item>
                <RouteCargoLoadingPanel
                    groups={deliveryGroups}
                    activeGroupUid={activeGroupUid}
                    onActiveGroupChange={setActiveGroupUid}
                    executionContext={activeLoadingContext}
                    onActiveLoadingStopChange={(stopUid) => {
                      if (!activeGroupUid) return;
                      setActiveLoadingStopByGroup((prev) => ({
                        ...prev,
                        [activeGroupUid]: stopUid
                      }));
                    }}
                    cargoRows={cargoDataForSelection}
                    fromStopDraft={fromStopDraft}
                    quantityDraft={quantityDraft}
                    toStopDraft={toStopDraft}
                    {...(multiEdgeEditorEnabled
                      ? {
                          multiEdgeEnabled: true as const,
                          allocationEdges: resolvedAllocationEdges,
                          onAllocationEdgesChange: handleAllocationEdgesChange,
                          richUnloadLabels: true
                        }
                      : {})}
                    legacyStops={stops}
                    executionTopology={executionTopologyPreview}
                    mainLoadingFields={executionMainStopFields.mainLoading}
                    mainUnloadingFields={executionMainStopFields.mainUnloading}
                    mainUnloading={{
                      position: stops.length + 1,
                      type: 'unloading',
                      pointType: stepData.unloadingPointType ?? unloadingPointType,
                      pointId: stepData.unloadingPointId ?? unloadingPointId
                    }}
                    applications={applications as any[]}
                    resolveWarehouseLabel={resolveWarehouseLabelForCargo}
                    resolveConsigneeLabel={resolveConsigneeLabelForCargo}
                    onToggleCargo={(cargoId, selected, defaultToStopUid) => {
                      const fromStopUid = activeLoadingContext?.stopUid;
                      if (!fromStopUid) return;
                      setQuantityDraft((prev) => {
                        const next = { ...prev };
                        if (!selected) {
                          next[cargoId] = 0;
                          return next;
                        }
                        const item = cargoDataForSelection.find((c: any) => Number(c.id) === cargoId);
                        const maxQty = Number(item?.availableQuantity ?? item?.quantity ?? 0);
                        next[cargoId] = maxQty > 0 ? maxQty : 1;
                        return next;
                      });
                      setFromStopDraft((prev) => {
                        const next = { ...prev };
                        if (selected) next[cargoId] = fromStopUid;
                        else if (next[cargoId] === fromStopUid) delete next[cargoId];
                        return next;
                      });
                      setToStopDraft((prev) => {
                        const next = { ...prev };
                        if (selected) {
                          if (next[cargoId] === undefined) next[cargoId] = defaultToStopUid;
                        } else {
                          delete next[cargoId];
                        }
                        return next;
                      });
                      setSelectedIds((prev) => {
                        const set = new Set(prev.map(Number));
                        if (selected) set.add(cargoId);
                        else set.delete(cargoId);
                        const merged = Array.from(set);
                        form.setFieldsValue({ cargoIds: merged });
                        setStepData((p) => ({ ...p, cargoIds: merged }));
                        return merged;
                      });
                    }}
                    onQuantityChange={(cargoId, qty) => {
                      setQuantityDraft((prev) => ({ ...prev, [cargoId]: qty }));
                    }}
                    onDestChange={(cargoId, toStopUid) => {
                      setToStopDraft((prev) => ({ ...prev, [cargoId]: toStopUid }));
                    }}
                  />
              </>
            ) : (
            <>
            {computedCargoSource.loadingPointType === 'application' && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Список грузов привязан к заявке"
                description="Сервер отдаёт только грузы выбранной заявки (или заявки из стопа погрузки). Чтобы взять грузы другой заявки, смените заявку на шаге 1 или выберите другой стоп погрузки."
              />
            )}
            {computedCargoSource.loadingPointType === 'warehouse' && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Погрузка со склада"
                description="В списке могут быть грузы разных заявок. При необходимости отфильтруйте по заявке ниже."
              />
            )}
            <Row style={{ marginBottom: 12 }}>
              <Col span={24}>
                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                  <Text type="secondary">
                    {groupUiEnabled ? 'Delivery group (вкладка):' : 'Точка погрузки (вкладка):'}
                  </Text>
                  {groupUiEnabled ? (
                    <Tabs
                      size="small"
                      type="card"
                      activeKey={activeGroupUid ?? deliveryGroups[0]?.uid ?? 'main'}
                      items={deliveryGroups.map((g) => ({
                        key: g.uid,
                        label: g.label || (g.isDefault ? 'Group A (default)' : `Group ${g.sortOrder + 1}`)
                      }))}
                      onChange={(key) => setActiveGroupUid(String(key))}
                    />
                  ) : (
                  <Tabs
                    size="small"
                    type="card"
                    activeKey={cargoSource.kind === 'main' ? 'main' : `stop-${cargoSource.stopIndex}`}
                    items={loadingSourceTabs.map((t) => ({ key: t.key, label: t.label }))}
                    onChange={(key) => {
                      if (key === 'main') {
                        setCargoSource({ kind: 'main' });
                        return;
                      }
                      if (String(key).startsWith('stop-')) {
                        const idx = Number(String(key).replace('stop-', ''));
                        setCargoSource({ kind: 'stop', stopIndex: idx });
                      }
                    }}
                  />
                  )}
                  <Card size="small" style={{ background: 'rgba(0,0,0,0.02)' }}>
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Text strong>{selectedLoadingInfo.title}</Text>
                      {selectedLoadingInfo.lines.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 6, rowGap: 4 }}>
                          {selectedLoadingInfo.lines.map((l, idx) => (
                            <React.Fragment key={idx}>
                              <Text type="secondary" style={{ fontSize: 12 }}>{l.label}:</Text>
                              <Text style={{ fontSize: 12 }}>{l.value}</Text>
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12 }}>Выберите точку погрузки, чтобы увидеть детали.</Text>
                      )}
                    </Space>
                  </Card>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    “Откуда” фиксируется выбранной вкладкой. В таблице выбирайте только “Куда” и количество.
                  </Text>
                </Space>
              </Col>
            </Row>
            {hiddenFieldNames.map((field) => (
              <Form.Item key={field} name={field} noStyle>
                <Input type="hidden" />
              </Form.Item>
            ))}

            <Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
              Выберите грузы для маршрута (по выбранной точке погрузки)
            </Text>

            {stepData.loadingPointType === 'warehouse' && cargoAppOptions.length > 0 && (
              <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col span={10}>
                  <Select
                    placeholder="Фильтр по заявке"
                    allowClear
                    value={cargoAppFilter}
                    onChange={(value) => {
                      setCargoAppFilter(value);
                      form.setFieldValue('applicationId', value ?? undefined);
                    }}
                    options={cargoAppOptions}
                    style={{ width: '100%' }}
                    showSearch
                    optionFilterProp="label"
                  />
                </Col>
              </Row>
            )}
            
            <Form.Item
              name="cargoIds"
              rules={[{ required: true, message: 'Выберите хотя бы один груз' }]}
            >
              <DataTable
                size="small"
                rowClassName={(record: any) =>
                  selectedIds.some((sid) => Number(sid) === Number(record.id))
                    ? 'route-create-cargo-row-selected'
                    : ''
                }
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedIds,
                  onChange: (selectedRowKeys) => {
                    console.log('SELECT CHANGE:', selectedRowKeys);
                    const keys = (selectedRowKeys as React.Key[]).map(Number).filter(Number.isFinite);
                    const prevGlobal: number[] = selectedIds;
                    const nowSelected: number[] = keys;
                    const currentDataIds = new Set((cargoDataForSelection || []).map((c: any) => Number(c.id)));
                    // Учитываем изменения только в рамках текущего источника
                    const prevInCurrent = prevGlobal.filter(id => currentDataIds.has(id));
                    const added = nowSelected.filter(id => !prevInCurrent.includes(id));
                    const removed = prevInCurrent.filter(id => !nowSelected.includes(id));
                    // Обновим количества: у выбранных в текущей таблице — дефолт max/1, если черновик пустой или 0; снятым — 0
                    setQuantityDraft((prev) => {
                      const next = { ...prev };
                      removed.forEach((id) => {
                        next[id] = 0;
                      });
                      nowSelected.forEach((id) => {
                        const item = (cargoDataForSelection || []).find(
                          (c: any) => Number(c.id) === Number(id)
                        );
                        const maxQty = Number(item?.quantity ?? 0);
                        next[id] = maxQty > 0 ? maxQty : 1;
                      });
                      return next;
                    });
                    const fromStopUid = groupUiEnabled && activeLoadingContext
                      ? activeLoadingContext.stopUid
                      : currentLegacyLoadingStopUid ?? legacyMainLoadingUid;
                    setFromStopDraft((prev) => {
                      const next = { ...prev };
                      added.forEach((id) => { next[id] = fromStopUid; });
                      removed.forEach((id) => { if (next[id] === fromStopUid) delete next[id]; });
                      return next;
                    });

                    setToStopDraft((prev) => {
                      const next = { ...prev };
                      const defaultToUid = unloadingStopOptions[0]?.stopUid ?? legacyMainUnloadingUid;
                      added.forEach((id) => {
                        if (next[id] === undefined) next[id] = defaultToUid;
                      });
                      return next;
                    });
                    // Объединяем выбор: сохраняем позиции из других источников + текущие
                    const keptOutside = prevGlobal.filter((id) => !currentDataIds.has(Number(id)));
                    const merged = Array.from(new Set<number>([...keptOutside, ...nowSelected]));
                    setSelectedIds(merged);
                    form.setFieldsValue({ cargoIds: merged });
                    setStepData((prev) => ({ ...prev, cargoIds: merged }));
                  }
                }}
                columns={availableCargoColumns}
                dataSource={cargoDataForSelection}
                rowKey={(row: any) => row.id}
                pagination={false}
                scroll={{ x: 'max-content', y: 300 }}
                locale={{ emptyText: 'Нет доступных грузов (все уже распределены)' }}
              />
            </Form.Item>

            <style>{`
              .route-create-cargo-row-selected > td {
                background: rgba(22, 119, 255, 0.08) !important;
              }
              .route-create-cargo-row-selected:hover > td {
                background: rgba(22, 119, 255, 0.12) !important;
              }
            `}</style>

            {cargoDataForSelection.length > 0 && (
              <Text type="secondary">
                Грузов в списке: {cargoDataForSelection.length}
              </Text>
            )}
            </>
            )}
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={editingRoute ? `Редактирование маршрута #${editingRoute.id}` : 'Создание маршрута'}
      open={visible}
      onCancel={handleCancel}
      width={1300}
      footer={null}
      style={{ backgroundColor: '#fff' }}
      styles={{ body: { backgroundColor: '#fff' } }}
    >
      <Steps
        current={currentStep}
        items={steps.map((s, i) => ({
          title: s.title,
          icon: s.icon,
          ...(cargoStepError && i === 3 ? { status: 'error' as const } : {})
        }))}
        style={{ marginBottom: 24 }}
      />
      
      <Form
        form={form}
        layout="vertical"
        style={{ minHeight: 400 }}
        onValuesChange={saveDraft}
        preserve
      >
        {renderCurrentStep()}
      </Form>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          {postCreatePenaltiesStep ? (
            <>
              <Button
                type="primary"
                onClick={() => void handleSaveCarrierPenalties()}
                loading={penaltiesBatchSaving}
                disabled={
                  carrierAgreementId == null ||
                  penaltyDrafts.length === 0 ||
                  !penaltiesDirty ||
                  penaltiesBatchSaving
                }
              >
                Сохранить штрафы
              </Button>
              <Button onClick={handleCancel}>Закрыть</Button>
            </>
          ) : (
            <>
              {currentStep > 0 && (
                <Button onClick={handlePrev}>Назад</Button>
              )}
              {currentStep < steps.length - 1 && (
                <Button type="primary" onClick={handleNext}>Далее</Button>
              )}
              {currentStep === steps.length - 1 && (
                <Button type="primary" onClick={handleFinish} loading={isLoading}>
                  {editingRoute ? 'Сохранить изменения' : 'Создать маршрут'}
                </Button>
              )}
              {!editingRoute && (
                <Button onClick={handleResetForm}>
                  Очистить форму
                </Button>
              )}
              <Button onClick={handleCancel}>Отмена</Button>
            </>
          )}
        </Space>
      </div>

      {/* Modals for creating new entities */}
      <CreateCarrierModal
        isOpen={carrierModalOpen}
        onClose={() => setCarrierModalOpen(false)}
        onCarrierCreated={handleCarrierCreated}
      />
      <CreateRailwayStationModal
        visible={railwayStationModalOpen}
        onCancel={() => setRailwayStationModalOpen(false)}
        onSuccess={handleRailwayStationCreated}
      />
      <CreateAirportModal
        visible={airportModalOpen}
        onCancel={() => setAirportModalOpen(false)}
        onSuccess={handleAirportCreated}
      />
      <CreateContractorModal
        isOpen={contractorModalOpen}
        onCancel={() => setContractorModalOpen(false)}
        onSuccess={handleContractorCreated}
        defaultRoles={contractorModalDefaultRoles}
      />
      <CreateDriverModal isOpen={driverModalOpen} onClose={() => setDriverModalOpen(false)} onDriverCreated={handleDriverCreated} />
    </Modal>
  );
};

export default RouteCreateForm;